import { portalFR } from '../portal/fr';
import { privacyPolicyBodyFr } from '@/shared/i18n/locales/privacy-policy-bodies/fr';
import { restaurantOrdersFr } from '@/shared/i18n/locales/restaurant-orders/fr';
import type { TranslationSchema } from '@/shared/i18n/types';

const paymentPolicyTemplate = `POLITIQUE DE PAIEMENT, DE CARTE ET D'ANNULATION

1. Identité de l'établissement
La réservation et son paiement sont gérés par [RAISON SOCIALE], dont le siège est [ADRESSE], joignable au [EMAIL/TÉLÉPHONE]. Stripe agit uniquement en tant que prestataire de services de paiement.

2. Vérification de la carte
Lorsque seule la vérification est requise, aucun montant n'est débité ni bloqué. La carte est vérifiée via Stripe afin d'en confirmer la validité.

3. Paiement anticipé
Lorsque le paiement immédiat est requis, le total affiché avant la confirmation est débité au moment de la réservation et enregistré comme [ACOMPTE/PAIEMENT INTÉGRAL].

4. Préautorisation
Lorsqu'une préautorisation est prévue, le montant affiché est bloqué temporairement. L'établissement peut l'encaisser dans les cas décrits par la présente politique. À défaut d'encaissement, les délais de déblocage dépendent de la banque du client.

5. Annulation et modification
Annulation gratuite jusqu'à [DÉLAI]. Passé ce délai, l'établissement peut retenir ou débiter [MONTANT/POURCENTAGE]. Les modifications sont soumises à disponibilité.

6. No-show et retard
En cas d'absence ou de retard supérieur à [MINUTES], l'établissement peut retenir ou débiter [MONTANT/POURCENTAGE].

7. Remboursements
Les remboursements sont effectués sur le même moyen de paiement sous [JOURS] jours ; les délais de crédit finaux dépendent du réseau de la carte et de la banque.

8. Conservation et utilisation future de la carte
La carte peut être conservée sous forme tokenisée par Stripe et réutilisée uniquement si le client donne un consentement spécifique pour les débits décrits. Ni PRO20 ni l'établissement ne conservent le numéro complet de la carte.

9. Litiges et assistance
Pour toute demande, annulation ou contestation, contactez [EMAIL/TÉLÉPHONE] en indiquant le code de réservation.

Version et validité
La version acceptée, le montant, le mode de paiement et la date d'acceptation sont enregistrés avec la réservation.`;

export const fr: TranslationSchema = {
  collaboratorRoles: {
    inherited:
      'This role is not configured yet and retains its current access. Save to define explicit permissions.',
    title: 'App permissions',
    description:
      'Choose which app operations each collaborator role can perform. Portal access is unchanged.',
    role: 'Collaborator role',
    enabled: 'enabled',
    wholeCategory: 'Entire category',
    search: 'Search operations',
    saved: 'Permissions saved',
    saveError:
      'Could not save. Reload the role to check for changes by another manager.',
    versionError: 'The permission catalog has changed. Reload before saving.',
    unsaved: 'Unsaved changes',
    appOnly: 'These permissions apply to the app only.',
    reset: 'Discard changes',
    save: 'Save permissions',
    discard: 'Discard unsaved changes and load the selected role?',
    selectBranch: 'Select a branch to configure permissions.',
    loading: 'Loading permissions…',
    unavailable:
      'Role permissions could not be loaded. Check your access and that the service supports this configuration. No permission changes have been applied.',
    retry: 'Retry',
  },

  portal: portalFR,
  restaurantOrders: restaurantOrdersFr,
  giftCards: {
    deselect: 'Désélectionner le client',
    cancelCard: 'Annuler la carte cadeau',
    cancelTitle: 'Annuler cette carte cadeau ?',
    cancelDescription:
      'La carte de {{recipient}} ne sera plus utilisable. Le solde et les opérations resteront dans l’historique.',
    cancelSuccess: 'Carte cadeau annulée.',
    cancelError: 'Impossible d’annuler la carte cadeau.',
    openRecap: 'Ouvrir le récapitulatif',
    recapError:
      'Impossible de récupérer le lien personnel. Le service Wallet est peut-être indisponible.',

    select: 'Sélectionner',
    card: 'Carte',
    invalidDates: 'Sélectionnez une période valide.',

    title: 'Cartes cadeaux',
    description: 'Cartes émises, QR et soldes',
    create: 'Créer une carte cadeau',
    search: 'Rechercher un client ou un code…',
    empty: 'Aucune carte cadeau',
    loadError: 'Impossible de charger les cartes.',
    noBranch: 'Sélectionnez un établissement.',
    customer: 'Client',
    amount: 'Montant (€)',
    chooseCustomer: 'Choisissez un client dans la liste',
    selected: 'Client sélectionné',
    createSuccess: 'Carte créée.',
    createError: 'Impossible de créer la carte.',
    amountError:
      'Saisissez un montant supérieur à zéro avec deux décimales maximum.',
    details: 'Détails de la carte',
    initial: 'Montant initial',
    balance: 'Solde',
    status: 'Statut',
    created: 'Création',
    expires: 'Expiration',
    operations: 'Opérations',
    operation: 'Opération',
    note: 'Note',
    noOperations: 'Aucune opération.',
    email: 'Envoyer un email',
    resend: 'Renvoyer',
    resendChannel: 'Comment souhaitez-vous renvoyer la carte cadeau ?',
    resendWhatsapp: 'Envoyer via WhatsApp',
    resendWhatsappHint:
      'Envoi depuis le numéro WhatsApp de l’établissement avec le modèle de carte cadeau configuré.',
    resendNoEmail: 'E-mail indisponible.',
    resendNoPhone: 'Numéro international indisponible ou invalide.',
    resendPopupBlocked:
      'Autorisez les fenêtres contextuelles pour ouvrir WhatsApp.',
    resendWhatsappMessage: 'Voici le lien vers votre carte cadeau :',
    resendWhatsappSuccess: 'Envoi WhatsApp demandé.',
    resendError: 'Renvoi non confirmé. Vérifiez l’état avant de réessayer.',
    resendEmail: 'Renvoyer l’e-mail',
    emailSuccess: 'Envoi demandé.',
    emailError: 'Impossible de demander l’envoi.',
    active: 'Active',
    used: 'Utilisée',
    expired: 'Expirée',
    cancelled: 'Annulée',
    allStates: 'Tous les statuts',
    from: 'Du',
    to: 'Au',
    dateRange: 'Plage de dates',
    clearDateRange: 'Effacer la plage de dates',
    requestPending:
      'Conservez le client et le montant pour réessayer sans émettre une seconde carte.',
  },
  common: {
    retry: 'Réessayer',
    loading: 'Chargement',
    save: 'Enregistrer',
    delete: 'Oui, supprimer',
    deleting: 'Suppression...',
    createLoading: 'Création...',
    cancel: 'Annuler',
    yes: 'Oui',
    no: 'Non',
    back: 'Retour',
    search: 'Rechercher',
    noData: 'Aucune donnée disponible',
    noDataDesc: 'Aucune donnée ne correspond aux filtres sélectionnés.',
    previous: 'Page précédente',
    next: 'Page suivante',
    actions: 'Actions',
    serialNumber: 'Sr No.',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: 'URL copiée dans le presse-papiers.',
    },
  },
  nav: {
    collaboratorRoles: 'Collaborator roles',
    deliverySection: 'Delivery',
    drivers: 'Livreurs',
    orders: 'Commandes',
    delivery: 'Delivery',
    deliveryTakeaway: 'Livraison / À emporter',
    orderingConfiguration: 'Configuration',
    takeaway: 'À emporter',
    operations: 'Commandes',
    suppliers: 'Suppliers and catalogs',
    supplierOrderHistory: 'Order history',
    supplierOrders: 'Supplier orders',
    ingredients: 'Ingrédients',
    menuList: 'Vos menus',
    home: 'ACCUEIL',
    menu: 'Menu',
    settingsGroup: 'PARAMÈTRES',
    dashboard: 'Tableau de bord',
    calendar: 'Calendrier',
    calendarDesc: 'Réservations et formulaires',
    marketing: 'Marketing',
    marketingDesc: 'Messages et promotions',
    menuItem: 'Menu',
    staff: 'Personnel',
    settings: 'Configurations',
    payments: 'Paiements',
    settingsDesc: 'Paramètres généraux',
    users: 'Utilisateurs',
    reports: 'Rapports',
    notifications: 'Notifications',
    bookingHistory: 'Réservations',
    forms: 'Formulaires de réservation',
    formHistory: 'Booking',
    formMenu: 'Menu',
    others: 'Autres',
    extraServiceAndProduct: 'Services extra',
    events: 'Événement',
    bulkMessage: 'Message groupé',
    surveys: 'Questionnaires',
    automationConfiguration: 'Configuration',
    automationDashboard: 'Tableau de bord des performances',
    automation: 'Automation',
    dataCollectionForm: 'Collecte de données',
    promotion: 'Promotions',
    fidelity: 'Programme de Fidélité',
    discountCards: 'Discount cards',
    cards: 'Cards',
    experienceCards: 'Experience',
    prizes: 'Récompense',
    loyalty: 'Fidélisation',
    landingPage: 'Pages de Destination',
    tag: 'Étiquette',
    publicRelation: 'Relations publiques',
    collaborator: 'Collaborateurs',
    collaboratorReport: 'Rapport collaborateurs',
    myCustomers: 'Mes clients',
    localConfiguration: 'Local',
    stripeConfiguration: 'Configuration Stripe',
    stripeTransactions: 'Transactions Stripe',
    configureWhatsApp: 'WhatsApp non officiel',
    messages: 'Messages',
    officialWhatsAppChannel: 'Canal WhatsApp officiel',
    templates: 'Modèles',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: 'Configurer',
    whatsAppOfficialTemplates: 'Modèles',
    whatsAppOfficialInbox: 'Boîte de réception',
  },
  header: {
    fullscreen: 'Plein écran',
    language: 'Langue',
    theme: 'Thème',
    lightMode: 'Clair',
    darkMode: 'Sombre',
    profile: 'Profil',
    signOut: 'Déconnexion',
    userCode: 'Code utilisateur',
    branchList: 'Liste des succursales',
    searchBranches: 'Rechercher des succursales...',
    noBranchesFound: 'Aucune succursale trouvée',
    switchingBranch: 'Changement de succursale…',
  },
  dashboard: {
    aiCredits: 'Crédits IA',
    smsAvailable: 'SMS disponibles',
    smsLoading: 'Chargement du solde SMS…',
    smsError: 'Erreur du solde SMS',
    smsErrorHint: 'Solde SMS indisponible. Réessayez.',
    smsRefresh: 'Actualiser le solde SMS',
    smsEmpty: 'Aucun SMS disponible',
    title: 'Tableau de bord',
    subtitle: 'Voici ce qui se passe dans votre activité.',
    headerMeta: '{{dateRange}} · fenêtre de performance de {{days}} jours',
    customRange: 'Plage de dates',
    customRangePlaceholder: 'Sélectionner une plage de dates',
    refresh: 'Actualiser',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: 'Arrivés',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: 'Impossible de charger cette section.',
    bookingsFlow: 'Bookings flow',
    forms: 'Formulaires',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: "En attente d'action",
    waitingActionSubtitle: 'Réservations IN_ATTESA prêtes à traiter.',
    waitingBadge: 'En attente',
    people: 'personnes',
    openBooking: 'Ouvrir la réservation',
    copyContact: 'Copier le contact',
    copySuccess: 'Copié dans le presse-papiers.',
    emptyWaiting: "Aucune réservation en attente d'action.",
    emptyChart: 'Aucune donnée pour cette période.',
    emptyCustomers: 'Aucun client à afficher.',
    emptyForms: 'Aucun formulaire à afficher.',
    guests: 'couverts',
    children: 'enfants',
    accept: 'Accepter',
    reject: 'Refuser',
    acceptSuccess: 'Réservation acceptée.',
    rejectSuccess: 'Réservation refusée.',
    actionError: 'Impossible de mettre à jour le statut de la réservation.',
    busiestSlots: 'Créneaux les plus fréquentés',
    busiestSlotsSubtitle: 'Là où le service se densifie.',
    customerMix: 'Base clients',
    customerRisk: 'Risque clients',
    customerRiskSubtitle: 'Concentration des no-shows et refus.',
    bestCustomers: 'Meilleurs clients',
    bestCustomersSubtitle: 'Présence récurrente à forte valeur.',
    lastBooking: 'Dernière réservation',
    messageDelivery: 'Livraison des messages',
    messageDeliverySubtitle: 'Santé de la communication automatisée.',
    dailyOperations: 'Opérations quotidiennes',
    dailyOperationsSubtitle: 'Réservations et charge de couverts par jour.',
    bookingsFlowSubtitle:
      'Réservations quotidiennes sur la période sélectionnée.',
    formsSubtitle: "D'où viennent les réservations.",
    statusDistributionSubtitle: 'Comment les réservations se sont terminées.',
    customerMixSubtitle: 'Votre base clients sur cette période.',
    pulse: {
      today: "Aujourd'hui",
      upcoming: 'À venir',
      pending: 'En attente',
      waitList: "Liste d'attente",
      refusalRate: 'Taux de refus',
      google: 'Google Reservations',
      googleOn: 'Actif',
      googleOff: 'Inactif',
      googleMissing: '{{count}} prérequis manquants',
    },
    waitingTabs: {
      all: 'Tous',
      today: "Aujourd'hui",
      future: 'Futures',
      large: 'Grandes',
    },
    flow: {
      volume: 'Volume',
      people: 'Personnes',
      outcome: 'Résultat',
    },
    kpis: {
      reservations: 'Réservations',
      reservationsHint: '{{count}} personnes',
      arrivalRate: "Taux d'arrivée",
      customers: 'Clients',
      arrivedOfTotal: '{{arrived}} sur {{total}} arrivés',
      customersHint: '{{active}} actifs · {{new}} nouveaux',
      messageErrors: "{{count}} erreurs d'envoi",
      noShowRate: 'Taux de no-show',
      arrivedVsNoShow: '{{arrived}} arrivés · {{noShow}} no-show',
      peakDay: 'Jour de pic',
      peakSlot: 'Créneau de pic',
      dailyAverage: 'Moyenne quotidienne',
      dailyAverageHint: 'Réservations par jour',
      messagesWaiting: 'Messages en attente',
      scheduledTotal: '{{count}} planifiés au total',
      bookingsGuests: '{{bookings}} réservations, {{guests}} couverts',
      slotStats: '{{bookings}} réservations · {{arrived}} arrivés ({{rate}}%)',
      totalBookings: 'Réservations totales',
      activeUsers: 'Utilisateurs actifs',
      newUsers: 'Nouveaux utilisateurs',
      peakDate: 'Date de pic',
      managedBookings: 'Réservations gérées',
    },
    series: {
      bookings: 'Réservations',
      confirmed: 'Confirmées',
      pending: 'En attente',
      refused: 'Refusées',
      noShow: 'No show',
      risk: 'Risque',
    },
    mix: {
      active: 'Actifs',
      new: 'Nouveaux',
      marketingOptIn: 'Opt-in marketing',
      blacklisted: 'Liste noire',
      ofBase: '{{percent}} % de la base',
      total: 'au total',
    },
    comm: {
      bookingNotifications: 'Notifications de réservation',
      scheduledMessages: 'Messages planifiés',
      successRate: 'Taux de succès',
      total: 'Total',
      processed: 'traités',
      waiting: 'en attente',
      errors: 'erreurs',
      centralino: 'Statut centralino',
      centralinoActive: 'Centralino actif',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'Email',
      enabled: 'On',
      disabled: 'Off',
    },
    ops: {
      byDay: 'Par jour',
      byWeekday: 'Par jour de semaine',
      avgPeople: 'Personnes moy.',
      avgBookings: 'Réservations moy.',
      avgPartySize: 'Taille moy. du groupe',
      busiestWeekday: 'Jour le plus chargé',
      totalPeople: 'Personnes au total',
      daysCount: '{{count}} jours',
    },
    days: {
      mon: 'Lun',
      tue: 'Mar',
      wed: 'Mer',
      thu: 'Jeu',
      fri: 'Ven',
      sat: 'Sam',
      sun: 'Dim',
    },
  },
  auth: {
    welcomeBack: 'Bon retour !',
    signIn: 'Se connecter',
    email: 'Email',
    password: 'Mot de passe',
    enterPassword: 'Entrez votre mot de passe',
    userCode: 'Code utilisateur',
    enterUserCode: 'Entrez votre code utilisateur',
    branchCode: 'Code établissement',
    enterBranchCode: "Entrez le code d'établissement",
    employeeId: 'ID employé',
    enterEmployeeId: "Entrez l'ID employé",
    rememberMe: 'Se souvenir de moi',
    rememberCredentials: 'Mémoriser les identifiants',
    forgotPassword: 'Mot de passe oublié ?',
    signInToContinue:
      'Connectez-vous pour continuer à gérer votre restaurant avec PRO20.',
    signInToAccount: 'Connectez-vous à votre compte',
    chooseSignInMethod: 'Choisissez votre méthode de connexion',
    or: 'ou',
    loginWithEmployeeId: "Se connecter avec l'ID employé",
    loginWithUserCode: 'Se connecter avec le code utilisateur',
    loginWithAdminCode: 'Connexion avec code administrateur',
    invalidCredentials: 'Identifiants invalides. Réessayez.',
    enterpriseTagline: 'Admin entreprise, simplifié.',
    enterpriseDescription:
      'Gérez les utilisateurs, surveillez les analyses et configurez votre espace de travail depuis un seul tableau de bord.',
    trustedBy: 'Adopté par des restaurants du monde entier',
    trustSecurityTitle: 'Sécurité entreprise',
    trustSecurityDesc:
      'Protection des données et conformité de niveau bancaire.',
    trustUptimeTitle: 'Disponibilité 99,9 %',
    trustUptimeDesc: 'Des performances fiables quand vous en avez besoin.',
    trustSupportTitle: 'Support 24/7',
    trustSupportDesc: 'Nous sommes là pour vous aider à tout moment.',
    secureSignIn: 'Connexion sécurisée | Votre connexion est chiffrée.',
    copyright: '© {{year}} PRO20. Tous droits réservés.',
    dashboardPreviewAlt: 'Aperçu du tableau de bord PRO20',
    demoCredentials: 'Identifiants démo : admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: 'Abonnement expiré',
      title: 'Votre abonnement a expiré',
      description: 'Si vous avez déjà renouvelé, contactez le support.',
      infoTitle: 'Besoin de renouveler ou de rétablir l’accès ?',
      infoDescription:
        'Contactez notre équipe de support et nous vous aiderons à reprendre.',
      footerHelp: 'Besoin d’aide supplémentaire ?',
      footerHelpDesc:
        'Notre équipe de support est disponible 24/7 pour vous assister.',
    },
  },
  automation: {
    disabled: {
      badge: 'Section non activée',
      title: 'Cette section n’est pas activée',
      description:
        'Cette fonctionnalité est actuellement désactivée pour votre compte. Contactez le support pour en savoir plus.',
      infoTitle: 'Besoin d’accéder à cette section ?',
      infoDescription:
        'Contactez notre équipe d’assistance et nous vous aiderons à démarrer.',
      footerHelp: 'Besoin d’aide encore ?',
      footerHelpDesc:
        'Notre équipe d’assistance est disponible 24h/24 et 7j/7.',
    },
  },
  orders: {
    disabled: {
      badge: 'Section non activée',
      title: 'Cette section n’est pas activée',
      description:
        'Cette fonctionnalité est actuellement désactivée pour votre compte. Contactez le support pour en savoir plus.',
      infoTitle: 'Besoin d’accéder à cette section ?',
      infoDescription:
        'Contactez notre équipe d’assistance et nous vous aiderons à démarrer.',
      footerHelp: 'Besoin d’aide encore ?',
      footerHelpDesc:
        'Notre équipe d’assistance est disponible 24h/24 et 7j/7.',
    },
  },
  delivery: {
    disabled: {
      badge: 'Section non activée',
      title: 'Cette section n’est pas activée',
      description:
        'Cette fonctionnalité est actuellement désactivée pour votre compte. Contactez le support pour en savoir plus.',
      infoTitle: 'Besoin d’accéder à cette section ?',
      infoDescription:
        'Contactez notre équipe d’assistance et nous vous aiderons à démarrer.',
      footerHelp: 'Besoin d’aide encore ?',
      footerHelpDesc:
        'Notre équipe d’assistance est disponible 24h/24 et 7j/7.',
    },
  },
  users: {
    title: 'Utilisateurs',
    subtitle: "Gérer les membres de l'équipe et les permissions",
    searchPlaceholder: 'Rechercher des utilisateurs...',
    role: 'Rôle',
    status: 'Statut',
    joined: 'Inscrit',
    accountDetails: 'Détails du compte',
    userId: 'ID utilisateur',
    created: 'Créé',
    lastUpdated: 'Dernière mise à jour',
    notFound: 'Utilisateur introuvable',
  },
  settings: {
    title: 'Paramètres',
    subtitle: "Gérer les préférences de l'espace de travail",
    comingSoon: 'Cette section sera bientôt disponible.',
    general: 'Général',
    theme: 'Thème',
    language: 'Langue',
    timezone: 'Fuseau horaire',
    notifications: 'Notifications',
    emailNotifications: 'Notifications par email',
    pushNotifications: 'Notifications push',
    enabled: 'Activé',
    disabled: 'Désactivé',
  },
  userSettings: {
    title: 'Paramètres utilisateur',
    subtitle: 'Gérez le mot de passe et les préférences de votre compte',
    password: {
      sectionTitle: 'Gestion du mot de passe',
      sectionSubtitle: 'Changer le mot de passe',
      passwordLabel: 'Mot de passe',
      confirmLabel: 'Confirmer le mot de passe',
      submit: 'Mettre à jour le mot de passe',
      errors: {
        required: 'Le mot de passe est requis',
        minLength: 'Le mot de passe doit contenir au moins 4 caractères',
        confirmRequired: 'Veuillez confirmer le mot de passe',
        mismatch: 'Les mots de passe ne correspondent pas',
      },
      toast: {
        success: 'Mot de passe mis à jour avec succès',
        failed: 'Impossible de mettre à jour le mot de passe. Réessayez.',
        missingUser: 'Compte introuvable. Veuillez vous reconnecter.',
      },
    },
  },
  localConfiguration: {
    subtitle:
      'Configurez le logo, les horaires, l’emplacement et le suivi du restaurant.',
    saveConfiguration: 'Enregistrer la configuration',
    saveTooltip: 'Enregistrer la configuration du restaurant',
    noBranch:
      'Sélectionnez un établissement pour configurer les paramètres locaux.',
    loadError:
      'Impossible de charger la configuration locale. Veuillez réessayer.',
    logoTitle: 'Logo du restaurant',
    logoHint: 'Télécharger une image',
    logoDescription:
      'Téléchargez un logo PNG avec un fond transparent. Double-cliquez sur l’image pour ouvrir un aperçu.',
    uploadImage: 'Télécharger une image',
    removeLogo: 'Supprimer le logo',
    logoUploading: 'Téléversement…',
    logoRemoving: 'Suppression…',
    timezoneTitle: 'Fuseau horaire de la salle',
    timezoneHint:
      'Déterminez le jour courant affiché dans le formulaire et évitez les décalages de date pour les clients à l’étranger.',
    timezoneLabel: 'Zone géographique',
    timezoneHelper:
      'Le changement entre l’heure solaire et l’heure d’été est géré automatiquement.',
    generalTitle: 'Détails généraux',
    generalHint:
      'Configurez la capacité de l’établissement, les créneaux horaires et les jours de fermeture.',
    scheduleTitle: 'Horaires',
    scheduleHint: 'Choisissez quand les clients peuvent réserver.',
    dayOverviewTitle: 'Quand les clients peuvent réserver',
    dayOverviewHint: 'Ouvrez ceci pour voir toute la journée.',
    dayOverviewShow: 'Afficher l’aperçu de la journée',
    dayOverviewHide: 'Masquer l’aperçu de la journée',
    guestsCanBook: 'Les clients peuvent réserver pendant cet horaire.',
    guestsCannotBook:
      'Les clients ne peuvent pas réserver pendant cet horaire.',
    emptyServiceTimes:
      'Aucun horaire pour le moment. Ajoutez le petit-déjeuner, le déjeuner ou le dîner pour que les clients sachent quand réserver.',
    timeStepHint:
      'Choisissez des heures par pas de 15 minutes. Les réservations s’arrêtent à l’heure de fin.',
    switchOn: 'Oui',
    switchOff: 'Non',
    editAction: 'Modifier',
    deleteAction: 'Supprimer',
    howItWorks: 'Comment ça marche',
    previewOnly: 'Aperçu uniquement — modifiez les plages ci-dessous',
    stepBadge: 'Pas : 15 min — 04:00 / 03:59',
    localDay: 'Journée locale : {{time}}',
    tips: {
      turnOnTitle: 'Activer',
      turnOn: 'Ne laissez actives que les plages que vous utilisez.',
      moveTitle: 'Déplacer',
      move: 'Déplacez le début et la fin avec les curseurs.',
      saveTitle: 'Enregistrer',
      save: 'Le système évite les chevauchements.',
    },
    shiftsTitle: 'Horaires de service',
    shiftsHint:
      'Ajoutez les horaires où vous prenez des réservations, comme le déjeuner ou le dîner. Vous pouvez en désactiver un sans le supprimer.',
    createMealBand: 'Ajouter un horaire',
    createMealBandAction: 'Ajouter',
    createMealBandHint:
      'Donnez-lui un nom, puis choisissez quand il commence et quand il se termine.',
    editMealBand: 'Modifier l’horaire',
    editMealBandNamed: 'Modifier {{name}}',
    updateMealBandAction: 'Enregistrer',
    mealBandName: 'Nom',
    mealBandNamePlaceholder: 'Déjeuner',
    selectStartTime: "Sélectionner l'heure de début",
    selectEndTime: "Sélectionner l'heure de fin",
    mealBandLimitReached: 'Vous pouvez ajouter jusqu’à 20 horaires.',
    customMealBand: 'Horaire de service',
    deleteMealBand: 'Supprimer {{name}}',
    deleteMealBandTitle: 'Supprimer cet horaire ?',
    deleteMealBandDescription:
      'Voulez-vous vraiment supprimer {{name}} ? Cette action est irréversible.',
    active: 'Actif',
    inactive: 'Inactif',
    shiftLegendOff: '{{name}} : n’accepte pas de réservations',
    shiftLegendRange: '{{name}} : {{from}} - {{to}}',
    booking: 'Réservation',
    activeBand: 'Accepter les réservations',
    activeBandHint:
      'Désactivez ceci si les clients ne peuvent pas réserver pendant cet horaire.',
    deactivate: 'Désactiver',
    deactivated: 'Désactivée',
    activate: 'Activer',
    valueSaved: 'Valeur enregistrée',
    valueSavedHint: 'Les clients pourront réserver dans cette plage horaire.',
    selectBandTitle: 'Sélectionnez la plage avec une seule barre',
    selectBandHint:
      'La partie sélectionnée respecte les autres plages actives.',
    fine: 'Fin',
    spaceAvailable: 'Créneau disponible pour {{name}} : {{from}} - {{to}}',
    minStartValue: 'Début min',
    maxEndValue: 'Fin max',
    bandTurnedOff:
      'Cette plage est désactivée. Activez-la avec l’interrupteur ci-dessus pour modifier les horaires.',
    activityHours: 'Heures d’activité',
    start: 'Début',
    end: 'Fin',
    closuresTitle: 'Jours où le restaurant est fermé',
    closuresHint: 'Les clients ne peuvent pas réserver à ces dates.',
    addClosure: 'Ajouter une fermeture',
    closuresModalHint:
      'Sélectionnez une date de début et de fin pour la période de fermeture.',
    closingPeriod: 'Période de fermeture',
    chooseDateRange: 'Choisir une plage de dates',
    fromDate: 'Date de début',
    toDate: 'Date de fin',
    actions: 'Actions',
    noClosures: 'Aucun jour de fermeture pour le moment.',
    deleteClosure: 'Supprimer la fermeture',
    locationTitle: 'Emplacement',
    trackingTitle: 'Suivi et analytique',
    trackingHint:
      'Configurez le Gestionnaire de balises Google, le Pixel Facebook et les outils de suivi',
    googleTagManager: 'Gestionnaire de balises Google',
    facebookPixel: 'Pixel Facebook',
    tabs: {
      tabListLabel: 'Sections de configuration locale',
      general: 'Général',
      bookingTimeSlots: 'Horaires',
      locationTracking: 'Emplacement et suivi',
    },
    shifts: {
      breakfast: 'Petit-déjeuner',
      breakfastSubtitle: 'Créneau petit-déjeuner',
      lunch: 'Déjeuner',
      lunchSubtitle: 'Créneau déjeuner',
      aperitivo: 'Happy hour',
      aperitivoSubtitle: 'Créneau apéritif',
      dinner: 'Dîner',
      dinnerSubtitle: 'Créneau dîner',
    },
    fields: {
      localName: 'Nom de l’établissement',
      localCapacity: 'Capacité de l’établissement',
      address: 'Adresse',
      city: 'Ville',
      zip: 'Code postal',
      province: 'Province',
      nation: 'Pays',
      state: 'État',
      latitude: 'Latitude',
      longitude: 'Longitude',
      gtmId: 'ID du conteneur',
      pixelId: 'ID du pixel',
    },
    validation: {
      nameRequired: 'Le nom de l’établissement est obligatoire',
      mealBandNameRequired: 'Saisissez un nom',
      mealBandNameDuplicate: 'Un horaire portant ce nom existe déjà',
      openingTimeRequired: "L'heure de début est obligatoire",
      closingTimeRequired: "L'heure de fin est obligatoire",
    },
    toast: {
      saved: 'Configuration enregistrée',
      saveError: 'Impossible d’enregistrer la configuration',
      logoUploadSuccess: 'Logo téléversé avec succès',
      logoUploadError: 'Impossible de téléverser le logo',
      logoRemoveSuccess: 'Logo supprimé avec succès',
      logoRemoveError: 'Impossible de supprimer le logo',
      selectDateRange: 'Sélectionnez une date de début et une date de fin',
      closureAdded: 'Période de fermeture ajoutée',
      closureAddError: 'Impossible d’ajouter la période de fermeture',
      closureOverlap:
        'Cette période chevauche une période de fermeture existante',
      closureRemoved: 'Période de fermeture supprimée',
      closureDeleteError: 'Impossible de supprimer la période de fermeture',
      mealBandCreated: 'Horaire ajouté',
      mealBandCreateError: 'Impossible d’ajouter cet horaire',
      mealBandUpdated: 'Horaire mis à jour',
      mealBandUpdateError: 'Impossible de mettre à jour cet horaire',
      mealBandStatusUpdated: 'Statut des réservations mis à jour',
      mealBandStatusError:
        'Impossible de mettre à jour le statut des réservations',
      mealBandDeleted: 'Horaire supprimé',
      mealBandDeleteError: 'Impossible de supprimer cet horaire',
      mealBandInvalidCode: 'Cet horaire n’a pas pu être enregistré. Réessayez.',
    },
  },
  stripe: {
    common: {
      refresh: 'Actualiser depuis Stripe',
      noBranch: 'Sélectionnez un établissement pour continuer.',
      processing: 'Traitement…',
    },
    configuration: {
      title: 'Configuration Stripe',
      subtitle: 'Données en direct du compte Stripe connecté.',
      subtitleWithBranch: 'Données en direct du compte connecté à {{branch}}.',
      loadError: 'Impossible de récupérer l’état directement depuis Stripe.',
      notConnectedTitle: 'Compte Stripe non configuré',
      notConnectedDescription:
        'L’établissement sélectionné n’a pas encore de compte Stripe connecté. Terminez l’onboarding depuis le backoffice.',
      accountSection: 'Informations du compte',
      accountHeading: 'Compte Stripe',
      email: 'E-mail',
      country: 'Pays',
      currency: 'Devise',
      accountType: 'Type de compte',
      disabledReason: 'Motif du blocage',
      disabledReasonNone: 'Aucun',
      detailsSubmitted: 'Données envoyées',
      chargesEnabled: 'Paiements',
      payoutsEnabled: 'Virements',
      requirements: {
        title: 'Exigences Stripe',
        currentlyDue: 'À compléter',
        pastDue: 'Échues',
        pendingVerification: 'En vérification',
        none: 'Aucune',
      },
      policy: {
        title: 'Politique de paiement',
        description:
          'Texte affiché au client lorsque la réservation nécessite une vérification de carte, une préautorisation ou un paiement.',
        versionMeta: 'Version {{version}} · mise à jour {{date}}',
        useTemplate: 'Proposer une structure de politique',
        templateWarning:
          'Le modèle est un point de départ : remplacez tous les champs entre crochets et faites relire le texte par un conseiller juridique.',
        placeholder: 'Saisissez la politique de paiement de l’établissement…',
        charCount: '{{count}}/{{max}} caractères',
        save: 'Enregistrer et créer une nouvelle version',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: 'Politique de paiement enregistrée.',
        policyError: 'Impossible d’enregistrer la politique de paiement.',
      },
      validation: {
        policyRequired: 'Saisissez le texte de la politique de paiement.',
        policyMax: 'La politique ne peut pas dépasser 20000 caractères.',
      },
    },
    transactions: {
      title: 'Transactions Stripe',
      empty: 'Aucune opération',
      emptyDescription:
        'Aucun paiement ni acompte n’a encore été enregistré pour cet établissement.',
      notConnectedTitle: 'Compte Stripe non connecté.',
      notConnectedDescription:
        'Toutes les transactions historiques enregistrées pour l’établissement restent disponibles ici.',
      filters: {
        all: 'Toutes',
        succeeded: 'Réussies',
        uncaptured: 'Non encaissées',
        failed: 'Échouées',
      },
      columns: {
        date: 'Date',
        booking: 'Réservation',
        operation: 'Opération',
        requested: 'Demandé',
        captured: 'Encaissé',
        status: 'État',
        details: 'Détails',
      },
      mode: {
        payNow: 'Paiement',
        authorizeOnly: 'Acompte / préautorisation',
      },
      status: {
        linkCreated: 'En attente du paiement',
        processing: 'Enregistré, en attente de la confirmation Stripe',
        pending: 'En attente',
        authorized: 'Non encaissé',
        partiallyPaid: 'Partiellement encaissé',
        paid: 'Réussi',
        failed: 'Échoué',
        canceled: 'Annulé',
        refundPending: 'Remboursement en cours',
        partiallyRefunded: 'Partiellement remboursé',
        refunded: 'Remboursé',
        refundFailed: 'Remboursement échoué',
      },
      actions: {
        capture: 'Encaisser l’acompte',
        release: 'Refuser l’acompte',
      },
      confirm: {
        captureTitle: 'Encaisser l’acompte ?',
        captureDescription:
          '{{amount}} seront encaissés pour la réservation {{booking}}.',
        releaseTitle: 'Refuser l’acompte ?',
        releaseDescription:
          'L’autorisation de {{amount}} sera annulée et le montant libéré.',
      },
      toast: {
        captureSuccess: 'Stripe a confirmé l’encaissement.',
        captureError:
          'Encaissement échoué. Vérifiez l’état sur Stripe et réessayez.',
        releaseSuccess: 'Stripe a annulé l’autorisation.',
        releaseError:
          'Libération échouée. Vérifiez l’état sur Stripe et réessayez.',
      },
      technical: {
        title: 'Détails techniques',
        description:
          'Identifiants utilisés par PRO20 et Stripe pour cette opération.',
        open: 'Afficher les détails techniques',
        close: 'Fermer',
        bookingCode: 'Code de réservation',
        depositCode: 'Code de transaction PRO20',
        paymentIntentId: 'PaymentIntent Stripe',
        paymentLinkId: 'Payment Link Stripe',
        connectedAccountId: 'Compte Stripe',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: 'Commande reçue',
      ACCEPTED: 'Commande acceptée',
      PREPARING: 'Commande en préparation',
      READY: 'Commande prête',
      DELIVERED: 'Commande livrée',
      COLLECTED: 'Commande retirée',
      CANCELLED: 'Commande annulée',
      FAILED: 'Livraison non réussie',
      PAYMENT_REQUIRED: 'Lien de paiement',
      PAYMENT_CONFIRMED: 'Paiement reçu',
      PAYMENT_FAILED: 'Paiement non réussi',
      REFUNDED: 'Remboursement confirmé',
      SCHEDULE_REMINDER: 'Rappel de commande',
    },
    orderingFlows: {
      CREATED:
        'Après la création de la commande. Le client sait que le restaurant doit encore l’accepter.',
      ACCEPTED:
        'Lorsque vous acceptez la commande et choisissez d’avertir le client.',
      PREPARING:
        'Lorsque la préparation commence et que vous choisissez d’avertir le client.',
      READY:
        'Lorsque la commande est prête et que vous choisissez d’avertir le client. Pour la livraison, cela ne signifie pas que le livreur est parti.',
      DELIVERED:
        'Lorsque vous traitez individuellement la commande comme livrée et choisissez d’avertir le client. Les actions groupées et du livreur restent silencieuses.',
      COLLECTED:
        'Lorsque vous traitez individuellement la commande comme retirée et choisissez d’avertir le client. Les actions groupées restent silencieuses.',
      CANCELLED:
        'Lorsque vous annulez la commande et choisissez d’avertir le client. Cela ne confirme pas un remboursement.',
      FAILED:
        'Lorsque vous marquez la livraison comme non réussie et choisissez d’avertir le client.',
      PAYMENT_REQUIRED:
        'Uniquement après la création effective d’un lien de paiement valide.',
      PAYMENT_CONFIRMED:
        'Après la confirmation effective du paiement, automatique ou manuelle. Les actions groupées restent silencieuses.',
      PAYMENT_FAILED:
        'Si une tentative de paiement échoue et que la commande reste impayée.',
      REFUNDED: 'Uniquement après le remboursement effectivement terminé.',
      SCHEDULE_REMINDER:
        'Avant l’heure prévue, uniquement pour les commandes acceptées ou en préparation. Les paiements par carte non finalisés sont exclus.',
    },
    ordering: {
      configurationHint:
        'Activez ce message pour les commandes de cet établissement. L’enregistrement n’envoie aucun message.',
      initialize: 'Créer les messages Livraison et À emporter',
      initialized: 'Messages créés. Aucun envoi effectué.',
      loadError:
        'Impossible de charger les messages Livraison et À emporter. Réessayez.',
      missing:
        'Créez les messages manquants. Le contenu déjà personnalisé est conservé. Les nouveaux messages démarrent désactivés.',
      minutes: 'Minutes avant l’heure de la commande',
      scheduleError: 'Saisissez un nombre entier de 1 à 10080 minutes.',
      exclusive:
        'Choisissez WhatsApp non officiel ou un modèle officiel, pas les deux.',
      emailPreset: 'Contenu e-mail par défaut',
      paymentHint:
        'Laissez désactivé si vous envoyez déjà le lien de paiement manuellement sur WhatsApp.',
    },

    searchPlaceholder: 'Rechercher par nom, code, catégorie ou canal…',
    allCategories: 'Toutes les catégories',
    browseCategories: 'Catégories de messages',
    searchEmpty:
      'Aucun résultat. Essayez un autre terme ou sélectionnez toutes les catégories.',

    giftCards: {
      scheduleTitle: 'Rappel avant expiration',
      daysBeforeExpiry: 'Jours avant expiration',
      scheduleHint:
        'Envoyer le rappel ce nombre de jours avant expiration à l’heure choisie dans le fuseau de l’établissement. Exclure les cartes utilisées, annulées ou sans solde.',
      scheduleError:
        'Saisissez un nombre entier positif de jours et une heure entre 00:00 et 23:00.',

      creation: 'Création de carte cadeau',
      expired: 'Carte cadeau expirée',
      walletHint:
        'Le backend fournit wallet_link lors de l’envoi. Le bouton Wallet utilise le suffixe de la carte, jamais un lien client fixe.',

      initialize: 'Créer les messages manquants',
      initializeSuccess: 'Configurations créées.',
      initializeError: 'Le backend ne peut pas créer les configurations.',
      missing:
        'Ces configurations manquent. Créez-les pour associer les modèles officiels WhatsApp et email.',
      notCreated: 'À créer sur le backend',
      loadError:
        'Impossible de charger les configurations Marketing. Réessayez.',
    },
    title: 'Centre de messages',
    subtitle:
      'Gérez tous les messages automatiques WhatsApp, SMS et e-mail de l’établissement.',
    configuredLabel: 'Messages configurés',
    refresh: 'Actualiser',
    emptyCategory: 'Aucun message dans cette catégorie pour le moment.',
    emptyTitle: 'Aucun message trouvé',
    emptyDescription:
      'Aucun message automatique n’est encore configuré pour cet établissement.',
    noBranchTitle: 'Aucun établissement sélectionné',
    noBranchDescription:
      'Sélectionnez un établissement pour voir et gérer les messages automatiques.',
    loadErrorTitle: 'Impossible de charger les messages',
    loadErrorDescription:
      'Échec du chargement de la liste des messages. Veuillez réessayer.',
    categories: {
      promo: 'Promo',
      fidelity: 'Fidelity',
      restaurantOrdering: 'Livraison et à emporter',
      supplierOrders: 'Commandes fournisseurs',
      giftCards: 'Messages des cartes cadeaux',
      standard: 'Messages standard',
      cta: 'CTA',
      marketing: 'Messages marketing',
      preBooking: 'Communications pré-réservation',
      postBooking: 'Communications post-réservation',
      switchboard: 'Messages standard téléphonique',
    },
    categoryDescriptions: {
      promo: 'Messages pour les promotions et les coupons.',
      fidelity: 'Messages pour les programmes de fidélité, les points et les cartes.',
      marketing:
        'Messages promotionnels, de fidélisation et actions automatiques sur les cartes.',
    },
    items: {
      ORDER_SENT: 'Commande envoyée',
      ORDER_UPDATED: 'Commande mise à jour',
      ORDER_DELIVERY: 'Livraison de la commande',
      ORDER_GENERIC: 'Commande fournisseur',

      ATT: 'Message en attente de confirmation',
      ARR: 'Confirmation d’arrivée',
      CONF: 'Confirmation de réservation',
      REF: 'Réservation refusée',
      DEL: 'Réservation supprimée',
      FQ1: 'Gestion de file 1',
      FQ2: 'Gestion de file 2',
      EDTU: 'Mise à jour réservation (utilisateur)',
      EDTC: 'Mise à jour réservation (client)',
      EDTR: 'Mise à jour réservation (restaurant)',
      ANS1: 'Alerte no-show 1',
      ANS2: 'Alerte no-show 2',
      ANS3: 'Alerte no-show 3',
      RPR: 'Rappel promo',
      DOB: 'Promo anniversaire',
      AAD: 'Demande d’avis',
      RRP: 'Promo vous nous manquez',
      CBP: 'Invitation à revenir',
      CENTRALINO1: 'Fonction standard 1',
      CENTRALINO2: 'Fonction standard 2',
      CENTRALINO3: 'Fonction standard 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: 'Fermer',
      pageSubtitle:
        'Configurez les canaux de messages automatiques pour ce modèle.',
      marketingTitle: 'Message marketing',
      enableConfiguration: 'Activer cette configuration',
      enableConfigurationHint:
        'Les modifications s’appliquent à tous les programmes qui utilisent {{type}}.',
      enableWhatsApp: 'Activer WhatsApp',
      enableTemplates: 'Activer les modèles',
      enableEmail: 'Activer Email',
      emailObject: 'Objet',
      configuration: 'Configuration',
      configurationHint: 'Choisissez comment envoyer les messages',
      configurationHintPreBooking:
        'Configurez les méthodes d’envoi et le timing',
      configurationHintPostBooking:
        'Configurez l’envoi et la planification des messages post-réservation',
      sendingMethods: 'Méthodes d’envoi',
      sendingMode: 'Mode d’envoi',
      sendingTiming: 'Timing d’envoi',
      whenToSend: 'Quand envoyer',
      sendingNow: 'Heure d’envoi',
      timingImmediate:
        'Envoyer le message immédiatement après la réservation 🕒',
      timingOneHourBefore: 'Envoyer le message 1 heure avant 🕒',
      timingHoursAdvance: 'Envoyer le message {{count}} heures à l’avance 🕒',
      timingOneDayAdvance: 'Envoyer le message 1 jour à l’avance 🕒',
      timingDaysAdvance: 'Envoyer le message {{count}} jours à l’avance 🕒',
      whenToSendOneDayLater: 'Envoyer le message 1 jour plus tard',
      whenToSendDaysLater: 'Envoyer le message {{count}} jours plus tard',
      sendingWhatsAppOnly: 'Envoyer uniquement WhatsApp',
      sendingWhatsAppAndEmail: 'Envoyer WhatsApp et e-mail',
      whatsappMessage: 'Message WhatsApp',
      whatsappMessageDesc: 'Configurez le message WhatsApp envoyé au client',
      whatsappForeign: 'WhatsApp clients étrangers',
      whatsappForeignDesc:
        'Version anglaise. Message automatique en anglais pour les clients avec indicatif international.',
      whatsappBusinessTemplate: 'Modèle WhatsApp Business',
      whatsappBusinessTemplateDesc:
        'Sélectionnez parmi les modèles approuvés par Meta pour l’envoi via WhatsApp Business API.',
      smsMessage: 'Message SMS',
      smsMessageDesc: 'Configurez le message SMS envoyé au client',
      email: 'E-mail',
      emailDesc: 'Configurez le message e-mail envoyé au client',
      emailSubject: 'Objet de l’e-mail',
      emailSubjectPlaceholder: 'Ex. : Communication de @hotel-name',
      bodyPlaceholder: 'Écrivez votre message ici...',
      inspireMe: 'Inspirez-moi',
      testSending: 'Envoi test',
      testSendPrompt:
        'Saisissez le numéro auquel vous souhaitez envoyer le message',
      testSendAction: 'Envoyer',
      selectTemplate: 'Sélectionner un modèle',
      selectTemplateTooltip: 'Sélectionnez un modèle approuvé par Meta',
      selectTemplateHint:
        'Choisissez un modèle approuvé par Meta pour ce message.',
      selectedTemplate: 'Modèle sélectionné',
      noTemplateSelected: 'Aucun modèle sélectionné',
      noTemplateHint:
        'Aucun modèle sélectionné. Sélectionnez un modèle approuvé par Meta pour configurer l’envoi via WhatsApp Business API.',
      templateCategory: 'Catégorie',
      templateType: 'Type',
      templateLanguages: 'Langues',
      templateLanguage: 'Langue',
      templateVariableMap: 'Carte des variables',
      templateBody: 'Corps',
      templateBodyEmpty: 'Aucun aperçu du corps disponible pour cette langue.',
      ctaButton: 'Appel à l’action',
      ctaDynamicPath: 'Chemin dynamique',
      ctaDynamicPathPlaceholder:
        'Chemin dynamique, ex. /menu/xxxx0111 ou /booking/0000101111',
      ctaDynamicPathHint:
        'Il est ajouté à l’URL du modèle lors de l’envoi, à la place de {{1}}, pour créer des liens selon le contexte du message.',
      ctaDynamicPathRequired: 'Le chemin dynamique ne peut pas être vide.',
      ctaUrlValid: '{{url}} est valide',
      ctaUrlInvalid: 'URL invalide. Vérifiez le format.',
      ctaLinksTooltip: 'Quelles sont mes options ?',
      ctaLinksTitle: 'Liens disponibles pour les services créés sur Pro20',
      ctaLinksDesc:
        'Sélectionnez un lien pour l’utiliser comme chemin dynamique.',
      ctaLinksEmpty:
        'Vous n’avez encore créé aucun service sur Pro20, il n’y a donc rien à suggérer.',
      ctaLinkUnnamed: 'Lien sans nom',
    },
    toast: {
      refreshed: 'Liste des messages actualisée.',
      saveSuccess: 'Configuration du message enregistrée.',
      saveError: 'Impossible d’enregistrer la configuration du message.',
      testSent: 'Message de test envoyé avec succès.',
      sendError: 'Impossible d’envoyer le message.',
      templateSelected: 'Modèle sélectionné.',
      resetSuccess: 'Configuration restaurée aux valeurs par défaut.',
      resetError:
        'Impossible de réinitialiser la configuration. Veuillez réessayer.',
      applyDefaultsSuccess: 'Textes par défaut appliqués et canal activé.',
      applyDefaultsError:
        'Impossible d’appliquer les valeurs par défaut. Réessayez ou annulez.',
      detailUnavailable:
        "Le détail du message n'est pas encore disponible pour cette catégorie.",
    },
    validation: {
      whatsappBodyRequired:
        'Saisissez un message WhatsApp lorsque WhatsApp est activé.',
      templateRequired:
        'Sélectionnez un modèle WhatsApp Business lorsque les modèles sont activés.',
      emailSubjectRequired:
        'Saisissez l’objet de l’e-mail lorsque Email est activé.',
      emailBodyRequired:
        'Saisissez le corps de l’e-mail lorsque Email est activé.',
    },
    reset: {
      tooltip: 'Restaurer la configuration par défaut',
      title: 'Réinitialiser la configuration ?',
      description:
        'Les configurations existantes seront remplacées par la configuration par défaut.',
      confirm: 'Oui, continuer',
      cancel: 'Annuler',
      resetting: 'Réinitialisation…',
    },
    applyDefaults: {
      whatsappTooltip:
        'Appliquer les textes par défaut et activer WhatsApp pour ce groupe',
      emailTooltip:
        'Appliquer l’objet et le texte par défaut et activer l’e-mail pour ce groupe',
      whatsappAria: 'Appliquer la configuration WhatsApp par défaut',
      emailAria: 'Appliquer la configuration e-mail par défaut',
      whatsappTitle: 'Appliquer les valeurs par défaut WhatsApp ?',
      emailTitle: 'Appliquer les valeurs par défaut e-mail ?',
      description:
        'Les textes du canal choisi seront remplacés et le canal sera activé pour tout le groupe. L’activation des messages, les autres canaux et les règles d’envoi restent inchangés.',
      marketingWhatsappNote:
        'WhatsApp utilisera le message libre ; les données du modèle officiel seront conservées.',
      confirm: 'Appliquer et activer',
      cancel: 'Annuler',
      applying: 'Application…',
    },
  },
  configureWhatsApp: {
    title: 'Configurer WhatsApp',
    subtitle: 'Connectez un numéro WhatsApp Business pour cette filiale',
    disabled: {
      badge: 'Configuration désactivée',
      title: 'Configuration WhatsApp non disponible',
      description:
        'Passez par l’application mobile pour activer cette fonctionnalité.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: 'Connecter le numéro WhatsApp local',
      description:
        'Créez une nouvelle instance, scannez le QR code et activez l’envoi de messages automatiques depuis la plateforme PRO20.',
      create: 'Créer la configuration',
      guided: 'Opération guidée et réversible',
      featureSecureTitle: 'Sécurisé et fiable',
      featureSecureDesc: 'Sécurité de niveau entreprise',
      featureQuickTitle: 'Configuration rapide',
      featureQuickDesc: 'Moins de 2 minutes',
      featureReversibleTitle: 'Réversible',
      featureReversibleDesc: 'Modifiez ou déconnectez à tout moment',
      step1Title: 'Créer l’instance',
      step1Desc:
        'Générez la configuration technique liée à la filiale sélectionnée.',
      step1Time: '~ 30 sec',
      step2Title: 'Scanner le QR',
      step2Desc:
        'Ouvrez WhatsApp sur votre téléphone et connectez votre appareil via QR code.',
      step2Time: '~ 60 sec',
      step3Title: 'Essayer l’envoi',
      step3Desc: 'Envoyez un message de test et vérifiez que tout fonctionne.',
      step3Time: '~ 30 sec',
    },
    creating: {
      title: 'Configuration en cours…',
      description:
        'Veuillez patienter pendant la création de l’instance WhatsApp.',
      starting:
        'WhatsApp démarre. La première connexion peut prendre quelques minutes.',
    },
    success: {
      title: 'Terminé',
      description: 'Instance créée avec succès !',
      banner:
        'Vous pouvez maintenant lier votre appareil avec un QR code ou un code d’association.',
      continue: 'Continuer',
    },
    configured: {
      heading: 'Instance WhatsApp',
      subtitle:
        'Gérez la liaison, le statut et les messages de test pour cette filiale.',
      statusInstanceCreated: 'Instance créée',
      statusQr: 'En attente du scan QR',
      statusReady: 'Connecté',
      statusCreating: 'Création…',
      statusFailed: 'Connexion échouée',
      restart: 'Relancer la connexion',
      stillStarting:
        'WhatsApp démarre encore. Cette page continue de vérifier. Utilisez Scanner le QR lorsque le code est prêt, ou relancez la connexion.',
      failureHint:
        'Le moteur WhatsApp n’a pas démarré. Relancez la connexion pour réessayer.',
      scanQr: 'Scanner le QR',
      delete: 'Supprimer',
      deleting: 'Suppression...',
      statusDeleting: 'Suppression...',
      sessionName: 'Nom de session',
      instanceId: 'ID d’instance',
      connectedNumber: 'Numéro connecté',
      operatingStatus: 'Statut opérationnel',
      nextStepTitle: 'Prochaine étape',
      nextStepQr:
        'Scannez le QR code avec WhatsApp sur votre téléphone pour lier l’appareil.',
      notLinked: 'Pas encore lié',
    },
    link: {
      title: 'Lier l’appareil',
      description:
        'Utilisez un QR code ou un code d’association pour connecter WhatsApp.',
      tabQr: 'QR code',
      tabPhone: 'Code mobile',
      qrInstructions:
        'Ouvrez WhatsApp sur votre téléphone → Appareils connectés → Connecter un appareil, puis scannez ce code.',
      waiting: 'En attente du scan…',
      phoneInstructions:
        'Saisissez le numéro mobile à lier, puis demandez un code d’association.',
      phoneLabel: 'Numéro mobile',
      phonePlaceholder: '333 123 4567',
      getCode: 'Obtenir le code',
      pairingCode: 'Votre code d’association',
      pairingHint:
        'Saisissez ce code dans WhatsApp → Appareils connectés → Lier avec un numéro de téléphone.',
    },
    delete: {
      title: 'Supprimer la configuration WhatsApp ?',
      description:
        'Cela supprimera l’instance pour cette filiale. Vous pourrez en créer une nouvelle plus tard.',
      confirm: 'Oui, supprimer',
    },
    test: {
      title: 'Envoyer un message de test',
      phone: 'Numéro du destinataire',
      phonePlaceholder: '333 123 4567',
      message: 'Message',
      defaultMessage: 'Bonjour ! Ceci est un message de test de Proventi.',
      send: 'Envoyer le test',
      success: 'Message de test envoyé avec succès.',
      disabledHint:
        'Scannez le QR code et liez WhatsApp avant d’envoyer un message de test.',
      notConnected:
        'La session WhatsApp n’est pas connectée. Scannez le QR code pour lier d’abord votre appareil.',
    },
    toast: {
      linked: 'WhatsApp lié avec succès.',
      stillStarting:
        'WhatsApp démarre encore. Cette page continue de vérifier.',
      createFailed: 'Impossible de créer la session WhatsApp.',
      qrFailed: 'Impossible de préparer le QR WhatsApp.',
    },
  },
  configureWhatsAppOfficial: {
    title: 'Configurer WhatsApp Officiel',
    subtitle:
      'Connectez votre numéro WhatsApp Business officiel (Meta) pour cette filiale.',
    loading: 'Chargement du statut d’intégration…',
    loadError: 'Échec du chargement du statut d’intégration.',
    retry: 'Réessayer',
    toast: {
      onboardSuccess: 'Intégration WhatsApp terminée avec succès.',
      onboardError: 'Échec de l’intégration WhatsApp.',
      registerSuccess: 'Numéro de téléphone enregistré avec succès.',
      registerError: 'Échec de l’enregistrement du numéro de téléphone.',
      businessDetailsSuccess: 'Détails de l’entreprise chargés avec succès.',
      businessDetailsError: 'Échec du chargement des détails de l’entreprise.',
      missingPhoneNumberId: 'L’ID du numéro de téléphone est manquant.',
      metaError: 'Erreur d’inscription WhatsApp : {{message}}',
      missingBranch: 'Code de filiale manquant.',
      sdkNotReady: 'Le SDK Facebook n’est pas encore prêt.',
    },
    onboarding: {
      eyebrow: 'API WhatsApp Business',
      title: 'Se connecter avec WhatsApp Business API',
      description:
        'Dans cette section, vous pouvez créer et connecter un numéro au canal WhatsApp Business officiel. La configuration vous permet d’associer votre numéro professionnel à la plateforme pour commencer à envoyer et recevoir des messages de manière professionnelle, automatisée et conforme aux règles.',
      connect: 'Créer la configuration',
      connecting: 'Connexion…',
      howToConnect: 'Comment se connecter',
      featuresTitle: 'Avec Meta, vous pouvez',
      featureExisting: 'Connecter un numéro WhatsApp Business existant',
      featureNew: 'Créer un nouveau compte avec Meta',
      step1Title: 'Enregistrez votre numéro',
      step1Desc:
        'Ajoutez et vérifiez le numéro de téléphone de votre entreprise avec Meta pour activer le canal.',
      step1Time: '~ 2 min',
      step2Title: 'Créer un modèle et le faire approuver',
      step2Desc:
        'Créez un modèle de message et envoyez-le à Meta pour examen et approbation.',
      step2Time: '~ 1 jour',
      step3Title: 'Envoyer un modèle texte',
      step3Desc:
        'Envoyez un modèle approuvé pour démarrer une nouvelle conversation.',
      step3Time: '~ 30 s',
      step4Title: 'Envoyer à un numéro de téléphone',
      step4Desc:
        'Envoyez des messages à n’importe quel numéro de téléphone client et commencez à discuter.',
      step4Time: '~ 30 s',
    },
    configured: {
      heading: 'Informations d’intégration WhatsApp',
      subtitle: 'Votre compte WhatsApp Business officiel est connecté.',
      connectedBadge: 'Connecté',
      phoneNumberId: 'ID du numéro de téléphone',
      wabaId: 'ID du compte WhatsApp Business',
      businessId: 'ID de l’entreprise',
      onboardingStatus: 'Statut d’intégration',
      phoneRegistrationStatus: 'Statut d’enregistrement du numéro',
      statusCompleted: 'Terminé',
      statusSuccess: 'Succès',
      statusPending: 'En attente',
      statusRegistered: 'Enregistré',
      statusNotRegistered: 'Non enregistré',
      registerPhone: 'Enregistrer votre numéro',
      registeringPhone: 'Enregistrement…',
      businessDetailsHeading: 'Détails de l’entreprise',
      businessDetailsEmpty:
        'Les détails de l’entreprise ne sont pas encore disponibles. Récupérez-les depuis Meta avec l’ID de votre numéro.',
      fetchBusinessDetails: 'Obtenir les détails',
      fetchingBusinessDetails: 'Chargement…',
      verifiedName: 'Nom vérifié',
      displayPhoneNumber: 'Numéro affiché',
      qualityRating: 'Note de qualité',
      codeVerificationStatus: 'Vérification du code',
      platformType: 'Type de plateforme',
      throughputLevel: 'Débit',
      phoneStatus: 'Statut du téléphone',
      accountMode: 'Mode du compte',
      nameStatus: 'Statut du nom',
      messagingLimitTier: 'Limite de messages',
      officialBusinessAccount: 'Compte professionnel officiel',
      businessProfileHeading: "Profil de l'entreprise",
      email: 'E-mail professionnelle',
      vertical: "Catégorie d'activité",
      description: "Description de l'entreprise",
      about: 'À propos',
      address: 'Adresse',
      websites: 'Sites web',
      metaPhoneNumberId: 'ID numéro Meta',
      webhookApplication: 'URL du webhook',
      reset: 'Réinitialiser la configuration',
      resetting: 'Réinitialisation…',
    },
    reset: {
      title: 'Réinitialiser la configuration ?',
      description:
        'Cela réinitialise uniquement la vue locale pour relancer la configuration. Cela ne supprime pas l’intégration côté serveur.',
      confirm: 'Oui, réinitialiser',
    },
    guide: {
      title: 'Guide d’intégration de l’API WhatsApp Business',
      intro:
        'Bienvenue ! Suivez les étapes ci-dessous pour connecter votre compte WhatsApp Business à notre plateforme.',
      prerequisitesTitle: 'Prérequis',
      prerequisitesIntro:
        'Avant de commencer, assurez-vous de disposer des éléments suivants :',
      prereq1: 'Un compte Facebook avec un accès Administrateur.',
      prereq2: 'Un compte Meta Business Manager (ou être prêt à en créer un).',
      prereq3:
        'Un numéro de mobile valide pouvant recevoir une vérification par SMS ou appel vocal.',
      prereq4:
        'Un numéro de téléphone non enregistré actuellement avec WhatsApp Messenger, l’application WhatsApp Business ou un autre fournisseur d’API WhatsApp Business.',
      prereq5:
        'Les informations de votre entreprise prêtes : nom, site web (facultatif), e-mail et catégorie.',
      prereq6:
        'Une carte de crédit/débit valide à ajouter comme moyen de paiement dans Meta.',
      prereq7: 'Une connexion internet stable.',
      prereqNote:
        'Important : si votre numéro de téléphone est actuellement utilisé dans l’application WhatsApp Business, supprimez-le de l’application avant de le connecter à la WhatsApp Business Platform.',
      stepsTitle: 'Étapes d’intégration',
      step1Title: 'Étape 1 – Connectez-vous à notre plateforme',
      step1Body:
        'Connectez-vous à votre compte, accédez à Intégration WhatsApp et cliquez sur Connecter WhatsApp.',
      step2Title: 'Étape 2 – Connectez-vous avec Facebook',
      step2Body:
        'Une fenêtre de connexion sécurisée Meta (Facebook) s’ouvre. Connectez-vous avec votre compte Facebook et cliquez sur Continuer.',
      step3Title: 'Étape 3 – Sélectionnez ou créez un Business Manager',
      step3Body:
        'Sélectionnez votre Meta Business Manager existant ou cliquez sur Créer un compte professionnel et saisissez le nom, l’e-mail, le site web et le pays de votre entreprise, puis cliquez sur Suivant.',
      step4Title: 'Étape 4 – Créez ou sélectionnez un compte WhatsApp Business',
      step4Body:
        'Utilisez un compte WhatsApp Business existant ou créez-en un nouveau en fournissant le nom du compte, le nom affiché et la catégorie d’entreprise, puis cliquez sur Continuer.',
      step5Title: 'Étape 5 – Ajoutez votre numéro de téléphone',
      step5Body:
        'Sélectionnez votre pays, saisissez votre numéro de téléphone, choisissez la vérification par SMS ou appel vocal, puis saisissez le code de vérification à 6 chiffres.',
      step6Title: 'Étape 6 – Accordez les autorisations',
      step6Body:
        'Meta demandera les autorisations nécessaires. Cliquez sur Continuer puis Terminer ; notre plateforme finalise la configuration automatiquement.',
      step7Title: 'Étape 7 – Connexion terminée',
      step7Body:
        'Une fois connecté, vous verrez votre numéro de téléphone connecté, votre compte WhatsApp Business, l’état de la connexion et serez prêt à envoyer et recevoir des messages.',
      nextStepsTitle: 'Prochaines étapes recommandées',
      nextStep1: 'Créez vos modèles de messages WhatsApp.',
      nextStep2:
        'Ajoutez un moyen de paiement valide dans Meta Business Manager.',
      nextStep3: 'Complétez la vérification d’entreprise Meta (recommandé).',
      nextStep4: 'Envoyez un message modèle de test.',
      nextStep5:
        'Configurez votre chatbot ou votre automatisation (le cas échéant).',
      notesTitle: 'Remarques importantes',
      note1:
        'Un numéro de téléphone ne peut être connecté qu’à un seul compte WhatsApp à la fois.',
      note2:
        'Ne supprimez pas votre compte WhatsApp Business de Meta après l’intégration.',
      note3:
        'Maintenez votre moyen de paiement actif pour éviter les interruptions de livraison des messages.',
      note4:
        'La vérification d’entreprise est recommandée pour des limites de messagerie plus élevées.',
      note5:
        'L’approbation du nom affiché est gérée par Meta et peut prendre un certain temps.',
      issuesTitle: 'Problèmes courants',
      issue1Title: 'Code de vérification non reçu',
      issue1Body:
        'Vérifiez que le numéro de téléphone est correct, essayez l’option appel vocal et attendez quelques minutes avant de demander un autre code.',
      issue2Title: 'Numéro de téléphone déjà enregistré',
      issue2Body:
        'Le numéro est connecté à WhatsApp Messenger, à l’application WhatsApp Business ou à un autre fournisseur d’API WhatsApp Business. Retirez-le du service existant avant l’intégration.',
      issue3Title: 'Erreur d’autorisation',
      issue3Body:
        'Assurez-vous d’être Administrateur à la fois du compte Facebook et du Meta Business Manager.',
      issue4Title: 'Paiement requis',
      issue4Body:
        'Meta exige un moyen de paiement valide avant de pouvoir initier certaines conversations. Ajoutez votre moyen de paiement dans Meta Business Manager.',
      issue5Title: 'Vérification d’entreprise en attente',
      issue5Body:
        'Votre compte peut toujours être connecté et utilisé, mais il est fortement recommandé de compléter la vérification d’entreprise pour débloquer des limites de messagerie plus élevées et améliorer la confiance du compte.',
      needHelpTitle: 'Besoin d’aide ?',
      needHelpIntro:
        'Si vous rencontrez un problème lors de l’intégration, contactez notre équipe d’assistance avec :',
      help1: 'Une capture d’écran de l’erreur.',
      help2: 'Le numéro de téléphone (indicatif pays inclus).',
      help3: 'L’ID Meta Business Manager (si disponible).',
      help4: 'L’ID du compte WhatsApp Business (si disponible).',
      needHelpOutro:
        'Fournir ces informations nous aidera à résoudre votre problème plus rapidement.',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: 'Section non activée',
      title: 'Cette section n’est pas activée',
      description:
        'Cette fonctionnalité est actuellement désactivée pour votre compte. Contactez le support pour en savoir plus.',
      infoTitle: 'Besoin d’accéder à cette section ?',
      infoDescription:
        'Contactez notre équipe d’assistance et nous vous aiderons à démarrer.',
      footerHelp: 'Besoin d’aide encore ?',
      footerHelpDesc:
        'Notre équipe d’assistance est disponible 24h/24 et 7j/7.',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: 'Envoi…',
      retry: 'Réessayer',
      retryConfirmTitle: 'Réessayer le message ?',
      retryConfirmMessage: 'Voulez-vous vraiment renvoyer ce message ?',
      billingFree: 'Gratuit',
      billingPaid: 'Payant',
    },
    delivery: {
      warningTooltip: 'Non livré — appuyez pour les détails',
      detailsTitle: 'Problème de livraison',
      fallbackTitle: 'Le message n’a pas été livré',
      technicalDetails: 'Détails techniques',
      noTechnicalDetails: 'Aucun détail technique disponible.',
    },
    composer: {
      placeholder: 'Écrire un message...',
      send: 'Envoyer',
      submitTemplate: 'Envoyer un modèle',
      templateOnlyTitle: 'Modèle requis',
      templateOnlyDescription:
        'La fenêtre de service de 24 heures est inactive. Envoyez un modèle approuvé pour rouvrir la conversation.',
      attach: 'Joindre un fichier',
      attachImage: 'Image',
      attachAudio: 'Audio',
      attachDocument: 'Document',
      removeAttachment: 'Supprimer la pièce jointe',
    },
    templateSend: {
      title: 'Envoyer un modèle',
      fillVariablesTitle: 'Remplir les variables',
      customerLabel: 'Client',
      phoneLabel: 'Téléphone du destinataire',
      prefix: 'Préfixe',
      number: 'Numéro',
      sendingTo: 'Envoi à',
      phoneInvalid: 'Saisissez le préfixe et le numéro avant d’envoyer.',
      enterValuePlaceholder: 'Saisir une valeur',
      fieldRequired: 'Ce champ est obligatoire',
      ctaUrlSuffix: 'Suffixe URL CTA',
      ctaUrlSuffixPlaceholder: 'Suffixe URL',
      ctaUrlSuffixHint:
        'Ajouté à l’URL du bouton lorsque le modèle a un lien dynamique.',
      noVariables: 'Ce modèle n’a aucune variable à remplir.',
      sendMessage: 'Envoyer le message',
      cancel: 'Annuler',
      previewTitle: 'Aperçu',
      infoCategory: 'Catégorie',
      infoLanguage: 'Langue',
      infoType: 'Type',
      infoSendingTo: 'Envoi à',
    },
    toast: {
      sendSuccess: 'Message envoyé.',
      sendError: 'Impossible d’envoyer le message.',
      templateSendSuccess: 'Modèle envoyé.',
      templateSendError: 'Impossible d’envoyer le modèle.',
      loadConversationsError: 'Impossible de charger les conversations.',
      loadMessagesError: 'Impossible de charger les messages.',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: 'Groupes de modèles',
    allGroups: 'Tous les modèles',
    allStatuses: 'Tous les statuts',
    filterStatus: 'Filtrer par statut',
    searchEmpty: 'Aucun modèle ne correspond à la recherche ou aux filtres.',
    groups: {
      UTILITY: 'Service',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Authentification',
      OTHER: 'Autres',
    },
    title: 'Modèles',
    subtitle:
      'Organisez les modèles pour un accès rapide et développez la communication avec des messages prédéfinis.',
    learnMore: 'En savoir plus',
    importGlobal: 'Importer un modèle global',
    addTemplate: 'Ajouter un modèle',
    tableTitle: 'Modèles',
    sync: 'Synchroniser les modèles',
    searchPlaceholder: 'Rechercher des modèles...',
    emptyTitle: 'Aucun modèle trouvé',
    emptyDescription:
      'Créez ou synchronisez des modèles pour commencer à messager les clients.',
    detailsTitle: 'Détails du modèle',
    deleteTitle: 'Supprimer le modèle ?',
    deleteDescription:
      'Voulez-vous vraiment supprimer « {{name}} » ? Cette action est irréversible.',
    columns: {
      statusDate: 'Statut / Date',
      nameType: 'Nom / Type',
      category: 'Catégorie',
      language: 'Langue',
      actions: 'Actions',
    },
    stats: {
      approved: 'Modèles approuvés',
      pending: 'Modèles en attente',
      draft: 'Modèles en brouillon',
      rejected: 'Modèles refusés',
    },
    status: {
      APPROVED: 'APPROVED',
      PENDING: 'PENDING',
      DRAFT: 'DRAFT',
      REJECTED: 'REJECTED',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: 'Actions du modèle',
      edit: 'Modifier',
      delete: 'Supprimer',
    },
    toast: {
      learnMoreSoon: 'La documentation sera bientôt disponible.',
      importSoon: 'L’importation globale sera bientôt disponible.',
      createSoon: 'La création de modèles sera bientôt disponible.',
      syncSuccess: 'Modèles synchronisés avec succès.',
      syncError: 'Impossible de synchroniser les modèles.',
      importSuccess: 'Modèles globaux importés.',
      importError: 'Impossible d’importer les modèles globaux.',
      loadError: 'Impossible de charger les modèles.',
      deleteSuccess: 'Modèle supprimé.',
      deleteError: 'Impossible de supprimer le modèle.',
    },
    create: {
      title: 'Nouveau modèle',
      subtitle: 'Créez facilement de nouveaux modèles selon vos besoins.',
      backToList: 'Retour aux modèles',
      formTitle: 'Ajouter un nouveau modèle',
      editTitle: 'Modifier le modèle',
      editSubtitle:
        'Mettez à jour un modèle rejeté et renvoyez-le pour examen.',
      editFormTitle: 'Modifier le modèle',
      category: 'Catégorie',
      name: 'Nom du modèle',
      namePlaceholder: 'ex. booking_confirmation_v1',
      language: 'Langue du modèle',
      typeLabel: 'Type de modèle',
      types: {
        text: 'Texte',
        image: 'Image',
        video: 'Vidéo',
        audio: 'Audio',
        document: 'Document',
      },
      categories: {
        UTILITY: 'Utilitaire',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Authentification',
      },
      uploadFile: 'Téléverser un fichier',
      browse: 'Parcourir…',
      uploading: 'Téléversement…',
      clearMedia: 'Effacer',
      noFileChosen: 'Aucun fichier choisi',
      orPasteUrl: 'Ou coller handle / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder:
        'Média d’en-tête téléversé (aperçu indisponible)',
      mediaHintImage: 'JPEG ou PNG uniquement, maximum 5 Mo.',
      mediaHintVideo: 'MP4 uniquement, maximum 16 Mo, max 60 secondes.',
      mediaHintAudio: 'MP3, OGG ou M4A, maximum 16 Mo.',
      mediaHintDocument: 'PDF ou documents Office, maximum 100 Mo.',
      heading: 'En-tête (optionnel)',
      headingHint: 'Affiché au-dessus du corps du message.',
      body: 'Corps',
      footer: 'Pied de page',
      variablesTitle: 'Variables',
      variables: {
        requested_amount: 'Acompte demandé',
        balance_due: 'Solde restant',
        paid_total: 'Total reçu',
        deposit_percentage: 'Pourcentage d’acompte',
        payment_link: 'Lien de paiement de l’acompte',

        service_name: 'Service',
        order_number: 'Numéro de commande',
        ordering_channel: 'À emporter / Livraison',
        scheduled_label: 'Date et heure de la commande',
        total_amount: 'Total de la commande',
        order_link: 'Lien du récapitulatif de commande',
        delivery_address: 'Adresse de livraison',
        pickup_address: 'Adresse de retrait',
        paid_amount: 'Montant payé',
        refund_amount: 'Montant remboursé',
        cancellation_reason: 'Motif d’annulation',
        failure_reason: 'Motif d’échec de livraison',
        amount_due: 'Montant dû',

        customerName: 'Nom du client',
        branchName: 'Nom de l’établissement',
        bookingId: 'ID de réservation',
        discountCode: 'Code de réduction',
        promoCode: 'Code promo',
        bookingDate: 'Date de réservation',
        bookingTime: 'Heure de réservation',
        bookingUpdateLink: 'Lien de modification',
        branchCode: 'Code établissement',
        guestCount: 'Nombre d’invités',
        childrenCount: 'Nombre d’enfants',
        creditCardRequestUrl: 'URL de demande de carte de crédit',
        depositRequestLink: 'Lien de demande d’acompte',
        depositAmount: 'Montant de l’acompte',
        expectedLeaveTime: 'Heure de départ prévue',
        qrCodeImageUrl: 'URL de l’image du code QR',
      },
      buttonsTitle: 'Boutons',
      buttonType: 'Type de bouton',
      buttonText: 'Texte du bouton',
      buttonUrl: 'URL du bouton',
      quickReply: 'Réponse rapide',
      ctaUrl: 'CTA URL',
      addQuickReply: 'Réponse rapide',
      addCta: 'CTA',
      removeButton: 'Supprimer',
      buttonsHint: 'Vous pouvez ajouter jusqu’à 3 boutons par modèle.',
      submit: 'Envoyer',
      previewTitle: 'Aperçu WhatsApp',
      previewEmpty: 'L’aperçu du modèle apparaîtra ici.',
      validation: {
        categoryRequired: 'Sélectionnez une catégorie.',
        nameMin: 'Le nom doit contenir au moins 3 caractères.',
        nameMax: 'Le nom peut contenir au maximum 60 caractères.',
        nameFormat:
          'Utilisez uniquement des minuscules, chiffres et underscores.',
        languageRequired: 'Sélectionnez une langue.',
        bodyRequired: 'Le corps est obligatoire.',
        bodyMax: 'Le corps peut contenir au maximum 1024 caractères.',
        mediaRequired:
          'Le média est obligatoire pour les modèles image et vidéo.',
        invalidImageType: 'L’image doit être au format JPEG ou PNG.',
        invalidVideoType: 'La vidéo doit être au format MP4.',
        imageTooLarge: 'L’image doit faire 5 Mo ou moins.',
        videoTooLarge: 'La vidéo doit faire 16 Mo ou moins.',
        videoDurationMax: 'La vidéo doit durer 60 secondes ou moins.',
        videoDurationUnreadable: 'Impossible de lire la durée de la vidéo.',
        buttonLabelRequired: 'Le texte du bouton est obligatoire.',
        buttonUrlRequired: 'Les boutons CTA nécessitent une URL.',
        qrPlaceholder:
          'Le texte de réponse rapide ne peut pas contenir de variables.',
        qrMax:
          'Le texte de réponse rapide doit contenir 25 caractères ou moins.',
        urlInvalid: 'L’URL CTA doit être une adresse http(s) valide.',
      },
      toast: {
        success: 'Modèle créé avec succès.',
        error: 'Impossible de créer le modèle.',
        updateSuccess: 'Modèle mis à jour avec succès.',
        updateError: 'Impossible de mettre à jour le modèle.',
        uploadSuccess: 'Média d’en-tête téléversé.',
        uploadFailed: 'Impossible de téléverser le média d’en-tête.',
      },
    },
  },
  notifications: {
    title: 'Notifications',
    allCaughtUp: 'Tout est à jour',
    unread: 'notification non lue',
    unreadPlural: 'notifications non lues',
    markAllRead: 'Tout marquer comme lu',
    noNotifications: 'Aucune notification',
    noNotificationsDesc:
      'Vous êtes à jour. Les nouvelles notifications apparaîtront ici.',
    viewAll: 'Voir toutes les notifications',
  },
  bookings: {
    history: {
      title: 'Historique des réservations',
      subtitle: 'Réservations pour {{branch}}',
      subtitleDefault: "Voir et gérer l'historique des réservations",
      totalBookings: '{{count}} réservations',
      bookingsForDateBefore: '{{count}} réservations pour le ',
      bookingsForDateAfter: '',
      previousDay: 'Jour précédent',
      nextDay: 'Jour suivant',
      calendar: {
        title: 'Sélectionner une date de réservation',
        openCalendar: 'Ouvrir le calendrier des réservations',
        previousMonth: 'Mois précédent',
        nextMonth: 'Mois suivant',
        bookings: '{{count}} réservations',
        arrived: '{{count}} arrivés',
        refused: '{{count}} refusés',
        legendBookings: 'Réservations',
        legendArrived: 'Arrivés',
        legendRefused: 'Refusés',
      },
      calendarConfig: {
        eyebrow: 'Calendrier des réservations',
        title: 'Configurer ce jour',
        subtitle:
          'Modifiez uniquement la date sélectionnée dans le calendrier.',
        branchLabel: 'Succursale',
        singleDay: 'Ce jour uniquement',
        updateDate: 'Mettre à jour {{date}}',
        dateRange: 'Plage de dates',
        dateRangeHint: 'Créer ou écraser plusieurs jours',
        eventLabel: 'Récurrence ou événement',
        eventPlaceholder: 'Ajouter une récurrence ou un nom d’événement',
        daysSelected: '{{count}} jour sélectionné',
        daysSelectedPlural: '{{count}} jours sélectionnés',
        rangePlaceholder: 'Sélectionner une plage de dates',
        dateRangeRequired: 'Veuillez sélectionner une date de début et de fin.',
        rulesTitle: 'Règles de réservation',
        manual: 'Manuel',
        acceptTo: 'Accepter jusqu’à',
        acceptToValue: 'Nombre',
        acceptToRequired: 'Ce champ est obligatoire',
        acceptAll: 'Tout accepter',
        refuseAll: 'Tout refuser',
        manualHint:
          'Manuel laisse la décision au flux normal de l’app/portail.',
        close: 'Fermer',
        saveDay: 'Enregistrer le jour',
        saveRange: 'Enregistrer la plage',
        loadError: 'Impossible de charger la configuration du jour.',
        saveSuccess: 'Configuration du jour enregistrée.',
        saveError: 'Impossible d’enregistrer la configuration du jour.',
      },
      exportPdf: "Télécharger le PDF des réservations d'aujourd'hui",
      exportPdfGeneratingTitle: 'Génération du PDF',
      exportPdfGeneratingDescription:
        'Préparation du fichier des réservations...',
      exportPdfReadyTitle: 'PDF prêt',
      exportPdfReadyDescription: 'Le fichier a été généré avec succès.',
      exportPdfOpen: 'Ouvrir le PDF',
      exportPdfClose: 'Fermer',
      exportPdfError: 'Échec de la génération du PDF. Veuillez réessayer.',
      addBooking: 'Ajouter une réservation',
      shifts: {
        all: 'Tous',
        filterLabel: 'Service',
        breakfast: 'Petit-déjeuner',
        daily: 'Déjeuner',
        happyhour: 'Happy hour',
        night: 'Dîner',
      },
      searchPlaceholder:
        'Rechercher client, email, téléphone ou code réservation',
      filterStatus: 'Filtrer les statuts',
      callCustomer: 'Appeler {{name}}',
      moreActions: 'Plus d’actions',
      snapshot: {
        bookings: 'Réservations',
        confirmed: 'Confirmé',
        arrived: 'Arrivés',
        waiting: 'En attente',
        issues: 'No-show / refusés',
        configureShifts: 'Configurer les règles du jour',
        filterShift: 'Filtrer {{shift}}',
        shiftSelected: '{{shift}} sélectionné — cliquer pour retirer',
        shiftUnavailable:
          '{{shift}} n’est pas configuré pour cet établissement',
      },
      itemsPerPage: 'Éléments par page',
      range: '{{start}} - {{end}} sur {{total}}',
      date: 'Date',
      time: 'Heure',
      timeAndGuests: 'Heure et convives',
      customer: 'Client',
      guests: 'Convives',
      guestCount: '{{count}} convives',
      childrenCount: '+ {{count}} enfants',
      event: 'Événement',
      noEvent: 'no event',
      contacts: 'Contacts',
      details: 'Détails',
      openImage: "Ouvrir l'image",
      openPdf: 'Ouvrir le PDF de la réservation',
      googleReservation: 'Réservation depuis Google Reservation',
      walkIn: 'Walk in',
      associatedServicesProducts: 'Services/produits associés',
      generatedBooking: 'Réservation générée {{score}}/100',
      extraServices: 'Services supplémentaires {{count}}',
      extraServicesOnly: 'Services supplémentaires',
      tables: 'Tables assignées',
      noTables: 'Aucune table assignée',
      notes: 'Notes privées',
      noNotes: 'Aucune note privée',
      manageBooking: 'Gérer la réservation',
      eventDetails: {
        title: 'Détails de l’événement',
        eventName: 'Nom de l’événement',
        eventCode: 'Code événement',
        startDate: 'Début',
        maxCapacity: 'Capacité',
        eventDescription: 'Description',
        eventImage: 'Image de l’événement',
        formCodes: 'Formulaires',
        noImage: 'Aucune image',
      },
      menuDetails: {
        title: 'Détails du menu',
        menuName: 'Nom du menu',
        peopleCount: 'Nombre de personnes',
      },
      viewBookingImage: "Voir l'image de la réservation",
      bookingImage: 'Image de la réservation',
      users: 'users',
      status: 'Statut',
      processedBy: 'Traité par',
      source: 'Source',
      noBranch: "Sélectionnez une succursale pour voir l'historique",
      empty: 'Aucune réservation trouvée',
      emptyDesc: "Il n'y a pas de réservations pour le jour {{date}}.",
      updateStatus: 'Mettre à jour le statut de la réservation',
      confirmOperationTitle: "Confirmer l'opération ?",
      confirmStatusChange:
        'Mettre à jour le statut de la réservation vers « {{status}} » ?',
      notifyCustomerTitle: 'Notifier le client ?',
      notifyCustomerMessage:
        'Souhaitez-vous notifier le client de ce changement ?',
      statusUpdateSuccess: 'Statut de la réservation mis à jour avec succès.',
      statusUpdateError:
        'Échec de la mise à jour du statut de la réservation. Veuillez réessayer.',
      updatingStatus: 'Mise à jour du statut...',
      associatedServices: {
        title: 'Services/produits associés',
        reference: 'Réservation {{code}}',
        quantity: '×{{count}}',
        empty: 'Aucun service associé à cette réservation.',
        paid: 'Payé',
        unpaid: 'Non payé',
      },
      messageHistory: {
        open: 'Historique des messages',
        communicationError: 'Échec de l’envoi du message',
        title: 'Historique des messages',
        reference: 'Référence {{code}}',
        refresh: 'Actualiser',
        empty: 'Aucun message envoyé pour cette réservation.',
        loadError: 'Échec du chargement de l’historique des messages.',
        endpoint: 'Endpoint',
        eventCode: 'Code événement',
        payload: 'Payload',
        response: 'Réponse',
        statusProcessed: 'Envoyé',
        statusPending: 'En attente',
        messageText: 'Message',
        messageTextUnavailable: 'Le texte du message n’est pas disponible.',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} tentatives',
        resubmit: 'Renvoyer le message',
        resubmitSuccess: 'Message renvoyé avec succès.',
        resubmitError: 'Échec du renvoi du message. Veuillez réessayer.',
        resubmitConfirmTitle: 'Renvoyer le message ?',
        resubmitConfirmMessage:
          'Êtes-vous sûr de vouloir renvoyer ce message ?',
      },
      statusActions: {
        confirm: 'Confirmation',
        refuse: 'Refuser',
        eliminate: 'Éliminer',
        arrived: 'Arrivé',
        notArrived: 'Non arrivé',
        notifyUser: "Notifier l'utilisateur pour se rendre sur place",
        bookingAlreadyProcessed: 'Réservation déjà traitée',
        moveToConfirm: 'Déplacer vers confirmé',
      },
      statuses: {
        IN_ATTESA: 'En attente',
        MODIFICATO_DA_UTENTE: "Modifié par l'utilisateur",
        ELIMINATO_DA_UTENTE: "Supprimé par l'utilisateur",
        MODIFICA_CONFERMATA: 'Modification confirmée',
        MODIFICA_RIFIUTATA: 'Modification refusée',
        PRE_CONFERMA_PAGAMENTO: 'Pré-confirmation de paiement',
        CONFERMATO: 'Confirmé',
        AVVISATO_LISTA_ATTESA: "Liste d'attente notifiée",
        ARRIVATO: 'Arrivé',
        NON_ARRIVATO: 'Non présenté',
        RIFIUTATO: 'Refusé',
        LISTA_ATTESA: "Liste d'attente",
        ELIMINATO: 'Annulé',
      },
    },
    payment: {
      column: 'Paiements',
      close: 'Fermer',
      connectRequired:
        'Stripe Connect n’est pas prêt pour cet établissement. Terminez l’onboarding pour gérer les paiements.',
      loadError:
        'Impossible de charger les opérations de paiement de cette réservation.',
      linkCopied: 'Lien de paiement copié.',
      linkCopyError: 'Impossible de copier le lien de paiement.',
      linkReadyTitle: 'Lien d’acompte prêt',
      linkReadyDescription: 'Le lien est prêt à être envoyé via vos canaux.',
      badge: {
        title: 'Paiements',
        cardPresent: 'Carte présente',
        cardMissing: 'Carte manquante',
        paidPendingOps: 'Payé · opérations en attente',
        manage: 'Gérer',
        noCard: 'Aucune carte associée',
        verifiedSuccess: 'Vérifiée avec succès',
        depositAuthorizedCapture: 'Acompte autorisé · Encaisser',
        depositCaptured: 'Acompte encaissé',
        depositRefunded: 'Acompte remboursé',
        depositPartiallyRefunded: 'Acompte partiellement remboursé',
        depositPrefix: 'Acompte',
      },
      tooltip: {
        cardPresent: 'Ouvrir les données de carte enregistrées',
        cardMissing: 'Envoyer au client la demande de données de carte',
        openOperations:
          'Ouvrir l’acompte ou les opérations de paiement de la réservation',
        openVerified: 'Ouvrir les détails de paiement et de la carte vérifiée',
        noCard: 'Aucune carte associée. Ouvrir pour gérer les paiements',
      },
      status: {
        notRequired: 'Non requis',
        pending: 'En attente du paiement',
        awaitingConfirmation: 'Paiement envoyé, confirmation Stripe en attente',
        cardVerified: 'Carte vérifiée',
        authorized: 'Non encaissé',
        paid: 'Payé',
        partiallyPaid: 'Partiellement payé',
        failed: 'Échoué',
        canceled: 'Annulé',
        refundPending: 'Remboursement en cours',
        partiallyRefunded: 'Partiellement remboursé',
        refunded: 'Remboursé',
        refundFailed: 'Remboursement échoué',
      },
      actions: {
        copyLink: 'Copier le lien',
        refund: 'Rembourser',
      },
      modal: {
        title: 'Acomptes',
        description:
          'Contrôlez les préautorisations, générez un lien et décidez d’encaisser ou de libérer l’acompte.',
        bookingLabel: 'Réservation {{code}}',
        pendingWarning:
          'Un ou plusieurs paiements sont terminés, mais des opérations ou des liens sont encore en attente.',
        requested: 'Demandé',
        captured: 'Encaissé',
        authorized: 'Autorisé',
        netCaptured: 'Encaissé net',
        refunded: 'Remboursé',
        descriptionLabel: 'Description',
        operationsTab: 'Opérations ({{count}})',
        newRequestTab: 'Nouvelle demande',
        historyTitle: 'Historique des opérations',
        historyDescription:
          'Acomptes et autorisations liés à cette réservation.',
        emptyTitle: 'Aucune opération',
        emptyDescription:
          'Ouvrez l’onglet « Nouvelle demande » pour générer le premier lien.',
        createRequest: 'Créer une demande',
      },
      create: {
        title: 'Créer un lien d’acompte',
        description:
          'Le client autorise le montant ; vous pourrez ensuite l’encaisser ou le libérer.',
        amountLabel: 'Montant de l’acompte (€)',
        descriptionLabel: 'Description facultative',
        descriptionPlaceholder: 'Exemple : acompte pour la réservation',
        authorizeOnlyHint:
          'Le montant est seulement autorisé. Vous pourrez l’encaisser ou le libérer plus tard.',
        submit: 'Générer le lien d’acompte',
        invalidAmount: 'Saisissez un montant supérieur à zéro.',
        error: 'Impossible de créer le lien Stripe.',
        success: 'Lien d’acompte créé.',
        sent: 'Lien d’acompte créé et envoyé.',
        linkCreatedMessageFailed:
          'Lien créé, mais le message de demande d’acompte n’a pas été envoyé.',
      },
      refund: {
        title: 'Rembourser le paiement',
        maxAmount: 'Montant maximum remboursable : {{amount}}',
        amountLabel: 'Montant (€)',
        reasonLabel: 'Motif',
        invalidAmount:
          'Saisissez un montant valide dans la limite remboursable.',
        started: 'Remboursement démarré.',
        completed: 'Remboursement terminé.',
        error:
          'Échec du remboursement. Vérifiez l’état sur Stripe et réessayez.',
        reasons: {
          requested_by_customer: 'Demandé par le client',
          duplicate: 'Paiement en double',
          fraudulent: 'Frauduleux',
        },
      },
    },
    create: {
      title: 'Créer une réservation',
      cancel: 'Annuler',
      submit: 'Créer une réservation',
      customerDetails: 'Coordonnées du client',
      bookingDetails: 'Détails de la réservation',
      firstName: 'Prénom *',
      firstNamePlaceholder: 'Saisir le prénom',
      lastName: 'Nom *',
      lastNamePlaceholder: 'Saisir le nom',
      email: 'E-mail',
      emailPlaceholder: 'Saisir l’e-mail',
      phone: 'Numéro de téléphone *',
      phonePlaceholder: 'Saisir le numéro de téléphone',
      nickName: 'Surnom',
      nickNamePlaceholder: 'Saisir le surnom',
      bookingImage: 'Image de réservation',
      pdfDocument: 'Document PDF',
      bookingTime: 'Heure de réservation *',
      bookingTimePlaceholder: 'Sélectionner l’heure',
      guests: 'Convives *',
      guestsPlaceholder: 'Saisir le nombre de convives',
      children: 'Enfants *',
      childrenPlaceholder: 'Saisir le nombre d’enfants',
      customerRequests: 'Demandes du client',
      customerRequestsPlaceholder: 'Saisir les demandes du client',
      restaurantNotes: 'Notes du restaurant',
      restaurantNotesPlaceholder: 'Saisir les notes du restaurant',
    },
    calendar: {
      selected: 'Sélectionné : {{date}}',
      dotsHint: 'Les points indiquent les réservations disponibles',
    },
  },
  customers: {
    title: 'Mes clients',
    subtitle: 'Clients de {{branch}}',
    subtitleDefault: 'Consultez et gérez votre liste de clients',
    statsCount: '{{count}} clients',
    searchPlaceholder: 'Rechercher nom, e-mail ou téléphone',
    customer: 'Client',
    surname: 'Nom',
    name: 'Prénom',
    email: 'E-mail',
    phone: 'Téléphone',
    contacts: 'Contacts',
    dateOfBirth: 'Date de naissance',
    reservations: 'Réservations',
    noShow: 'No Show',
    tags: 'Tags',
    visits: 'Visites',
    presenceCount: '{{count}} présences',
    noShowCount: '{{count}} No show',
    registrationDate: 'Inscrit',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    noBranch: 'Sélectionnez une succursale pour voir les clients',
    empty: 'Aucun client trouvé',
    emptyDesc: "Il n'y a pas encore de clients pour cette succursale.",
    downloadPdf: 'Télécharger le pdf',
    downloadPdfSuccess: 'PDF de l’historique des réservations téléchargé.',
    downloadPdfError:
      'Échec du téléchargement du PDF de l’historique des réservations.',
    downloadPdfEmpty: 'Aucune réservation trouvée pour ce client.',
    downloadPdfMissingPhone: 'Ce client n’a pas de numéro de téléphone.',
    importExport: {
      menuLabel: 'Importer/exporter',
      import: 'Importer la liste des clients',
      export: 'Exporter la liste des clients',
      downloadSample: 'Télécharger la liste d’exemple',
      downloadSampleSuccess: 'Fichier d’exemple téléchargé.',
      downloadSampleError: 'Échec du téléchargement du fichier d’exemple.',
      exportSuccess: 'Liste des clients exportée.',
      exportError: 'Échec de l’exportation de la liste des clients.',
      dialogTitle: 'Importer la liste des clients',
      dialogDescription:
        'Sélectionnez un fichier CSV ou XLSX pour importer des clients.',
      chooseFile: 'Choisir un fichier',
      selectedFile: 'Fichier sélectionné',
      upload: 'Téléverser',
      uploading: 'Téléversement de la liste des clients...',
      cancel: 'Annuler',
      close: 'Fermer',
      invalidType: 'Veuillez sélectionner un fichier CSV ou XLSX.',
      importError:
        'Échec de l’importation de la liste des clients. Veuillez réessayer.',
      importAnother: 'Importer un autre fichier',
      totalRecords: 'Enregistrements totaux',
      successfulImports: 'Importations réussies',
      failedImports: 'Importations échouées',
    },
    create: {
      new: 'Nouveau client',
      title: 'Créer un client',
      action: 'Client',
      tooltip: 'Ajouter un client',
      submit: 'Créer un client',
      cancel: 'Annuler',
      firstName: 'Prénom',
      firstNamePlaceholder: 'Saisir le prénom',
      lastName: 'Nom',
      lastNamePlaceholder: 'Saisir le nom',
      mobile: 'Numéro de mobile',
      mobilePlaceholder: 'Saisir le numéro de mobile',
      emailPlaceholder: 'Saisir l’adresse e-mail',
      nickname: 'Surnom du client',
      nicknamePlaceholder: 'Saisir le surnom',
      tagsTitle: 'Tags client',
      tagsSubtitle: 'Sélectionnez les tags associés au client.',
      tagsPlaceholder: 'Sélectionner des tags',
      success: 'Client créé avec succès.',
      error: 'Échec de la création du client. Veuillez réessayer.',
      loading: 'Création du client...',
    },
    bookingHistoryPdf: {
      title: 'Historique des réservations',
      generatedMeta: 'Généré le {{date}} · {{count}} réservations',
      customerCode: 'Code client : {{code}}',
      phone: 'Téléphone',
      email: 'E-mail',
      birthDate: 'Date de naissance',
      address: 'Adresse',
      registration: 'Inscription',
      tags: 'Tags',
      notAvailable: 'Non disponible',
      guestsWithChildren: '{{guests}} + {{children}} enfants',
      columns: {
        date: 'Date',
        time: 'Heure',
        code: 'Code',
        status: 'Statut',
        guests: 'Convives',
        source: 'Source',
      },
      sources: {
        WEB: 'Web',
        APP_CUSTOMER: 'Portail',
        APP: 'App',
      },
    },
    filters: {
      title: 'Filtres',
      tags: 'Tags',
      birthYears: 'Années de naissance',
      birthMonth: 'Mois de naissance',
      clearAll: 'Effacer tous les filtres',
      noTags: 'Aucun tag disponible',
      onlyWithEmail: 'e-mail configuré',
      marketingConsent: 'consentement marketing',
      blacklist: 'clients sur liste noire',
      phonePrefix: 'Indicatif pays',
      allPhonePrefixes: 'Tous les indicatifs',
      searchOptions: 'Rechercher...',
      noSearchResults: 'Aucune option correspondante',
      minimumBookings: 'Nombre minimum de réservations',
      selectMinimumBookings: 'Sélectionner le minimum de réservations',
      form: 'Formulaire',
      selectForm: 'Tous les formulaires',
      noForms: 'Aucun formulaire disponible',
      bookingPeriodTitle: 'Réservations sur la période',
      bookingRangeLabel: 'Période de réservation',
      bookingRangePlaceholder: 'Sélectionner une période',
      removePeriod: 'Supprimer la période',
      minimumBookingsOptions: {
        '1': 'Au moins 1 réservation',
        '2': 'Au moins 2 réservations',
        '3': 'Au moins 3 réservations',
        '4': 'Au moins 4 réservations',
        '5': 'Au moins 5 réservations',
        '10': 'Au moins 10 réservations',
        '15': 'Au moins 15 réservations',
        '20': 'Au moins 20 réservations',
        '30': 'Au moins 30 réservations',
        '50': 'Au moins 50 réservations',
      },
      months: {
        '1': 'Janvier',
        '2': 'Février',
        '3': 'Mars',
        '4': 'Avril',
        '5': 'Mai',
        '6': 'Juin',
        '7': 'Juillet',
        '8': 'Août',
        '9': 'Septembre',
        '10': 'Octobre',
        '11': 'Novembre',
        '12': 'Décembre',
      },
    },
  },
  customerDetails: {
    title: 'Détails du Client',
    subtitle: 'Détails du Client pour {{branch}}',
    subtitleDefault: 'Afficher et gérer les détails du client',
    deleteModelTitle: 'Supprimer le client ?',
    deleteModelDescription:
      'La suppression du client supprimera également les réservations associées.',
    deleteModelConfirmText: 'Oui, supprimer',
    deleteModelCancelText: 'Annuler',
    card: {
      title: 'Fiche client',
      close: 'Fermer',
      delete: 'Supprimer',
      update: 'Mettre à jour le client',
      name: 'Prénom',
      surname: 'Nom',
      nickname: 'Surnom',
      mobile: 'Mobile',
      address: 'Adresse',
      city: 'Ville',
      province: 'Province',
      postalCode: 'Code postal',
      country: 'Pays',
      personalDataTitle: 'Données personnelles',
      personalDataSubtitle: 'Prénom, nom et contacts principaux.',
      phoneTitle: 'Téléphone',
      phoneSubtitle: 'Indicatif pays et numéro mobile.',
      addressTitle: 'Adresse',
      addressSubtitle: 'Données géographiques et coordonnées du client.',
      birthDateTitle: 'Date de naissance',
      birthDateSubtitle: 'Format de stockage : yyyy-MM-dd.',
      tagsTitle: 'Tag client',
      tagsSubtitle: 'Sélectionnez les tags associés au client.',
      tagsPlaceholder: 'Sélectionner des tags',
      updateSuccess: 'Client mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du client. Veuillez réessayer.',
      deleteSuccess: 'Client supprimé avec succès.',
      deleteError: 'Échec de la suppression du client. Veuillez réessayer.',
    },
  },
  forms: {
    title: 'Formulaires de réservations',
    subtitle: 'Gérez tous les formulaires actifs, suspendus ou archivés',
    addDataCollectionForm: 'Ajouter un formulaire',
    searchPlaceholder: 'Rechercher des formulaires par nom',
    noDataTitle: 'Aucun formulaire',
    noDataDesc:
      "Vous n'avez pas encore créé de formulaire. Commencez par créer votre premier formulaire pour collecter des réservations et des interactions.",
    status: 'Statut',
    active: 'Actif',
    suspended: 'Suspendu',
    url: 'URL',
    qrCode: 'Code QR',
    preview: 'Aperçu',
    googleBooking: 'Google Booking',
    googleReservation: 'Google Reservation',
    copyUrl: 'Copier l’URL',
    downloadQr: 'Télécharger le QR',
    editForm: 'Modifier le formulaire',
    dublicateUrl: 'Dupliquer le formulaire',
    statusActive: 'Actif',
    statusInactive: 'Inactif',
    statusDraft: 'Brouillon',
    statsCount: '{{count}} formulaires',
    updatedAgo: 'Mis à jour il y a 2 minutes',
    deleteForm: 'Supprimer le formulaire',
    share: {
      label: 'Partager le formulaire',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: 'Réservez avec nous',
      instagramCopied: 'Lien copié. Collez-le sur Instagram pour partager.',
      instagramOpened:
        'Instagram ouvert. Lien du formulaire copié — collez-le pour partager.',
      copyFailed: 'Impossible de copier le lien. Réessayez.',
    },
    createTitle: 'Créer un formulaire',
    formName: 'Nom du formulaire',
    formNamePlaceholder: 'Saisir le nom du formulaire',
    createForm: 'Créer le formulaire',
    toast: {
      createSuccess: 'Formulaire créé avec succès.',
      createError: 'Échec de la création du formulaire. Veuillez réessayer.',
      deleteSuccess: 'Formulaire supprimé avec succès.',
      deleteError: 'Échec de la suppression du formulaire. Veuillez réessayer.',
      duplicateSuccess: 'Formulaire dupliqué avec succès.',
      duplicateError:
        'Échec de la duplication du formulaire. Veuillez réessayer.',
      statusActivateSuccess: 'Formulaire activé avec succès.',
      statusSuspendSuccess: 'Formulaire suspendu avec succès.',
      statusActivateError:
        "Échec de l'activation du formulaire. Veuillez réessayer.",
      statusSuspendError:
        'Échec de la suspension du formulaire. Veuillez réessayer.',
      googleReservationEnabled: '{{name}} Google Reservation activé',
      googleReservationDisabled: '{{name}} Google Reservation désactivé',
      googleReservationError:
        'Impossible de mettre à jour Google Reservation. Veuillez réessayer.',
      logoUploadSuccess: 'Logo téléversé avec succès.',
      logoUploadError: 'Échec du téléversement du logo.',
      logoRemoveSuccess: 'Logo supprimé avec succès.',
      logoRemoveError: 'Échec de la suppression du logo.',
      bannerUploadSuccess: 'Bannière téléversée avec succès.',
      bannerUploadError: 'Échec du téléversement de la bannière.',
      bannerRemoveSuccess: 'Bannière supprimée avec succès.',
      bannerRemoveError: 'Échec de la suppression de la bannière.',
    },
    deleteModel: {
      deleteTitle: 'Supprimer le formulaire ?',
      deleteDescription:
        'Êtes-vous sûr de vouloir supprimer ce formulaire ? Cette action est irréversible.',
    },
    validation: {
      formNameMin: 'Le nom doit contenir au moins 2 caractères',
      formNameMax: 'Le nom ne peut pas dépasser 50 caractères',
    },
    scanQrAccess: 'Scannez le code QR pour accéder :',
    qrClose: 'Fermer',
    qrDownload: 'Télécharger',
    edit: {
      pageTitle: 'Consentement aux données personnelles',
      editTitle: 'Modifier {{name}}',
      subtitle: 'Configurer le formulaire de réservation',
      saveChanges: 'Enregistrer les modifications',
      close: 'Fermer',
      toast: {
        saveSuccess: 'Formulaire enregistré avec succès.',
        saveError: 'Échec de l’enregistrement du formulaire.',
      },
      tabListLabel: 'Sections de modification du formulaire',
      uploading: 'Téléversement...',
      removing: 'Suppression...',
      tabs: {
        general: 'Général',
        capacity: 'Capacité',
        schedule: 'Horaires',
        formFields: 'Champs du formulaire',
        preferences: 'Préférences',
        services: 'Services',
      },
      general: {
        media: 'Média',
        logo: 'Logo',
        banner: 'Image de bannière',
        bannerHint: 'Image de bannière — glissez-déposez ou parcourez',
        basicDetails: 'Informations de base',
        formName: 'Nom du formulaire',
        customerName: 'Formulaire Emoji',
        address: 'Adresse du local à afficher sur le formulaire',
        customThankYouPage: 'Page de remerciement personnalisée',
        buttonTextOnBookingForm:
          'Texte du bouton sur le formulaire de réservation',
        policy: 'Politique',
        webLink: 'Lien web',
      },
      capacity: {
        hint: 'Limites de réservation et règles de table pour ce formulaire.',
        sections: {
          coverLimits: 'Limites de couverts',
          timeSettings: 'Paramètres des créneaux horaires',
          policies: 'Politiques',
        },
        maxGuests: 'Couverts maximum avant l’appel',
        maxGuestsHint:
          'Quel est le nombre maximum de couverts que votre établissement accepte avant d’exiger un appel ?',
        maxTableGuests: 'Invités max par table',
        maxTableGuestsHint:
          'Définissez le nombre maximum d’invités que vous pouvez accepter à une seule table. Si vous laissez la valeur à 0, le maximum correspondra à la capacité totale de l’établissement.',
        minGuests: 'Couverts min par table',
        minGuestsHint: 'Taille de groupe minimale pour une seule table.',
        phoneOverbooking: 'Mobile Overbooking',
        phoneOverbookingHint:
          'Téléphone affiché lorsque les invités dépassent le maximum par table. Format : PRÉFIXE + NUMÉRO (par ex. +39 3444393343).',
        cut: 'Découpe',
        cutHint:
          'Divise la disponibilité en créneaux de réservation de cette durée.',
        cutSlotOption:
          'Diviser les créneaux horaires en créneaux de {{minutes}} minutes',
        stayOnTable: 'Durée à table',
        stayOnTableHint: 'Durée de repas prévue par réservation.',
        minutes: '{{count}} min',
        dogsAccepted: 'Chiens acceptés',
        dogsAcceptedHint:
          'Permet aux clients d’indiquer qu’ils amènent un chien.',
        menuSelectionMandatory: 'Sélection de menu obligatoire',
        menuSelectionMandatoryHint:
          'Si activé, le client doit choisir un menu (ou « Sans menu ») pour chaque personne afin que le total corresponde à adultes + enfants pour continuer.',
        showRestaurantNoMenu: 'Afficher la carte restaurant sans menu',
        showRestaurantNoMenuHint:
          'Si activé, le formulaire public affiche une carte expliquant que le restaurant n’a pas de menu fixe.',
      },
      schedule: {
        shiftOverview: 'Aperçu des services',
        weeklyHours: 'Horaires hebdomadaires',
        addTimeSlot: 'Ajouter un créneau horaire',
        day: 'Jour',
        open: 'Ouvert',
        closed: 'Fermé',
        hours: 'Heures',
        editSlot: 'Modifier le créneau',
        deleteSlot: 'Supprimer le créneau',
        deleteSlotConfirmTitle: 'Supprimer ce créneau ?',
        deleteSlotConfirmDesc: 'Le créneau sélectionné sera retiré de ce jour.',
        deleteSlotConfirmMultiDay:
          'Ce service utilise le même code sur {{count}} jours. Sa suppression l’enlèvera de tous ces jours.',
        extraordinarySection: 'Ouvertures / fermetures extraordinaires',
        addException: 'Ajouter une exception',
        dateRange: 'Plage de dates',
        type: 'Type',
        closedOpenDay: 'Jour fermé/ouvert',
        timeSlots: 'Créneaux horaires',
        cuttingMin: 'Découpe (min)',
        maxGuests: 'Invités max',
        noShifts: 'Aucun service configuré pour le moment.',
        emptyWeeklyHours:
          'Aucun horaire hebdomadaire configuré. Utilisez + pour créer un horaire pour tous les jours.',
        emptyExtraordinary:
          'Aucune ouverture ou fermeture extraordinaire configurée.',
        createForAllDays: 'Créer une configuration générale pour chaque jour',
        resetConfiguration: 'Réinitialiser la configuration',
        saveConfiguration: 'Enregistrer la configuration',
        resetConfirmTitle: 'Supprimer toutes les configurations existantes ?',
        resetConfirmDesc:
          'Cela supprimera tous les paramètres d’horaire de ce formulaire.',
        resetProceed: 'Continuer',
        tags: 'Tags',
        tagsPlaceholder: 'Saisir des tags séparés par des virgules',
        tagsPickerDesc: 'Sélectionnez des tags pour ce créneau.',
        tagsPickerEmpty: 'Aucun tag disponible pour cette succursale.',
        allowOverbook: 'Autoriser le surbooking',
        alertMessage: 'Message d’alerte',
        alertMessagePlaceholder:
          'Saisir un message d’alerte pour cette plage horaire',
        enableOverbookBlocking: 'Activer le blocage du surbooking',
        disableOverbookBlocking: 'Désactiver le blocage du surbooking',
        deposit: {
          tooltipInactive: 'Configurer l’acompte / la vérification carte',
          tooltipValidation: 'Stripe : vérification de la carte uniquement',
          tooltipDeposit: 'Acompte Stripe : {{amount}} par convive',
          tooltipMixed: 'Configuration Stripe mixte selon les plages',
          allStandardBands: 'Toutes les plages standard',
          allExtraordinaryBands: 'Toutes les plages extraordinaires',
          singleBand: 'Plage horaire sélectionnée',
          depositTitle: 'Acompte',
          checkCardTitle: 'Vérifier la carte',
          bulkHint:
            'La configuration sera appliquée en une seule opération à toutes les plages de cette section.',
          requestTitle: 'Demander une carte de crédit',
          requestDesc:
            'Vous pouvez demander uniquement la vérification de la carte ou aussi un acompte préautorisé.',
          validationTitle: 'Validation de carte',
          validationDesc: 'Vérifier et lier la carte sans bloquer un montant.',
          depositOptionTitle: 'Acompte',
          depositOptionDesc:
            'Valider la carte et préautoriser le montant configuré.',
          amountLabel: 'Montant EUR',
          amountHint:
            'Saisissez un montant supérieur à zéro pour activer l’acompte.',
          saveConfiguration: 'Enregistrer la configuration',
          close: 'Fermer',
          toastSaveSuccess: 'Configuration d’acompte enregistrée.',
          toastSaveError:
            'Échec de l’enregistrement de la configuration d’acompte.',
        },
        legacyCreditCard: {
          enable: 'Activer la demande directe des données de carte',
          disable: 'Désactiver la demande directe des données de carte',
        },
        maxGuestsPerSlot: 'Couverture max par créneau horaire',
        customizeCutter:
          'Personnaliser la découpe des créneaux sur la configuration standard',
        noSlotsForDay:
          'Aucun créneau pour ce jour. Utilisez + pour en ajouter un.',
        slotClosedOnDate: 'Créneau non disponible le {{date}}',
        alertModalTitle: 'Alerte pour le client en cas de sélection de créneau',
        maxGuestsModalTitle:
          'Définir le nombre maximum de places pour ce créneau {{range}}',
        maxGuestsModalDesc:
          'Choisissez le nombre maximum d’invités autorisés pour ce créneau horaire.',
        cutterModalTitle: 'Mettre à jour la découpe du créneau horaire',
        days: {
          monday: 'Lundi',
          tuesday: 'Mardi',
          wednesday: 'Mercredi',
          thursday: 'Jeudi',
          friday: 'Vendredi',
          saturday: 'Samedi',
          sunday: 'Dimanche',
        },
        addTimeAllDays: {
          title: 'Ajouter une heure à tous les jours',
          subtitle:
            'Les heures disponibles respectent les créneaux horaires du lieu et ceux déjà configurés.',
          chosenTime: 'Heure choisie',
          duration: 'Durée : {{duration}}',
          startAt: '1. Commence à',
          endsAt: '2. Se termine à',
          allOkDesc: 'Vous pouvez enregistrer cet horaire pour tous les jours.',
          saveOnAllDays: 'Enregistrer pour tous les jours',
        },
        slotTime: {
          title: 'Heure du créneau',
          subtitle: 'Choisissez d’abord le début. Puis choisissez la fin.',
          allOk: 'Tout est correct',
          allOkDesc: 'Vous pouvez enregistrer cette heure.',
          saveTime: 'Enregistrer l’heure',
          timeRangeInvalid:
            'L’heure de fin doit être postérieure à l’heure de début.',
          timeRangeConflict:
            'Cette plage horaire est en conflit avec une autre. Modifiez ou supprimez l’une des plages.',
          occupiedTitle: 'Horaires déjà occupés',
          occupiedHint: 'La seconde liste évite automatiquement ces horaires.',
          cannotSave: 'Changez l’horaire',
          cannotSaveDesc: 'Il n’y a pas de fin valide pour ce début.',
          cannotSaveButton: 'Impossible d’enregistrer',
          durationMinutes: '{{count}} minutes',
          durationHours: '{{count}} heures',
          durationHoursMinutes: '{{hours}} heures et {{minutes}} minutes',
          selectService: 'Sélectionnez un service',
          selectServiceDesc:
            'Choisissez une plage horaire pour ajouter ses heures d’ouverture.',
          noLocalSlots:
            'Les créneaux ont été supprimés dans la configuration locale. Recréez-les là-bas, puis ajoutez-les à ce planning.',
          selectedCount: '{{count}} services sélectionnés',
          alreadyAdded: 'Déjà ajouté',
        },
        extraordinary: {
          badge: 'Configuration extraordinaire',
          createRange: 'Créer une plage',
          editRange: 'Modifier la plage',
          description: 'Description',
          descriptionPlaceholder: 'P. ex. Ferragosto, événement privé...',
          rangeDate: 'Date de la plage',
          rangeDatePlaceholder: 'Sélectionner la plage de dates',
          clearDates: 'Effacer',
          applyDates: 'Appliquer',
          open: 'Ouvert',
          create: 'Créer',
          update: 'Mettre à jour',
          validation: 'La description et la plage de dates sont obligatoires.',
          dateRequired: 'Veuillez sélectionner une plage de dates.',
          dateOrder:
            'La date de fin ne peut pas être antérieure à la date de début.',
          timeRangeInvalid:
            'L’heure de fin doit être postérieure à l’heure de début.',
          addConfiguration: 'Ajouter une configuration extraordinaire',
          saveConfiguration: 'Enregistrer la configuration',
          addTimeSlot: 'Ajouter un créneau horaire',
          deleteRange: 'Supprimer la plage',
          deleteConfirmTitle: 'Supprimer la plage extraordinaire ?',
          deleteConfirmDesc:
            'Cela supprimera définitivement la configuration de cette plage de dates.',
          toast: {
            createSuccess: 'Plage extraordinaire créée.',
            updateSuccess: 'Plage extraordinaire mise à jour.',
            saveError: 'Échec de l’enregistrement de la plage extraordinaire.',
            deleteSuccess: 'Plage extraordinaire supprimée.',
            deleteError: 'Échec de la suppression de la plage extraordinaire.',
          },
        },
        toast: {
          saveSuccess: 'Configuration d’horaire enregistrée.',
          saveError: 'Échec de l’enregistrement de l’horaire.',
          resetSuccess: 'Configuration d’horaire réinitialisée.',
          resetError: 'Échec de la réinitialisation de l’horaire.',
          addSlotSuccess: 'Créneau horaire ajouté.',
          addSlotError: 'Échec de l’ajout du créneau horaire.',
          deleteSlotSuccess: 'Créneau horaire supprimé.',
          deleteSlotError: 'Échec de la suppression du créneau horaire.',
          updateTimeSuccess: 'Créneau horaire mis à jour.',
          updateTimeError: 'Échec de la mise à jour du créneau horaire.',
          dayToggleSuccess: 'Statut du jour mis à jour.',
          dayToggleError: 'Échec de la mise à jour du statut du jour.',
          configurationMissing:
            'La configuration du jour n’est pas encore disponible.',
          slotNotReady:
            'Le créneau horaire n’est pas prêt. Veuillez actualiser et réessayer.',
          overbookToggleSuccess: 'Paramètre de surbooking mis à jour.',
          overbookToggleError:
            'Échec de la mise à jour du paramètre de surbooking.',
          legacyCreditCardToggleSuccess:
            'Demande de données de carte mise à jour.',
          legacyCreditCardToggleError: 'Erreur lors de la modification.',
          tagsSaveSuccess: 'Tags enregistrés.',
          tagsLoadError: 'Échec du chargement des tags.',
          tagsSaveError: 'Échec de l’enregistrement des tags.',
          tagsDeleteSuccess: 'Tags supprimés.',
          alertSaveSuccess: 'Message d’alerte enregistré.',
          alertSaveError: 'Échec de l’enregistrement du message d’alerte.',
          alertDeleteSuccess: 'Message d’alerte supprimé.',
          maxGuestsInvalid: 'Saisissez un nombre d’invités valide.',
          maxGuestsSaveSuccess: 'Nombre maximum d’invités mis à jour.',
          maxGuestsSaveError:
            'Échec de la mise à jour du nombre maximum d’invités.',
          cutterSaveSuccess: 'Découpe du créneau horaire mise à jour.',
          cutterSaveError:
            'Échec de la mise à jour de la découpe du créneau horaire.',
        },
      },
      formFields: {
        hint: 'Choisissez les champs qui apparaissent sur le formulaire public de réservation.',
        configure: 'Configurer les champs du formulaire',
        configureDesc:
          'Activez les champs affichés aux clients lors de la réservation.',
        name: 'Prénom',
        surname: 'Nom',
        phone: 'Téléphone',
        email: 'Email',
        dateOfBirth: 'Date de naissance',
        zipCode: 'Code postal (CAP)',
        customerNotes: 'Notes du client',
        field: 'Champ',
        mandatory: 'Obligatoire',
        nonMandatory: 'Non obligatoire',
      },
      preferences: {
        sectionTitle: 'Options supplémentaires',
        sectionDescription:
          'Configurations supplémentaires ; permettez à vos clients de communiquer plus facilement leurs préférences, allergies ou intolérances.',
        singleChoicePredefined:
          'Remplissez la liste comme suggéré pour créer des options de préférence à choix unique.',
        singleChoiceAdditional:
          'Crée une option de préférence supplémentaire à choix unique',
        multiChoicePredefined:
          'Remplissez la liste comme suggéré pour créer des options de préférence à choix multiple.',
        multiChoiceAdditional:
          'Crée une option de préférence supplémentaire à choix multiple',
        singleChoiceOptionsPlaceholder:
          'Exemples : Salle extérieure | Salle intérieure | Bar',
        multiChoiceOptionsPlaceholder: 'Ex : Lactose | Cœliaque | Cacahuètes',
        singleChoiceFieldPlaceholder: 'Ex : Nom du champ à choix unique',
        multiChoiceFieldPlaceholder: 'Ex : Nom du champ à choix multiple',
        pipeSeparatedNote: 'Remarque : séparez les valeurs par le caractère |.',
        importRooms: 'Importer mes salles',
        importRoomsSuccess: 'Salles importées avec succès.',
        importRoomsError: 'Échec de l’importation des salles.',
        importRoomsEmpty: 'Aucune salle trouvée pour cette succursale.',
        importRoomsNotFound:
          'Aucune salle trouvée pour cette succursale. Vérifiez le code de succursale ou créez d’abord des salles.',
        suggestAllergens: 'Suggérez-moi une liste complète d’allergènes',
        validation: {
          fillRequired:
            'Si Mandatory ou Non-mandatory est coché, remplissez le libellé et les options.',
          labelRequired: 'Le libellé est requis lorsque l’option est activée.',
          optionsRequired:
            'Ajoutez au moins une option séparée par | lorsque l’option est activée.',
        },
      },
      services: {
        sectionTitle: 'Section produits et services supplémentaires',
        sectionDescription:
          'Créez une liste de produits et services à associer au formulaire de réservation.',
        mandatoryLabel:
          'Je veux que le choix des produits ou services soit obligatoire',
        title: 'Produits et services supplémentaires',
        description: 'Proposez des extras pendant le processus de réservation.',
        add: 'Ajouter un produit ou service',
        name: 'Nom',
        descriptionCol: 'Description',
        price: 'Prix',
        pax: 'Pax',
        empty: 'Aucun produit ou service lié à ce formulaire pour le moment.',
        pickerTitle: 'Liste des services/produits',
        pickerDesc:
          'Sélectionnez un ou plusieurs éléments à associer à ce formulaire.',
        pickerEmpty:
          'Aucun service ou produit disponible pour cette succursale.',
        deleteTitle: 'Supprimer le service/produit ?',
        deleteDesc:
          'Cela supprimera l’élément sélectionné de ce formulaire de réservation.',
        toast: {
          saveSuccess: 'Services et produits mis à jour.',
          saveError: 'Échec de la mise à jour des services et produits.',
          deleteSuccess: 'Service/produit supprimé.',
          deleteError: 'Échec de la suppression du service/produit.',
          loadError: 'Échec du chargement des services et produits.',
        },
      },
      formConfiguration: 'Configuration du formulaire',
      formConfigurationDesc: 'Configurations générales du formulaire',
      formImage: 'Image du formulaire',
      formImageHint:
        'Nous recommandons un logo PNG avec fond transparent. Double-cliquez sur l’image pour rediriger immédiatement.',
      formBackgroundImage: 'Image de fond du formulaire',
      imageUpload: 'Téléverser une image',
      removeImage: 'Supprimer l’image',
      redirectAfterCompilation: 'Redirection après remplissage',
      linkForm: 'Lien du formulaire',
      buttonText: 'Texte du bouton',
      dataFields: 'Champs de données',
      requestEmail: 'Demander l’email',
      requestAddress: 'Demander l’adresse',
      requestNationality: 'Demander la nationalité',
      requestZipCode: 'Demander le code postal',
      requestDateOfBirth: 'Demander la date de naissance',
      tagsApplied: 'Tags appliqués au formulaire',
      tagsPlaceholder: 'Sélectionner des tags',
      tagsHint: 'Noms de tags séparés par des virgules',
      validation: {
        buttonTextRequired: 'Le texte du bouton est requis',
      },
    },
  },
  dcfQuestions: {
    title: 'Questions et tags des réponses',
    description:
      'Collectez les coordonnées et les réponses dans un seul formulaire. Associez des tags aux questions renseignées et aux options choisies.',
    backendRequired:
      'Les questions nécessitent une mise à jour des API. Vous pouvez les configurer ici, mais pas les enregistrer avant cette mise à jour.',
    saveMismatch:
      'Les API n’ont pas renvoyé les questions enregistrées. Vos modifications sont conservées ici. Contactez l’équipe API.',
    unavailable:
      'Ce formulaire ne peut pas encore accepter de réponses. Réessayez plus tard.',
  },
  dataCollectionForms: {
    title: 'Formulaire de Collecte de Données',
    subtitle: 'Formulaires présents',
    addDataCollectionForm: 'Ajouter un Formulaire',
    searchPlaceholder:
      'Rechercher des formulaires de collecte de données par nom',
    noDataTitle: 'Aucun formulaire présent',
    noDataDesc:
      'Créez votre premier formulaire de collecte de données pour commencer à collecter des leads et des interactions.',
    form: 'Formulaire',
    tags: 'Tags',
    tagsMore: '+{{count}} de plus',
    tagsLess: 'Moins',
    status: 'Statut',
    interactions: 'Interactions',
    interactionsCount: '{{count}} interactions',
    url: 'URL',
    qrCode: 'Code QR',
    preview: 'Aperçu',
    copyUrl: 'Copier l’URL',
    downloadQr: 'Télécharger le QR',
    editDataCollectionForm: 'Modifier le formulaire de collecte de données',
    statusActive: 'Actif',
    statusInactive: 'Inactif',
    statusSuspended: 'Suspendu',
    statusDraft: 'Brouillon',
    statsCount: '{{count}} formulaires de collecte de données',
    updatedAgo: 'Mis à jour il y a 2 minutes',
    deleteDataCollectionForm: 'Supprimer le formulaire de collecte de données',
    duplicate: 'Dupliquer',
    createTitle: 'Créer un formulaire de collecte de données',
    formName: 'Nom du formulaire',
    formNamePlaceholder: 'Saisir le nom du formulaire',
    createForm: 'Créer le formulaire',
    deleteModel: {
      deleteTitle: 'Supprimer le formulaire de collecte de données ?',
      deleteDescription:
        'Êtes-vous sûr de vouloir supprimer ce formulaire ? Cette action est irréversible.',
    },
    validation: {
      formNameMin: 'Le nom doit contenir au moins 2 caractères',
      formNameMax: 'Le nom ne peut pas dépasser 255 caractères',
    },
    scanQrAccess: 'Scannez le code QR pour accéder à :',
    qrClose: 'Fermer',
    qrDownload: 'Télécharger',
    toast: {
      createSuccess: 'Formulaire créé avec succès.',
      createError: 'Échec de la création du formulaire. Veuillez réessayer.',
      updateSuccess: 'Formulaire mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du formulaire. Veuillez réessayer.',
      logoUploadSuccess: 'Logo téléversé avec succès.',
      logoUploadError: 'Échec du téléversement du logo.',
      logoRemoveSuccess: 'Logo supprimé avec succès.',
      logoRemoveError: 'Échec de la suppression du logo.',
      bgUploadSuccess: 'Arrière-plan téléversé avec succès.',
      bgUploadError: 'Échec du téléversement de l’arrière-plan.',
      bgRemoveSuccess: 'Arrière-plan supprimé avec succès.',
      bgRemoveError: 'Échec de la suppression de l’arrière-plan.',
      statusUpdateSuccess: 'Statut mis à jour avec succès.',
      statusUpdateError:
        'Échec de la mise à jour du statut. Veuillez réessayer.',
      deleteSuccess: 'Formulaire supprimé avec succès.',
      deleteError: 'Échec de la suppression du formulaire. Veuillez réessayer.',
      duplicateSuccess: 'Formulaire dupliqué avec succès.',
      duplicateError:
        'Échec de la duplication du formulaire. Veuillez réessayer.',
    },
    edit: {
      pageTitle: 'Consentement aux données personnelles',
      subtitle: 'Configurer le formulaire de collecte de données',
      close: 'Fermer',
      formConfiguration: 'Configuration du formulaire',
      formConfigurationDesc:
        'Configurations générales du formulaire de collecte de données',
      formImage: 'Image du formulaire',
      formImageHint:
        "Nous recommandons un logo PNG avec fond transparent. Double-cliquez sur l'image pour rediriger.",
      formBackgroundImage: 'Image de fond du formulaire',
      imageUpload: 'Téléverser une image',
      removeImage: "Supprimer l'image",
      redirectAfterCompilation: 'Redirection après compilation',
      linkForm: 'Lien du formulaire',
      buttonText: 'Texte du bouton',
      description: 'Description',
      descriptionPlaceholder: 'Texte affiché à gauche du formulaire public',
      dataFields: 'Champs de données',
      requestEmail: "Demander l'email",
      requestAddress: "Demander l'adresse leave",
      requestNationality: 'Demander la nationalité',
      requestZipCode: 'Demander le code postal',
      requestDateOfBirth: 'Demander la date de naissance',
      tagsApplied: 'Tags appliqués au formulaire',
      tagsOnDownload: 'Tags appliqués au téléchargement',
      tagsOnUse: 'Tags attribués après l’enregistrement des données',
      tagsPlaceholder: 'Sélectionner des tags',
      tagsHint: 'Noms de tags séparés par des virgules',
      uploading: 'Téléversement...',
      removing: 'Suppression...',
      validation: {
        redirectUrlRequired: 'L’URL de redirection est obligatoire.',
        redirectUrlInvalid:
          'Saisissez une URL valide (ex. https://example.com).',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: 'Formulaire de collecte de données',
    loading: 'Chargement du formulaire',
    missingFormCode: 'Le code du formulaire est manquant dans l’URL.',
    loadError: 'Impossible de charger ce formulaire de collecte de données.',
    unavailableTitle: 'Formulaire non disponible',
    unavailableMessage:
      'Ce formulaire de collecte de données est actuellement suspendu ou indisponible.',
    personalInfo: 'Informations personnelles',
    prefixLabel: 'Indicatif',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Votre numéro',
    firstName: 'Prénom',
    firstNamePlaceholder: 'Votre prénom',
    lastName: 'Nom',
    lastNamePlaceholder: 'Votre nom',
    email: 'E-mail',
    emailPlaceholder: 'nom@exemple.fr',
    optional: 'Facultatif',
    cap: 'Code postal',
    capPlaceholder: 'Ex. 09124',
    birthDate: 'Date de naissance',
    address: 'Adresse',
    addressPlaceholder: 'Rue et numéro',
    nationality: 'Nationalité',
    nationalityPlaceholder: 'Votre nationalité',
    requiredNote: '* Champs obligatoires',
    consentsTitle: 'Vos préférences',
    privacyConsent: 'Confidentialité et traitement des données',
    marketingConsent: 'Consentement marketing et communication',
    profilingConsent: 'Consentement au profilage',
    detailsLink: 'Informations',
    selectAll: 'Tout sélectionner',
    submit: 'Accéder à la page',
    welcomeBackTitle: 'Bon retour, {{name}} !',
    welcomeBackTitleGeneric: 'Bon retour !',
    welcomeBackMessage:
      'Nous avons déjà vos données. Vous pouvez ajouter un autre contact si c’est pour quelqu’un d’autre.',
    addAnotherContact: 'Ajouter un autre contact',
    toast: {
      success: 'Données envoyées avec succès.',
      error: 'Échec de l’envoi des données. Réessayez.',
    },
    thankYou: {
      title: 'Merci !',
      message: 'Vos données ont été envoyées avec succès.',
      continue: 'Continuer',
    },
    validation: {
      prefixRequired: 'Sélectionnez un indicatif pays.',
      phoneMin: 'Saisissez un numéro de mobile valide.',
      firstNameRequired: 'Le prénom est requis.',
      lastNameRequired: 'Le nom est requis.',
      emailRequired: 'L’e-mail est requis.',
      emailInvalid: 'Saisissez une adresse e-mail valide.',
      birthDateInvalid: 'Saisissez une date valide, non future.',
      privacyRequired: 'Le consentement à la confidentialité est requis.',
    },
  },
  bulkMessage: {
    title: 'Envoyer en masse',
    subtitle: 'Gérez les campagnes WhatsApp, Email et SMS pour {{branch}}',
    subtitleDefault: 'Consultez et gérez vos campagnes de messages groupés',
    statsCount: '{{count}} campagnes',
    searchPlaceholder: 'Rechercher un envoi groupé...',
    campaignName: 'Nom de la campagne',
    campaignNamePlaceholder: 'Saisir le nom de la campagne',
    addCampaign: 'Nouvelle campagne',
    creating: 'Création...',
    createTitle: 'Créer une nouvelle campagne d’envoi de messages',
    createDesc:
      'Ajoutez une nouvelle campagne de messages groupés pour votre succursale.',
    close: 'Fermer',
    duplicate: 'Dupliquer',
    editCampaign: 'Modifier la campagne',
    viewDetails: 'Voir les détails',
    pause: 'Mettre en pause',
    resume: 'Reprendre l’envoi',
    delete: 'Supprimer la campagne',
    noBranch: 'Sélectionnez une succursale pour voir les messages groupés',
    empty: 'Aucune campagne trouvée',
    emptyDesc:
      'Il n’y a pas encore de campagnes de messages groupés pour cette succursale.',
    channels: {
      whatsappWithImage: 'WHATSAPP AVEC IMAGE',
    },
    columns: {
      name: 'Nom',
      type: 'Type',
      state: 'État',
      progress: 'Progression',
      selectedCustomers: 'Clients sélectionnés',
      status: 'Statut',
      channel: 'Canal',
      messageType: 'Type de message',
      customer: 'Client',
      reference: 'Référence',
      attempts: 'Tentatives',
      createdAt: 'Créé le',
    },
    statusFilter: {
      all: 'Tous les statuts',
      pending: 'En attente',
      sent: 'Envoyé',
      delivered: 'Livré',
      failed: 'Échoué',
    },
    noDataTitle: 'Aucun message trouvé',
    noDataDesc:
      'Il n’y a pas encore d’événements de livraison de messages pour cette succursale.',
    deleteModal: {
      title: 'Supprimer la campagne ?',
      description:
        'Voulez-vous vraiment supprimer cette campagne ? Cette action est irréversible.',
    },
    details: {
      pageTitle: 'Campagne {{name}}',
      subtitle: 'Progression de la campagne d’envoi groupé',
      empty: 'Aucun résultat trouvé',
      emptyDesc:
        'Il n’y a pas encore d’événements de livraison pour cette campagne.',
      loadError:
        'Impossible de charger les événements de livraison. Réessayez.',
      emailLabel: 'Email :',
      phoneLabel: 'Tél :',
      chatId: 'Chat {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: 'Renvoyer le message',
      resubmitSuccess: 'Événement remis en file d’attente.',
      resubmitError: 'Impossible de remettre le message en file. Réessayez.',
      resubmitConfirmTitle: 'La tentative précédente a réussi',
      resubmitConfirmDescription:
        'Voulez-vous vraiment envoyer à nouveau ce message ?',
      resubmitConfirmAction: 'Oui, renvoyer',
      columns: {
        recipient: 'Destinataire',
        message: 'Message',
        channel: 'Canal',
        status: 'Résultat',
        details: 'Détails',
      },
    },
    validation: {
      nameMin: 'Le nom de la campagne doit contenir au moins 2 caractères',
      nameMax: 'Le nom de la campagne ne peut pas dépasser 120 caractères',
    },
    toast: {
      createSuccess: 'Campagne créée avec succès.',
      createError: 'Échec de la création de la campagne. Réessayez.',
      updateSuccess: 'Campagne enregistrée avec succès.',
      updateError: 'Échec de l’enregistrement de la campagne. Réessayez.',
      deleteSuccess: 'Campagne supprimée avec succès.',
      deleteError: 'Échec de la suppression de la campagne. Réessayez.',
      duplicateSuccess: 'Campagne dupliquée avec succès.',
      duplicateError: 'Échec de la duplication de la campagne. Réessayez.',
      imageRemoveSuccess: 'Image jointe supprimée avec succès.',
      imageRemoveError: 'Échec de la suppression de l’image jointe.',
      imageUploadSuccess: 'Image téléversée avec succès.',
      imageUploadError: 'Échec du téléversement de l’image. Réessayez.',
      imageTypeError: 'Seules les images JPG et PNG sont autorisées.',
      imageSizeError: 'L’image doit faire 5 Mo ou moins.',
      startSuccess:
        'L’envoi des messages groupés a démarré. Surveillez la liste pour suivre la progression.',
      startError: 'Échec du démarrage de la campagne. Réessayez.',
      pauseSuccess: 'Campagne mise en pause avec succès.',
      pauseError: 'Échec de la mise en pause. Réessayez dans quelques minutes.',
      resumeSuccess: 'Campagne reprise avec succès.',
      resumeError:
        'Échec de la reprise de la campagne. Réessayez dans quelques minutes.',
    },
    edit: {
      pageTitle: 'Modifier {{name}}',
      subtitle:
        'Configurez les détails de la campagne et le contenu du message',
      startCampaign: 'Démarrer la campagne',
      start: {
        confirmTitle: 'On continue ?',
        confirmDescription:
          'Vous allez envoyer un message groupé de type {{channel}} à {{count}} clients.',
        confirmAction: 'Oui, continuer',
        noRecipients:
          'Il n’y a aucun destinataire. Ajoutez des destinataires pour démarrer la campagne.',
        emptyMessage: 'Saisissez un message pour démarrer la campagne.',
        emptySubject: 'Saisissez un objet pour démarrer la campagne email.',
        missingImage:
          'Ajoutez une image pour démarrer la campagne WhatsApp avec image.',
        missingTemplate:
          'Sélectionnez un modèle pour démarrer la campagne WhatsApp Business.',
        corruptTemplateVariables:
          'Les variables de ce modèle sont vides ou indisponibles. Le modèle semble corrompu. Choisissez un autre modèle.',
        corruptTemplateLanguages:
          'Ce modèle n’a aucune langue associée et semble corrompu. Choisissez un autre modèle.',
      },
      saveConfiguration: 'Enregistrer la configuration',
      configTitle: 'Configuration du message groupé',
      configDesc:
        'Configurez des campagnes WhatsApp et email à envoyer aux clients sélectionnés.',
      configuration: 'Configuration',
      configurationDesc:
        'Définissez le nom de la campagne et le canal d’envoi groupé.',
      audience: 'Audience',
      selectedCustomers: 'Clients sélectionnés',
      customersReady: '{{count}} clients prêts pour l’envoi groupé',
      selectCustomers: 'Sélectionner des clients',
      selectCustomersSoon: 'La sélection des clients sera bientôt disponible.',
      channel: 'Canal',
      whatsappTitle: 'Message WhatsApp',
      whatsappDesc: 'Configurez le contenu du message WhatsApp.',
      whatsappPlaceholder: 'Écrivez votre message ici...',
      whatsappBusinessTitle: 'Modèle WhatsApp Business',
      whatsappBusinessDesc:
        'Sélectionnez parmi les modèles approuvés par Meta pour l’envoi via WhatsApp Business API.',
      whatsappBusinessEmptyDesc:
        'Sélectionnez un modèle approuvé par Meta pour configurer l’envoi via WhatsApp Business API.',
      selectedTemplate: 'Modèle sélectionné',
      noTemplateSelected: 'Aucun modèle sélectionné',
      selectTemplate: 'Sélectionner un modèle',
      selectTemplateTooltip:
        'Sélectionnez un modèle parmi ceux approuvés par Meta',
      selectTemplateSoon: 'La sélection de modèles sera bientôt disponible.',
      templateModal: {
        title: 'Sélectionnez un modèle',
        description:
          'Choisissez l’un des modèles approuvés sur WhatsApp Business API.',
        messageTypeBadge: 'Pour {{messageType}}',
        countBadge: '{{count}} modèles',
        countBadgeFiltered: '{{count}} sur {{total}}',
        searchPlaceholder: 'Rechercher un modèle par nom...',
        empty: 'Aucun modèle disponible',
        selectAction: 'Sélectionner le modèle',
        recommended:
          'Modèle recommandé selon le type de message sélectionné ({{messageType}})',
        bodyPreview: 'Body Preview',
        noPreview: 'Aucun aperçu du corps disponible.',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          'Ce modèle semble corrompu et ne peut pas être utilisé. Il a probablement été modifié manuellement dans Facebook Business Manager et ne respecte plus la structure requise par WhatsApp Business API.',
        loadError: 'Impossible de charger les modèles',
        loadErrorDesc:
          'Échec du chargement des modèles WhatsApp Business. Veuillez réessayer.',
        selectSuccess: 'Modèle sélectionné avec succès.',
        selectError: 'Échec de la sélection du modèle. Veuillez réessayer.',
      },
      whatsappWithImageTitle: 'WhatsApp avec image',
      whatsappWithImageDesc:
        'Configurez le message, l’image jointe et les variables dynamiques.',
      whatsappMessageText: 'Texte du message WhatsApp',
      emailTitle: 'Email groupé',
      emailDesc: 'Configurez l’email, les pièces jointes et le contenu HTML.',
      emailSubject: 'Objet de l’email',
      emailSubjectPlaceholder: 'Ex : Promo spéciale pour vous 🎉',
      emailBody: 'Contenu de l’email',
      emailBodyHint: 'Si vous insérez un lien, laissez un espace',
      applyTemplate: 'Appliquer le modèle',
      sendTest: 'Envoyer un message de test',
      attachedImage: 'Image jointe',
      uploadImage: 'Téléverser une image',
      removeImage: 'Supprimer l’image',
      uploading: 'Téléversement...',
      removing: 'Suppression...',
      test: {
        title: 'Envoi test',
        emailTitle: 'Envoi test',
        oopsTitle: 'Oops...',
        ok: 'OK',
        send: 'Envoyer',
        testSending: 'Envoi test',
        backwards: 'Retour',
        emptyMessage: 'Saisissez un message pour tester l’envoi.',
        emptySubject: 'Saisissez un objet d’email pour tester l’envoi.',
        missingImage:
          'Aucune image n’est associée à cette campagne. Ajoutez-en une pour tester l’envoi avec image.',
        mobilePlaceholder: 'Téléphone mobile',
        emptyPhone: 'Saisissez un numéro de mobile pour tester l’envoi.',
        emptyEmail: 'Saisissez une adresse email pour tester l’envoi.',
        emailLabel: 'Email :',
        emailPlaceholder:
          'Saisissez l’adresse email où vous souhaitez recevoir le test',
        apiSoon: 'L’API d’envoi test sera bientôt disponible.',
        sendSuccess: 'Message de test envoyé avec succès.',
        sendError:
          'Impossible d’envoyer le message de test. Veuillez réessayer.',
      },
    },
    selectCustomers: {
      pageTitle: 'Attribuer des clients à {{name}}',
      subtitle: 'Filtrez et sélectionnez des clients pour cette campagne',
      subtitleWithBranch: 'Filtrez et sélectionnez des clients pour {{branch}}',
      searchPlaceholder: 'Rechercher un client...',
      selected: 'Sélectionnés',
      clearSelection: 'Effacer la sélection',
      removeAll: 'Tout supprimer',
      saveList: 'Enregistrer la liste des clients',
      bookingPeriodTitle: 'Réservations sur la période',
      bookingRangeLabel: 'Plage de réservations',
      bookingRangePlaceholder: 'Sélectionner une plage de réservations',
      removePeriod: 'Supprimer la période',
      marketingConsentShort: 'Consentement marketing',
      listTitle: 'Liste des clients',
      listDesc: 'Gérer la sélection des clients',
      selectPage: 'Sélectionner la page',
      deselectPage: 'Désélectionner la page',
      selectAll: 'Tout sélectionner',
      deselectAll: 'Tout désélectionner',
      progressAdding: 'Ajout de {{current}} sur {{total}}',
      selectRow: 'Sélectionner le client',
      results: 'Résultats',
      page: 'Page',
      noEmail: 'Pas d’email',
      columns: {
        customer: 'Client',
        phone: 'Téléphone',
        birth: 'Naissance',
        bookings: 'Réservations',
        tags: 'Tags',
      },
      toast: {
        saveSuccess: 'Liste des clients enregistrée avec succès.',
        saveError:
          'Échec de l’enregistrement de la liste des clients. Réessayez.',
        updateError:
          'Échec de la mise à jour de la sélection des clients. Réessayez.',
        selectAllSuccess: '{{count}} clients sélectionnés.',
        selectAllPartial:
          '{{added}} clients ont été ajoutés, puis la requête a échoué. Réessayez Tout sélectionner.',
        clearSuccess: 'Tous les clients ont été retirés de la sélection.',
        clearError:
          'Échec de la suppression de la sélection des clients. Réessayez.',
      },
    },
  },
  fidelity: {
    showPreview: 'Afficher l’aperçu',
    title: 'Fidélité',
    subtitleDefault: 'Consultez et gérez vos fidélités',
    statsCount: '{{count}} fidélités',
    searchPlaceholder: 'Rechercher une fidélité par nom...',
    addProgram: 'Nouvelle fidélité',
    creating: 'Création...',
    saving: 'Enregistrement...',
    createTitle: 'Créer une fidélité',
    createDesc: 'Ajoutez une nouvelle fidélité pour votre succursale.',
    createActiveConflict:
      'Une fidélité {{type}} active « {{name}} » existe déjà pour cette succursale.',
    createActiveConflictAction:
      'Désactivez d’abord la fidélité existante, ou laissez celle-ci inactive.',
    createActiveBlocked:
      'Désactivez Fidélité active pour continuer, ou désactivez « {{name}} » dans la liste.',
    edit: {
      pageTitle: 'Modifier la fidélité',
    },
    programName: 'Nom de la fidélité',
    programNamePlaceholder: 'Saisissez le nom de la fidélité',
    description: 'Description',
    descriptionPlaceholder: 'Saisissez la description de la fidélité',
    policyMessageCard: 'Politique / Carte message',
    tagsOnDownload: 'Tags appliqués au téléchargement',
    tagsOnUse: 'Tags appliqués à l’utilisation',
    tagsPlaceholder: 'Sélectionner des tags',
    type: 'Type',
    typePoints: 'Points',
    typeDiscount: 'Remise',
    typeDiscountPrepaid: 'Remise / prépayée',
    customerCards: 'Cartes clients',
    noThresholds: 'Aucun seuil de points configuré.',
    codeLinePrepaid:
      'Programme avec crédit prépayé et remise appliquée à la carte.',
    color: 'Couleur',
    value: 'Valeur',
    active: 'Actif',
    inactive: 'Inactif',
    activeProgram: 'Fidélité active',
    discountApplied: 'Remise appliquée (%)',
    codeLineRange: 'De {{from}} à {{to}} euros',
    codeLineDiscount: '{{value}} % de remise',
    thresholdCount: '{{count}} seuils',
    customersCount: '{{count}} clients',
    thresholdsTitle: 'Seuils de points',
    thresholdsHint: 'Le backend réordonne les seuils et comble les écarts.',
    addThreshold: 'Seuil',
    updateThreshold: 'Mettre à jour le seuil',
    thresholdFrom: 'À partir de euros*',
    thresholdTo: 'Jusqu’à euros*',
    thresholdPointsLabel: 'Points*',
    thresholdLabel: 'Libellé',
    thresholdColor: 'Couleur du seuil',
    editProgram: 'Modifier la fidélité',
    deleteProgram: 'Supprimer la fidélité',
    duplicate: 'Dupliquer',
    editThreshold: 'Modifier le seuil',
    deleteThreshold: 'Supprimer le seuil',
    noBranch: 'Sélectionnez une succursale pour voir les fidélités',
    empty: 'Aucune fidélité configurée',
    emptyDesc:
      'Créez une fidélité points ou remise pour commencer à générer des cartes clients.',
    cardsTitle: 'Cartes clients',
    cardsSubtitle: 'Cartes émises, QR et soldes',
    cardsEmpty: 'Aucune carte de fidélité trouvée pour cette fidélité.',
    searchCustomerPlaceholder: 'À propos du client, de la carte...',
    createCard: 'Créer une carte',
    refreshCards: 'Actualiser les cartes',
    update: 'Mettre à jour',
    scanQrCode: 'Scanner le code QR',
    useCard: 'Utiliser la carte',
    toggleCardStatus: 'Changer le statut utilisé/disponible',
    operationHistory: 'Historique des opérations',
    qrCodeTitle: 'Scanner le code QR',
    downloadQr: 'Télécharger',
    historyTitle: 'Historique de la carte de fidélité',
    historyEmpty: 'Aucune opération disponible pour cette carte.',
    postedBy: 'Publié par',
    branchCode: 'Succursale',
    fidelityCode: 'Fidélité',
    customerId: 'ID client',
    pointsSuffix: 'points',
    amountSuffix: 'euros',
    cardAvailable: 'disponible',
    cardUsed: 'utilisée',
    columns: {
      qr: 'QR',
      customer: 'Client',
      contacts: 'Contacts',
      card: 'Carte',
      balance: 'Solde',
      state: 'État',
      activation: 'Activation',
      threshold: 'Seuil',
      label: 'Libellé',
      points: 'Points',
      color: 'Couleur',
    },
    createCardPage: {
      title: 'Choisir un client',
      subtitle: 'Sélectionnez un client pour émettre une nouvelle carte.',
      subtitleWithProgram: 'Clients pour {{branch}} · {{program}}',
      newCard: 'Nouvelle carte',
      chooseCustomer: 'Choisir un client',
      searchPlaceholder: 'Rechercher par nom, téléphone ou e-mail...',
      results: 'Résultats',
      page: 'Page',
      availableCustomers: 'Clients disponibles',
      availableCustomersDesc:
        'Sélectionnez un client pour ouvrir la confirmation de création.',
      noEmail: 'Pas d’e-mail',
      columns: {
        customer: 'Client',
        telephone: 'Téléphone',
        wood: 'Visites',
        tags: 'Tags',
      },
    },
    createCardConfirm: {
      title: 'Voulez-vous créer une carte pour ce client ?',
      confirm: 'Oui, créer les cartes',
      creating: 'Création...',
    },
    statCards: {
      programs: 'Fidélité',
      active: 'Actifs',
      types: 'Types',
    },
    deleteModal: {
      title: 'Supprimer la fidélité ?',
      description:
        'Voulez-vous vraiment supprimer cette fidélité ? Cette action est irréversible.',
    },
    deleteThresholdModal: {
      title: 'Supprimer le seuil ?',
      description:
        'Voulez-vous vraiment supprimer ce seuil ? Cette action est irréversible.',
    },
    thresholdModal: {
      createTitle: 'Ajouter un seuil de points',
      editTitle: 'Modifier le seuil de points',
      description:
        'Configurez la plage, les points, le libellé et la couleur du seuil.',
      lowerLimit: 'Limite inférieure*',
      upperLimit: 'Limite supérieure*',
      pointsApplied: 'Points appliqués*',
    },
    toast: {
      createSuccess: 'Fidélité créée avec succès.',
      createError: 'Échec de la création de la fidélité. Réessayez.',
      updateSuccess: 'Fidélité mise à jour avec succès.',
      updateError: 'Échec de la mise à jour de la fidélité. Réessayez.',
      deleteSuccess: 'Fidélité supprimée avec succès.',
      deleteError: 'Échec de la suppression de la fidélité. Réessayez.',
      duplicateSuccess: 'Fidélité dupliquée avec succès.',
      duplicateError: 'Échec de la duplication de la fidélité. Réessayez.',
      thresholdCreateSuccess: 'Seuil créé avec succès.',
      thresholdCreateError: 'Échec de la création du seuil.',
      thresholdUpdateSuccess: 'Seuil mis à jour avec succès.',
      thresholdUpdateError: 'Échec de la mise à jour du seuil.',
      thresholdDeleteSuccess: 'Seuil supprimé avec succès.',
      thresholdDeleteError: 'Échec de la suppression du seuil.',
      cardCreateSuccess: 'Carte de fidélité créée avec succès.',
      cardCreateError: 'Échec de la création de la carte de fidélité.',
      cardUseSuccess: 'Carte marquée comme utilisée.',
      cardUseError: 'Échec de la mise à jour de l’utilisation de la carte.',
      cardToggleSuccess: 'Statut de la carte mis à jour avec succès.',
      cardToggleError: 'Échec de la mise à jour du statut de la carte.',
    },
    operations: {
      addPoints: 'Points ajoutés',
      updatePoints: 'Points mis à jour',
      removePoints: 'Points retirés',
      addBillAmount: 'Montant de facture ajouté',
      addPrepaidAmount: 'Montant prépayé ajouté',
      updatePrepaidAmount: 'Montant prépayé mis à jour',
      disableAccount: 'Compte désactivé',
      createAccount: 'Compte créé',
      used: 'Carte utilisée',
    },
    registration: {
      title: 'Inscription autonome des clients',
      description:
        'Partagez ce lien : les clients peuvent créer leur propre carte fidelity.',
      linkLabel: 'Lien d’inscription fidelity',
      openForm: 'Ouvrir le formulaire',
      copy: 'Copier le lien',
      qr: 'Code QR',
    },
    views: {
      label: 'Programme fidelity',
      configuration: 'Configuration',
      configurationHint: 'Règles, avantages et messages',
      cards: 'Cartes clients',
      cardsHint: 'Cartes émises, QR et mouvements',
      allPrograms: 'Tous les programmes fidelity',
    },
    wizard: {
      program: 'Programme et avantages',
      messages: 'Messages',
      review: 'Récapitulatif',
      back: 'Retour',
      continue: 'Continuer',
      save: 'Enregistrer la configuration',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: 'Étapes de configuration fidélité',
      pointsIntro:
        'Définissez le programme, les règles pour cumuler des points et le message de bienvenue.',
      discountIntro:
        'Définissez le pourcentage, l’apparence de la carte et le message de bienvenue.',
      discountNote:
        'Ce programme utilise un pourcentage de remise et n’accumule pas de points. Le solde prépayé est géré sur la fiche client.',
      benefitPoints: 'Cumul de points',
      benefitDiscount: '{{value}} % de remise',
      messageLabel: 'Message de bienvenue',
      messagePlaceholder: 'Choisissez un message fidelity',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · E-mail {{email}}',
      messageActive: 'Actif',
      messageInactive: 'Inactif',
      channelOn: 'activé',
      channelOff: 'désactivé',
      messageWarning:
        'Activez WhatsApp ou l’e-mail de ce message avant d’enregistrer.',
      messageLoadError: 'Impossible de charger les messages.',
      configureMessages: 'Configurer les messages',
      rulesLabel: 'Règles du programme',
      reviewTitle: 'Récapitulatif du programme',
      name: 'Nom',
      status: 'État',
      benefit: 'Avantage',
      message: 'Message',
      autoUpdates: 'Mises à jour automatiques',
      autoUpdatesValue:
        'Un message dédié à chaque opération, configurable dans Messages.',
      createProgram: 'Le programme',
      createBenefits: 'Avantages',
      createPoints: 'Tranches de dépense',
      createRewards: 'Récompenses à échanger',
      createHistorical: 'Niveaux cumulés',
      createProgramDesc: 'Choisissez le nom et le type de programme.',
      createPointsDesc:
        'Définissez une seule règle d’accumulation. Les mêmes points alimentent le solde disponible et le solde historique.',
      createRewardsDesc:
        'Configurez les seuils et les récompenses échangeables. L’échange déduit les points du solde disponible.',
      createHistoricalDesc:
        'Configurez les points, le nom, la couleur et la remise de chaque niveau, dès 0 point. Le QR Fidelity identifie la remise sans déduire les points historiques.',
      createBenefitsDesc:
        'Définissez les tranches de dépense pour les points, ou le pourcentage de remise.',
      createMessagesDesc:
        'Configurez WhatsApp et l’e-mail de bienvenue avec le QR de la carte.',
      createReviewDesc:
        'Vérifiez les avantages, l’état et le message avant de créer le programme.',
      createAction: 'Créer le programme',
      afterSaveNote:
        'Vous pourrez créer les cartes client après avoir enregistré le programme.',
      thresholdStepHint:
        'Pour chaque tranche de dépense, indiquez combien de points le client reçoit.',
      thresholdReviewLine: 'De {{from}} à {{to}} € : {{points}} points',
    },
    validation: {
      nameMin: 'Le nom de la fidélité doit contenir au moins 2 caractères',
      nameMax: 'Le nom de la fidélité ne peut pas dépasser 100 caractères',
      colorInvalid: 'Saisissez une couleur hexadécimale valide (ex. #FFFFFF)',
      thresholdRequired: 'Ajoutez au moins un seuil.',
      thresholdRange:
        'La limite supérieure doit être supérieure ou égale à la limite inférieure.',
      thresholdLower: 'Saisissez une limite inférieure valide.',
      thresholdUpper: 'Saisissez une limite supérieure valide.',
      thresholdPoints: 'Saisissez des points valides.',
      thresholdLabelRequired: 'Le libellé du seuil est obligatoire.',
      thresholdLabelMax: 'Le libellé ne peut pas dépasser 100 caractères.',
    },
  },
  marketingGuide: {
    infoButton: 'Guide marketing',
    title: 'Choisir le bon outil marketing',
    intro:
      'Les promotions et Fidelity peuvent offrir un avantage, mais répondent à des objectifs différents.',
    promotionTitle: 'Promotions · une campagne à durée limitée',
    promotionBody:
      'Utilisez une promotion pour attirer de nouveaux clients, réactiver les clients occasionnels ou soutenir une période calme. Le client participe avec un lien public ou un QR et reçoit un coupon, sans accumuler de points.\nExemple : « 10 % de remise jusqu’à dimanche, dès 30 € d’achat, une fois par client. »',
    fidelityTitle: 'Fidelity · une raison de revenir',
    fidelityBody:
      'Utilisez Fidelity pour récompenser les visites répétées. Les achats rapportent des points selon les règles configurées ; une fois un seuil atteint, le client peut échanger une récompense. Le solde et l’historique restent associés à sa carte.\nExemple : « Cumulez des points à chaque ticket et choisissez une récompense à un palier. »',
    rulesTitle: 'Bonnes pratiques marketing',
    rulesBody:
      '1. Fixez un objectif clair : acquérir, réactiver, augmenter les visites ou le panier moyen.\n2. Protégez votre marge : choisissez une remise et un minimum d’achat viables, avec une limite de quantité ou de budget.\n3. Précisez les conditions : dates, produits ou canaux concernés, minimum d’achat et cumul possible.\n4. Facilitez la compréhension et l’utilisation ; trop de conditions réduisent la participation.\n5. Mesurez les utilisations et les achats suivants, pas seulement les coupons distribués ou les points cumulés.',
    togetherTitle: 'Ils peuvent être complémentaires',
    togetherBody:
      'Utilisez une promotion limitée dans le temps pour faire venir un client, puis proposez-lui Fidelity pour lui donner une raison durable de revenir. Identifiez clairement le coupon promotionnel et la récompense Fidelity.',
  },
  promotions: {
    title: 'Promotions',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: 'Liste des promotions pour {{branch}}',
    subtitleDefault: 'Afficher et gérer la liste des promotions',
    pageDescription:
      'Gérez les promotions, coupons et configurations promo de l’établissement',
    statsCount: '{{count}} promotions',
    searchPlaceholder: 'Rechercher des promotions par nom...',
    promoName: 'Nom de la promo',
    promoNamePlaceholder: 'Saisir le nom de la promotion',
    description: 'Description',
    descriptionPlaceholder: 'Saisir la description de la promotion',
    used: 'Utilisés',
    usedCouponCount: '{{count}} bon',
    downloaded: 'Téléchargés',
    messages: 'Messages',
    messagesTooltip: 'Messages envoyés via cette promo',
    messageHistory: {
      title: 'Historique des messages',
      reference: 'Référence {{code}}',
      empty: 'Aucun message disponible.',
      refresh: 'Actualiser',
      loadError: 'Échec du chargement des messages.',
      attempts: '{{count}} tentatives',
    },
    customerCoupons: {
      title: 'Coupons clients',
      subtitle: 'Promo : {{name}} · {{count}} coupons émis',
      discount: '{{percent}} % de réduction',
      redeemed: 'Utilisé',
      notRedeemed: 'Non utilisé',
      generatedOn: 'Généré le {{date}}',
      redeemedOn: 'Utilisé le {{date}}',
      operator: 'Opérateur : {{name}}',
      empty: 'Aucun coupon émis pour le moment.',
      loadError: 'Échec du chargement des coupons clients.',
      loading: 'Chargement des coupons...',
    },
    status: 'Statut',
    active: 'Active',
    expired: 'Expirée',
    url: 'URL',
    qrCode: 'Code QR',
    preview: 'Aperçu',
    copyUrl: 'Copier l’URL',
    duplicate: 'Dupliquer',
    stop: 'Arrêter',
    restart: 'Redémarrer',
    editPromotion: 'Modifier la promotion',
    delete: 'Supprimer la promotion',
    addPromotion: 'Ajouter une promotion',
    creating: 'Création...',
    createTitle: 'Créer une promotion',
    createDesc:
      'Ajoutez une nouvelle campagne de messages pour votre succursale.',
    createWizard: {
      detailsDesc:
        'Définissez le nom, les coupons disponibles et la remise. Vous pourrez personnaliser le formulaire client après la création.',
      messageDesc:
        'Choisissez l’un des trois messages configurés dans Paramètres → Messages.',
      reviewHint: 'Vérifiez les paramètres avant de créer la promotion.',
      stepCount: '{{current}} / {{total}}',
      back: 'Retour',
      continue: 'Continuer',
      createAndCustomize: 'Créer et personnaliser',
      couponHint:
        'Utilisez 0 si la promotion n’a pas de remise en pourcentage.',
      automaticUpdatesLabel: 'Mises à jour automatiques',
      automaticUpdates:
        'Un message dédié à chaque action, configurable dans Messages.',
    },
    close: 'Fermer',
    save: 'Enregistrer',
    saveChanges: 'Enregistrer les modifications',
    buttonLabel: 'Texte du bouton',
    buttonLabelPlaceholder: 'Saisir le texte du bouton',
    redirectPage: 'Page de redirection',
    redirectPagePlaceholder: 'URL personnalisée de page de remerciement',
    percentageDiscount: 'Remise (%)',
    maxCouponCount: 'Coupons max',
    maxUsageCount: 'Utilisation max',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    noBranch: 'Sélectionnez une succursale pour voir les promotions',
    empty: 'Aucune promo disponible',
    emptyDesc:
      "Vous n'avez pas encore créé de promotions pour cet établissement. Commencez par créer votre première promo.",
    deleteModal: {
      title: 'Supprimer la promotion ?',
      description:
        'Êtes-vous sûr de vouloir supprimer cette promotion ? Cette action est irréversible.',
    },
    validation: {
      promotionNameMin: 'Le nom doit contenir au moins 2 caractères',
      promotionNameMax: 'Le nom ne peut pas dépasser 120 caractères',
      couponMin: 'Saisissez un entier supérieur à zéro.',
      discountRange: 'Saisissez un entier de 0 à 100.',
      redirectPageInvalid:
        'Saisissez une URL valide (ex. https://example.com).',
    },
    toast: {
      createSuccess: 'Promotion créée avec succès.',
      createError: 'Impossible de créer la promotion. Veuillez réessayer.',
      updateSuccess: 'Promotion mise à jour avec succès.',
      updateError:
        'Impossible de mettre à jour la promotion. Veuillez réessayer.',
      deleteSuccess: 'Promotion supprimée avec succès.',
      deleteError: 'Impossible de supprimer la promotion. Veuillez réessayer.',
      duplicateSuccess: 'Promotion dupliquée avec succès.',
      duplicateError:
        'Impossible de dupliquer la promotion. Veuillez réessayer.',
      stopSuccess: 'Promotion arrêtée avec succès.',
      stopError: 'Impossible d’arrêter la promotion. Veuillez réessayer.',
      restartSuccess: 'Promotion redémarrée avec succès.',
      restartError:
        'Impossible de redémarrer la promotion. Veuillez réessayer.',
      logoUploadSuccess: 'Logo téléchargé avec succès.',
      logoUploadError: 'Échec du téléchargement du logo.',
      logoRemoveSuccess: 'Logo supprimé avec succès.',
      logoRemoveError: 'Échec de la suppression du logo.',
      posterUploadSuccess: 'Affiche téléchargée avec succès.',
      posterUploadError: 'Échec du téléchargement de l’affiche.',
      posterRemoveSuccess: 'Affiche supprimée avec succès.',
      posterRemoveError: 'Échec de la suppression de l’affiche.',
    },
    edit: {
      pageTitle: 'Modifier la promotion',
      subtitle: 'Configurez le formulaire et les paramètres de la promotion.',
      tabListLabel: 'Sections de modification de la promotion',
      wizard: {
        stepListLabel: 'Étapes de configuration de la promotion',
        form: 'Formulaire client',
        formDesc:
          'Personnalisez le formulaire que le client remplit pour demander le coupon.',
        rules: 'Règles',
        rulesDesc:
          'Définissez la période, la disponibilité, la remise et les informations demandées au client.',
        message: 'Message',
        messageDesc:
          'Choisissez l’un des trois messages configurés dans Paramètres → Messages.',
        review: 'Récapitulatif',
        reviewDesc:
          'Vérifiez les paramètres et enregistrez. Les modifications non enregistrées restent sur cette page.',
        editStep: 'Modifier',
        period: 'Période',
        requestedFields: 'Informations demandées',
        noDate: 'Sans date',
        none: 'Aucun',
      },
      uploadImage: 'Télécharger une image',
      removeImage: 'Supprimer l’image',
      uploading: 'Téléchargement...',
      removing: 'Suppression...',
      tabs: {
        formConfig: 'Configuration du formulaire',
        promoConfig: 'Configuration de la promotion',
      },
      formConfig: {
        media: 'Médias',
        logo: 'Logo promo',
        details: 'Détails du formulaire',
        promoText: 'Texte promo',
        promoLink: 'Lien promo',
      },
      promoConfig: {
        poster: 'Affiche',
        posterLabel: 'Affiche promo',
        options: 'Options',
        useOnlyOnce: 'Coupon utilisable une seule fois',
        useOnlyOnceDesc:
          'Limiter le téléchargement du coupon à une fois par client',
        showEmail: 'Demander l’email',
        showEmailDesc: 'Afficher le champ email dans le formulaire promo',
        showAddress: 'Demander l’adresse',
        showAddressDesc: 'Afficher le champ adresse dans le formulaire promo',
        showCap: 'Demander le code postal',
        showCapDesc: 'Afficher le champ code postal dans le formulaire promo',
        showDob: 'Demander la date de naissance',
        showDobDesc:
          'Afficher le champ date de naissance dans le formulaire promo',
        showCountry: 'Demander le pays',
        limits: 'Dates et limites',
        startDate: 'Date de début',
        endDate: 'Date de fin',
        tags: 'Tags',
        tagsOnDownload: 'Tags appliqués au téléchargement',
        tagsOnUse: 'Tags appliqués à l’utilisation',
        tagsPlaceholder: 'Sélectionner des tags',
        messaging: 'Messagerie',
        messageLabel: 'Message pour le client',
        messagePlaceholder:
          'Choisissez l’une des trois configurations partagées.',
        messageStatus:
          '{{status}} · WhatsApp : {{whatsapp}} · E-mail : {{email}}',
        messageActive: 'Configuration active',
        messageInactive: 'Configuration désactivée',
        channelOn: 'actif',
        channelOff: 'désactivé',
        messageWarning:
          'Activez au moins un canal dans Messages pour envoyer cette communication.',
        configureMessages:
          'Configurez les textes, canaux et modèles dans Messages',
        messageLoadError:
          'Impossible de charger les configurations. Réessayez depuis Messages.',
        sharedMessageNote:
          'Chaque action utilise le message dédié configuré dans Messages. PROMO_INFO est réservé aux mises à jour de statut.',
        whatsappMessage: 'Message WhatsApp du coupon',
        whatsappSubtitle: 'Message envoyé avec le code QR promo',
        whatsappPlaceholder: 'Écrivez votre message ici...',
        applyTemplate: 'Appliquer le modèle par défaut',
        sendMessage: 'Envoyer le message',
      },
      detailsTitle: 'Détails de la promotion',
      detailsDesc:
        'Mettez à jour les informations et les limites de la campagne.',
      branchHint: 'Succursale : {{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: 'Reçu avec modifications',
    processedModified: 'Traité mais modifié',
    processed: 'Traité',
    title: 'Commandes fournisseurs',
    subtitle: 'Achats, livraisons et vérification des produits',
    history: 'Historique des commandes',
    create: 'Créer une commande',
    search: 'Rechercher une commande ou un produit…',
    supplier: 'Fournisseur',
    allSuppliers: 'Tous les fournisseurs',
    invalidDates: 'Sélectionnez une plage de dates valide.',
    onlyModified: 'Commandes modifiées uniquement',
    loadError: 'Impossible de charger les commandes.',
    ordersOn: 'Commandes du {{date}} · {{count}}',
    empty: 'Aucune commande trouvée',
    order: 'Commande',
    delivery: 'Livraison',
    status: 'Statut',
    totalVat: 'Total TTC',
    perPage: 'Par page',
    orderCount: '{{count}} commandes',
    modified: 'Modifiée',
    recipient: 'Destinataire',
    chooseRecipient: 'Choisir un fournisseur ou un établissement',
    branchSuffix: 'Établissement',
    deliveryDate: 'Date de livraison',
    futureDate: 'Choisissez aujourd’hui ou une date future.',
    pastDate: 'La date de livraison doit être aujourd’hui ou une date future.',
    receiveFrom: 'Réception à partir de',
    receiveTo: 'Jusqu’à',
    catalogSearch: 'Rechercher dans le catalogue…',
    catalogError: 'Impossible de charger le catalogue.',
    noProducts: 'Aucun produit disponible',
    product: 'Produit',
    vatLine: '{{unit}} · TVA {{vat}}%',
    net: 'Net',
    quantity: 'Quantité {{name}}',
    removeNamed: 'Retirer {{name}}',
    remove: 'Retirer',
    add: 'Ajouter',
    productCount: '{{count}} produits',
    totalLine: 'Total TTC : {{amount}}',
    createHint:
      'Choisissez un destinataire, une date et au moins un produit avec une quantité positive. L’heure de fin doit être après l’heure de début.',
    createError: 'Impossible de créer la commande.',
    calendarTitle: 'Calendrier des commandes',
    otherDate: 'Autre date',
    loadPeriod: 'Charger une autre période',
    today: 'Aujourd’hui',
    previousDays: 'Faire défiler vers les jours précédents',
    nextDays: 'Faire défiler vers les jours suivants',
    window: '(±20 jours)',
    incoming: 'En arrivage',
    toReview: 'À vérifier',
    calendarTotals: 'Totaux · tous les fournisseurs',
    calendarError: 'Les totaux du calendrier ne sont pas disponibles.',
    calendarDays: 'Jours du calendrier des commandes',
    incomingSummary: '{{count}} en arrivage',
    reviewSummary: '{{count}} à vérifier',
    processedSummary: '{{count}} traitées',
    ordersSummary: '{{count}} commandes, détail des statuts indisponible',
    countsUnavailable: 'totaux indisponibles',
    calendarHint:
      'Chaque jour inclut les commandes programmées, en cours et traitées. La recherche et le fournisseur filtrent la liste ci-dessous.',
    exportJson: 'Exporter JSON',
    importJson: 'Importer JSON',
    exported: 'Fichier exporté.',
    importedCount: '{{count}} éléments importés.',
    exportError: 'L’exportation a échoué. Réessayez.',
    invalidJson: 'JSON non valide.',
    importProducts:
      'Saisissez un tableau de produits avec un nom, un prix net, une TVA et une unité valides.',
    importSuppliers:
      'Saisissez un tableau de fournisseurs avec un nom pour chaque ligne.',
    importError: 'L’importation a échoué. Vérifiez les données et réessayez.',
    importTitle: 'Importer {{label}}',
    importAction: 'Importer',
    jsonFile: 'Fichier JSON',
    fileTooLarge: 'Le fichier dépasse 5 Mo.',
    fileReadError: 'Impossible de lire le fichier.',
    importOrdersHint:
      'L’importation crée de nouvelles commandes pour l’établissement sélectionné. Une nouvelle importation crée d’autres commandes.',
    jsonArray: 'Tableau JSON',
    ordersLabel: 'commandes',
    productsLabel: 'produits',
    suppliersLabel: 'fournisseurs',
    statuses: {
      BOZZA: 'Brouillon',
      PROGRAMMATO: 'Programmé',
      IN_PREPARAZIONE: 'En préparation',
      IN_CONSEGNA: 'En livraison',
      DA_VERIFICARE: 'À vérifier',
      RICEVUTO: 'Reçu',
      NON_CONSEGNATO: 'Non livré',
      ANNULLATO: 'Annulé',
      INVIATO: 'Envoyé',
      DA_CONFERMARE: 'À confirmer',
      CONSEGNATO: 'Livré',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Match to product',
    skipLine: 'Skip this line',
    duplicateMatch: 'Match each line to a different product.',
    continue: 'Continue',
    category: 'Category',
    close: 'Close',
    allCategories: 'All categories',
    personalWhatsApp: 'Personal WhatsApp',
    localWhatsApp: 'Restaurant WhatsApp',
    confirmSend: 'Send the order to the supplier using this channel?',
    confirmEdit: 'Save the updated order quantities?',
    checkQuantities: 'Check quantities',
    alreadyDelivered: 'Goods have arrived: proceed to verification',
    createReview: 'Order review',
    discardDraft: 'Discard the selected products?',
    savedShare:
      'Order saved. Would you like to choose how to send it to the supplier?',
    title: 'Commande fournisseur',
    orderTitle: 'Commande n° {{id}}',
    back: 'Commandes',
    unavailable: 'Cette commande n’est pas disponible.',
    requestedDelivery: 'Livraison demandée',
    changeDate: 'Changer la date',
    orderTotal: 'Total de la commande TTC',
    netLine: 'Net {{net}} · TVA {{vat}}',
    products: 'Produits commandés',
    addReceived: 'Ajouter un produit reçu',
    verifyAll: 'Tout vérifier',
    ordered: 'Commandée',
    sent: 'Envoyée',
    unitNet: 'Net unitaire',
    received: 'Reçue',
    receivedPrice: 'Prix net reçu',
    verified: 'Vérifié',
    verifyNamed: 'Vérifier {{name}}',
    receivedNamed: 'Quantité reçue {{name}}',
    receivedPriceNamed: 'Prix reçu {{name}}',
    editOrder: 'Modifier la commande',
    prepare: 'Préparer la commande',
    dispatch: 'Confirmer l’expédition',
    arrived: 'Livraison arrivée',
    cancelOrder: 'Annuler la commande',
    notDelivered: 'Non livrée',
    receiptTitle: 'Contrôler la réception',
    invoiceTotal: 'Total facture TTC',
    receivedProductsTotal: 'Total calculé des produits (TTC)',
    invoiceTotalHint: 'Montant enregistré à la confirmation de réception.',
    invoiceDraftHint:
      'Montant saisi pour la réception ; enregistré à la confirmation.',
    insertedAmount: 'Total saisi',
    expectedAmount: 'Total prévu de la commande',
    recordedAmount: 'Total enregistré',
    receivedProductsTitle: 'Récapitulatif des produits reçus',
    lineTotal: 'Total ligne TTC',
    receivedLineCalculation: '{{quantity}} × {{price}} + TVA {{vat}} %',
    invoiceGoodsDifference: 'Différence facture − produits : {{amount}}',
    pendingVerification: 'À vérifier',
    verifiedProgress: '{{checked}} lignes vérifiées sur {{total}}',
    receiptBlockedTitle: 'Pour activer « Confirmer la réception » :',
    receiptNeedsProducts: 'Ajoutez au moins un produit reçu.',
    receiptNeedsChecks:
      'Cochez « Vérifié » sur les lignes restantes : {{count}}.',
    receiptInvalidLines:
      'Corrigez les quantités ou prix reçus invalides (zéro ou plus). Lignes : {{count}}.',
    receiptInvalidInvoice:
      'Saisissez un total de facture valide, supérieur ou égal à zéro.',
    receiptSaving: 'Attendez la fin de l’enregistrement de la commande.',
    receiptUploading:
      'Attendez la fin du téléchargement de la photo de facture.',
    receiptAnalyzing: 'Attendez la fin de l’analyse de la facture.',
    goToVerification: 'Aller à la prochaine ligne à vérifier',
    receiptReady:
      'Toutes les lignes sont vérifiées. Vous pouvez confirmer la réception.',
    receiptTotalDifference:
      'Le total de facture {{invoice}} diffère du total des produits reçus {{products}}. Vérifiez les montants ; cette différence ne bloque pas la confirmation.',
    movedToHistory: 'Message déplacé dans l’historique des commandes',
    receiptHint:
      'Vérifiez la quantité reçue et le prix net, puis cochez « Vérifié » sur chaque ligne. Modifier une quantité ou un prix annule la vérification de cette ligne.',
    confirmReceipt: 'Confirmer la réception',
    invoice: 'Facture',
    invoiceAlt: 'Photo de la facture',
    invoicePhoto: 'Photo de la facture',
    choosePhoto: 'Choisir un fichier',
    ocrLabel: 'Notes optionnelles',
    ocrPlaceholder:
      'Notes optionnelles pour l’analyse IA. Laissez vide pour analyser uniquement la photo.',
    ocrRunning: 'Analyse de la facture avec l’IA…',
    ocrFailed:
      'Échec de l’analyse. Essayez une autre photo ou ajoutez des notes.',
    clearPhotoAndText: 'Effacer photo et texte',
    readingPhotosTitle: 'Chargement de la photo…',
    ocrProcessingTitle: 'Analyse de la facture avec l’IA…',
    ocrProcessingHint:
      'Lecture de la photo sur le serveur. Cela peut prendre quelques secondes.',
    aiAnalyzingTitle: 'Analyse de la facture avec l’IA…',
    aiAnalyzingHint:
      'Lecture de la photo sur le serveur. Cela peut prendre quelques secondes.',
    photoUploadingTitle: 'Enregistrement de la photo…',
    photoUploadingHint: 'Envoi de l’image avant l’analyse.',
    analyze: 'Analyser la facture',
    reviewExtracted: 'Vérifiez les valeurs extraites avant de les appliquer.',
    applyMatches: 'Appliquer aux produits correspondants',
    photoType: 'Importez une image JPG ou PNG de 5 Mo maximum.',
    imageInvalid: 'Image non valide.',
    imageReadError: 'Impossible de lire l’image.',
    photoSaved: 'Photo de la facture enregistrée.',
    photoSaveError: 'Impossible d’enregistrer la photo de la facture.',
    extractError: 'L’analyse de la facture a échoué.',
    communications: 'Messages au fournisseur',
    configureMessages: 'Configurer les messages',
    communicationsError: 'Impossible de charger les messages.',
    openSupplier: 'Ouvrir la page fournisseur',
    recipientEmail: 'E-mail du destinataire',
    sendEmail: 'Envoyer l’e-mail',
    updated: 'Commande mise à jour.',
    operationError: 'L’opération a échoué.',
    sendFailed: 'L’envoi a échoué.',
    messageSent: 'Message envoyé.',
    requestLogged:
      'Demande enregistrée. Vérifiez le résultat dans les messages.',
    communicationError: 'Le message a échoué.',
    saveDate: 'Enregistrer la date',
    invalidDate: 'Date non valide.',
    confirmTitle: 'Confirmer l’action',
    confirmAction: 'Confirmer',
    confirmBody: 'Mettre à jour cette commande ?',
    confirmReceive:
      'La réception sera enregistrée avec les quantités et les prix vérifiés.',
    unexpectedProduct: 'Produit reçu non prévu',
    catalogUnavailable: 'Le catalogue n’est pas disponible.',
  },
  supplierOrderHistory: {
    title: 'Historique des commandes',
    subtitle:
      'Rechercher les achats par fournisseur, produit et date de livraison',
    back: 'Commandes fournisseurs',
    dateRange: 'Dates de livraison',
    load: 'Charger l’historique',
    loading: 'Chargement…',
    reset: 'Réinitialiser les filtres',
    orders: 'Commandes',
    ordered: 'Commandé',
    received: 'Reçu',
    vatProducts: 'TTC · produits filtrés',
    allResults: 'Tous les résultats filtrés',
    search: 'Rechercher par nom de produit…',
    hint: 'Tous les statuts, y compris les archivés · Montants TTC',
    summaryError: 'Impossible de charger le récapitulatif.',
    dirty:
      'Filtres modifiés : appuyez sur « Charger l’historique » pour actualiser les résultats et les exports.',
    loadError: 'Impossible de charger l’historique.',
    results: 'Résultats de la recherche',
    resultsMeta:
      '{{count}} commandes · L’export inclut tous les résultats filtrés.',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: 'Export…',
    empty: 'Aucune commande trouvée avec ces filtres',
    deliveryDate: 'Date de livraison',
    products: 'Produits',
    filteredTotal: 'Produits filtrés · TTC',
    exported: '{{orders}} commandes et {{rows}} lignes produit exportées.',
    exportError: 'L’exportation a échoué.',
    allSuppliers: 'Tous les fournisseurs',
    exportSheetProducts: 'Produits',
    exportSheetSearch: 'Recherche',
    exportTitle: 'Historique des commandes fournisseurs',
    exportOrder: 'Commande',
    exportDelivery: 'Date de livraison',
    exportSupplier: 'Fournisseur',
    exportSupplierCode: 'Code fournisseur',
    exportStatus: 'Statut',
    exportProduct: 'Produit',
    exportUnit: 'Unité',
    exportOrderedQty: 'Quantité commandée',
    exportSentQty: 'Quantité envoyée',
    exportReceivedQty: 'Quantité reçue',
    exportOrderedNet: 'Prix net commandé',
    exportReceivedNet: 'Prix net reçu',
    exportVat: 'TVA %',
    exportOrderedTotal: 'Total commandé TTC',
    exportReceivedTotal: 'Total reçu TTC',
    exportQtyChange: 'Variation de quantité',
    exportPriceChange: 'Variation de prix',
    exportYes: 'Oui',
    exportNo: 'Non',
    exportBranch: 'Établissement',
    exportDeliveryFrom: 'Livraison du',
    exportDeliveryTo: 'Livraison au',
    exportProductName: 'Nom du produit',
    exportAllProducts: 'Tous',
    exportStatuses: 'Statuts',
    exportAllStatuses: 'Tous, y compris les archivés',
    exportOrderCount: 'Total des commandes',
    exportOrderedAmount: 'Montant commandé TTC',
    exportReceivedAmount: 'Montant reçu TTC',
    exportProductRows: 'Lignes produit',
    exportPage:
      'Page {{page}} sur {{pages}} · {{orders}} commandes · {{rows}} lignes produit',
  },
  suppliersPage: {
    title: 'Fournisseurs',
    subtitle: 'Contacts et catalogues pour les commandes de l’établissement',
    create: 'Nouveau fournisseur',
    active: 'Actifs',
    archived: 'Archivés',
    search: 'Rechercher un fournisseur, un e-mail ou un n° de TVA…',
    noBranch: 'Sélectionnez un établissement.',
    loadError: 'Impossible de charger les fournisseurs.',
    supplier: 'Fournisseur',
    contacts: 'Contacts',
    catalog: 'Catalogue',
    openCatalog: 'Ouvrir le catalogue',
    productCount: '{{count}} produits',
    vatLine: 'TVA {{vat}}',
    editNamed: 'Modifier {{name}}',
    archiveNamed: 'Archiver {{name}}',
    restoreNamed: 'Restaurer {{name}}',
    empty: 'Aucun fournisseur trouvé.',
    archiveTitle: 'Archiver le fournisseur',
    restoreTitle: 'Restaurer le fournisseur',
    archiveAction: 'Archiver',
    restoreAction: 'Restaurer',
    archiveDesc:
      'Archiver {{name}} ? Le catalogue et l’historique des commandes sont conservés. Le fournisseur ne pourra plus être choisi pour de nouvelles commandes tant qu’il n’est pas restauré.',
    restoreDesc: 'Restaurer {{name}} parmi les fournisseurs actifs ?',
    saveError: 'Impossible d’enregistrer le fournisseur. Réessayez.',
    archiveError:
      'Impossible de mettre à jour l’archivage du fournisseur. Réessayez.',
    editTitle: 'Modifier le fournisseur',
    groupContacts: 'Coordonnées',
    groupFiscal: 'Données fiscales',
    groupAddress: 'Adresse',
    legalName: 'Raison sociale',
    email: 'E-mail',
    phone: 'Téléphone',
    pec: 'E-mail certifié',
    vat: 'N° de TVA',
    taxCode: 'Code fiscal',
    street: 'Adresse',
    city: 'Ville',
    postcode: 'Code postal',
    country: 'Pays',
    fromInvoice: 'Depuis une facture',
    invoiceTitle: 'Fournisseur depuis une facture',
    createSupplier: 'Créer le fournisseur',
    invoicePhoto: 'Photo de la facture',
    photosSelected: '{{count}} photos sélectionnées',
    removePhotos: 'Retirer les photos',
    readingPhotos: 'Lecture des photos…',
    tooManyPhotos: 'Sélectionnez jusqu’à 5 images JPG ou PNG, 5 Mo chacune.',
    invalidImage: 'Image non valide.',
    imageReadError: 'Impossible de lire la photo.',
    photosReadError: 'Impossible de lire les photos. Réessayez.',
    analyze: 'Analyser',
    existing: 'Fournisseurs déjà enregistrés',
    analyzeError: 'Impossible d’analyser la facture.',
    createError: 'Impossible de créer le fournisseur.',
  },
  menu: {
    ingredientsPage: {
      title: 'Ingrédients',
      subtitle: 'Ingrédients au choix, catégories et suppléments',
      search: 'Rechercher un ingrédient…',
      createIngredient: 'Créer un ingrédient',
      createCategory: 'Créer une catégorie',
      ingredients: 'Ingrédients',
      categories: 'Catégories',
      loadError: 'Impossible de charger le catalogue.',
      operationError: 'L’opération a échoué. Réessayez.',
      filterCategory: 'Filtrer la catégorie',
      allCategories: 'Toutes les catégories',
      pageHint:
        'Les catégories regroupent les ingrédients de cette page. Une catégorie peut continuer sur la page suivante.',
      available: 'Disponible',
      unavailable: 'Désactivé',
      editNamed: 'Modifier {{name}}',
      deleteNamed: 'Supprimer {{name}}',
      emptyCategory: 'Aucun ingrédient dans cette catégorie.',
      emptyPage:
        'Aucun ingrédient sur cette page. Modifiez la recherche ou revenez à la page précédente.',
      perPage: 'Ingrédients par page',
      createCategoryFirst: 'Créez une catégorie pour commencer.',
      categoryOrder:
        'Ordre du catalogue. Chaque recette peut personnaliser l’ordre de ses catégories.',
      moveUp: 'Monter {{name}}',
      moveDown: 'Descendre {{name}}',
      newIngredient: 'Nouvel ingrédient',
      editIngredient: 'Modifier l’ingrédient',
      newCategory: 'Nouvelle catégorie',
      editCategory: 'Modifier la catégorie',
      ingredientDesc: 'Nom, catégorie et supplément.',
      categoryDesc: 'Nom et traductions de la catégorie.',
      name: 'Nom *',
      category: 'Catégorie *',
      select: 'Sélectionner',
      supplement: 'Supplément € *',
      description: 'Description',
      translations: 'Traductions',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: 'Supprimer du catalogue',
      deleteDesc:
        'Supprimer « {{name}} » ? Les ingrédients liés aux plats et les catégories non vides sont protégés.',
      delete: 'Supprimer',
    },
    recipeEditor: {
      createTitle: 'Créer une recette',
      tabsLabel: 'Recette et traductions',
      configurationTab: 'Recette',
      basePrice: 'Prix de base',
      dishName: 'Nom du plat',
      typeTitle: 'Type de plat',
      simpleTitle: 'Recette simple',
      simpleDesc: 'Nom, description et allergènes',
      customizableTitle: 'Recette personnalisable',
      customizableDesc: 'Base + ingrédients au choix',
      compositeTitle: 'Menu composé',
      compositeDesc: 'Plats simples à prix fixe',
      wineTitle: 'Vin / Champagne',
      wineDesc: 'Bouteille, verre et caractéristiques',
      includedDishes: 'Plats inclus',
      searchDishes: 'Rechercher un plat…',
      includedSections: 'Section des plats inclus',
      allSections: 'Toutes les sections',
      selectionHint:
        '{{count}} plats sélectionnés. Les sélections sont conservées lors des changements de page ou des recherches.',
      cannotInclude: '{{reason}} : ne peut pas être inclus',
      weightedDish: 'Prix au poids',
      noDishes: 'Aucun plat trouvé.',
      dishesPerPage: 'Plats par page',
      range: '{{from}}–{{to}} sur {{total}}',
      previous: 'Précédent',
      next: 'Suivant',
      minTwo: 'Sélectionnez au moins deux plats simples.',
      selectableIngredients: 'Ingrédients au choix',
      minimumTotal: 'Minimum total',
      maximumTotal: 'Maximum total',
      searchIngredients: 'Rechercher un ingrédient…',
      ingredientLoadError: 'Impossible de charger les ingrédients. Réessayer',
      disabled: 'Désactivé',
      ingredientPage: '{{total}} ingrédients · page {{page}}',
      categoryFilter: 'Catégorie',
      allCategories: 'Toutes les catégories',
      noIngredients: 'Aucun ingrédient trouvé.',
      categoryOrderHelp:
        'Utilisez les flèches pour ordonner les catégories présentées au client lors de la personnalisation. Seules les catégories avec des ingrédients sélectionnés apparaissent. Le tri ne modifie ni les ingrédients ni les prix.',
      categoryOrder: 'Ordre des catégories dans la recette',
      moveEarlier: 'Déplacer {{name}} avant',
      moveLater: 'Déplacer {{name}} après',
      availability: 'Disponibilité et prix',
      restaurantMenu: 'Menu du restaurant',
      takeaway: 'À emporter',
      delivery: 'Livraison',
      enabled: 'Activé',
      channelPrice: 'Prix {{channel}} (€)',
      invalidChoices:
        'Sélectionnez les ingrédients et vérifiez le nombre minimum et maximum de choix.',
      invalidPrices: 'Prix non valides : utilisez au maximum deux décimales.',
      invalidComposition:
        'Les recettes personnalisables et les menus composés nécessitent un prix fixe et ne peuvent pas être des vins.',
      invalidForm: 'Vérifiez le nom, le prix et les champs obligatoires.',
      numericError: 'Saisissez un nombre valide.',
      nonNegativeError: 'La valeur doit être supérieure ou égale à zéro.',
      wineRed: 'Rouge',
      wineWhite: 'Blanc',
      wineRose: 'Rosé',
      wineChampagne: 'Champagne',
      wineSparkling: 'Effervescent',
      wineOther: 'Autre',
    },
    title: 'Menu',
    disabled: {
      badge: 'Section non activée',
      title: 'Cette section n’est pas activée',
      description:
        'Cette fonctionnalité est actuellement désactivée pour votre compte. Contactez le support pour en savoir plus.',
      infoTitle: 'Besoin d’accéder à cette section ?',
      infoDescription:
        'Contactez notre équipe d’assistance et nous vous aiderons à démarrer.',
      footerHelp: 'Besoin d’aide encore ?',
      footerHelpDesc:
        'Notre équipe d’assistance est disponible 24h/24 et 7j/7.',
    },
    subtitleDefault: 'Consultez et gérez vos menus numériques',
    statsCount: '{{count}} menu',
    searchPlaceholder: 'Rechercher des menus par nom...',
    menuName: 'Nom du menu',
    menuNamePlaceholder: 'Saisir le nom du menu',
    description: 'Description',
    descriptionPlaceholder: 'Saisir la description du menu',
    newMenu: 'Nouveau menu',
    createTitle: 'Créer un nouveau menu',
    createAction: 'Créer',
    url: 'URL',
    qrCode: 'Code QR',
    preview: 'Aperçu',
    copyUrl: 'Copier l’URL',
    editMenu: 'Modifier le menu',
    deleteMenu: 'Supprimer le menu',
    duplicate: 'Dupliquer',
    close: 'Fermer',
    saveChanges: 'Enregistrer',
    noBranch: 'Sélectionnez une succursale pour voir les menus',
    empty: 'Aucun menu trouvé',
    emptyDesc: 'Il n’y a pas encore de menus pour cette succursale.',
    validation: {
      nameRequired: 'Le nom du menu est obligatoire',
      nameMax: 'Le nom du menu ne peut pas dépasser 100 caractères',
      descriptionMax: 'La description ne peut pas dépasser 500 caractères',
    },
    deleteModal: {
      title: 'Supprimer le menu ?',
      description:
        'Voulez-vous vraiment supprimer ce menu ? Cette action est irréversible.',
    },
    toast: {
      createSuccess: 'Menu créé avec succès.',
      createError: 'Échec de la création du menu. Veuillez réessayer.',
      updateSuccess: 'Menu mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du menu. Veuillez réessayer.',
      deleteSuccess: 'Menu supprimé avec succès.',
      deleteError: 'Échec de la suppression du menu. Veuillez réessayer.',
      duplicateSuccess: 'Menu dupliqué avec succès.',
      duplicateError: 'Échec de la duplication du menu. Veuillez réessayer.',
      logoUploadSuccess: 'Logo téléversé avec succès.',
      logoUploadError: 'Échec du téléversement du logo.',
      logoRemoveSuccess: 'Logo supprimé avec succès.',
      logoRemoveError: 'Échec de la suppression du logo.',
      backgroundUploadSuccess: 'Arrière-plan téléversé avec succès.',
      backgroundUploadError: 'Échec du téléversement de l’arrière-plan.',
      backgroundRemoveSuccess: 'Arrière-plan supprimé avec succès.',
      backgroundRemoveError: 'Échec de la suppression de l’arrière-plan.',
      templateUpdateSuccess: 'Modèle mis à jour avec succès.',
      templateUpdateError: 'Échec de la mise à jour du modèle.',
    },
    edit: {
      pageTitle: 'Modifier les détails du menu',
      subtitle: 'Configurez le contenu, le style et les sections du menu.',
      tabListLabel: 'Onglets d’édition du menu',
      withPhoto: 'avec photo',
      withoutPhoto: 'sans photo',
      uploading: 'Téléversement...',
      removing: 'Suppression...',
      tabs: {
        titleDescription: 'Titre et description',
        menuStyle: 'Style du menu',
        sectionsRecipes: 'Sections et recettes',
      },
      informationTitle: 'Informations du menu',
      informationDesc: 'Configurez le nom du menu et les traductions.',
      languages: {
        italian: 'Italien',
        italianMain: 'Langue principale',
        english: 'Anglais',
        englishTranslation: 'Traduction en anglais',
        spanish: 'Espagnol',
        spanishTranslation: 'Traduction en espagnol',
        french: 'Français',
        frenchTranslation: 'Traduction en français',
      },
      descriptions: {
        italian: 'Description du menu',
        english: 'Description en anglais',
        spanish: 'Description en espagnol',
        french: 'Description en français',
      },
      style: {
        appearanceTitle: 'Apparence du menu',
        appearanceDesc:
          'Téléversez le logo et l’image d’arrière-plan de votre menu.',
        logo: 'Sélectionner le logo du menu',
        logoHint: 'Nous recommandons un logo carré (PNG), max. 5 Mo.',
        background: 'Sélectionner l’image d’arrière-plan du menu',
        backgroundHint: 'Nous recommandons une image horizontale, max. 5 Mo.',
        removeImage: 'Supprimer l’image',
        templatesTitle: 'Sélectionner le style à appliquer au menu',
        templatesDesc:
          'Choisissez un modèle pour la présentation de votre menu.',
        previewStyleMenu: 'Aperçu du style du menu',
        select: 'Sélectionner',
        selected: 'Sélectionné',
        active: 'Actif',
      },
    },
    sections: {
      title: 'Sections et recettes',
      description: 'Organisez votre menu en sections et ajoutez des recettes.',
      addSection: 'Créer une nouvelle section',
      searchPlaceholder:
        'Rechercher des sections ou recettes par nom, description ou code...',
      noSearchResults: 'Aucune section ou recette correspondante',
      name: 'Nom de la section',
      namePlaceholder: 'Saisir le nom de la section',
      descriptionPlaceholder: 'Saisir la description de la section',
      createTitle: 'Créer une section sur le menu {{menu}}',
      createAction: 'Créer',
      editTitle: 'Modifier l’objet du menu',
      editDesc: 'Gérer le contenu, les traductions et les ingrédients',
      dishDrink: 'Plat/Boisson',
      missingTranslations: 'Traductions manquantes',
      missingTranslationsTooltip:
        "Le nom ou la description de cette section n'a pas été traduit dans toutes les langues du menu ; la version italienne sera affichée en cas de changement de langue",
      addRecipe: 'Ajouter une recette/un produit à {{section}}',
      sortAlphabetically: 'Trier les recettes de A à Z',
      moveLeft: 'Déplacer la section vers la gauche',
      moveRight: 'Déplacer la section vers la droite',
      settings: 'Paramètres de la section',
      delete: 'Supprimer la section',
      menuPrice: 'Prix du menu',
      sectionVisibility: 'Section visible',
      sectionVisibilityHint:
        'Afficher cette section dans les menus du restaurant, livraison et à emporter.',
      sectionHidden: 'Section masquée',
      hideSectionPrices: 'Masquer les prix de la section',
      showSectionPrices: 'Afficher les prix de la section',
      visibleSection: 'Section visible',
      visibility: 'Afficher / masquer la section',
      fixedMenuPrice: 'MENU FIXE ({{price}}€)',
      empty: 'Aucune section pour le moment',
      emptyDesc: 'Utilisez le bouton plus pour créer votre première section.',
      emptyRecipes: 'Aucune recette n’a encore été ajoutée à cette section.',
      recipeCount: '{{count}} recettes dans cette section',
      deleteModal: {
        title: 'Supprimer la section ?',
        description:
          'Voulez-vous vraiment supprimer cette section ? Cette action est irréversible.',
      },
      validation: {
        nameRequired: 'Le nom de la section est obligatoire',
        nameMax: 'Le nom de la section ne peut pas dépasser 100 caractères',
        descriptionMax: 'La description ne peut pas dépasser 500 caractères',
      },
      toast: {
        createSuccess: 'Section créée avec succès.',
        createError: 'Échec de la création de la section. Veuillez réessayer.',
        updateSuccess: 'Section mise à jour avec succès.',
        updateError:
          'Échec de la mise à jour de la section. Veuillez réessayer.',
        deleteSuccess: 'Section supprimée avec succès.',
        deleteError:
          'Échec de la suppression de la section. Veuillez réessayer.',
        sortSuccess: 'Recettes triées par ordre alphabétique.',
        sortError: 'Échec du tri des recettes. Veuillez réessayer.',
      },
    },
    recipes: {
      editTitle: 'Modifier l’objet du menu',
      editDesc: 'Gérer le contenu, les traductions et les ingrédients',
      imageTitle: 'Image',
      productPhoto: 'Photo du produit',
      winePhoto: 'Photo du vin',
      wineChampagne: 'Vin/Champagne',
      imageHintCreate:
        'Sélectionnez une image à téléverser. Vous pouvez ajouter le nom de la recette avant d’enregistrer.',
      generalTitle: 'Informations générales',
      generalDesc: 'Nom, description et configuration',
      priceSection: 'Prix',
      priceHint: 'Saisir le prix final du produit',
      price: 'Prix €',
      bottlePrice: 'Prix de la bouteille',
      priceBottleLabel: 'Prix bouteille (€)',
      chalice: 'Calice',
      pricePerGlass: 'Prix au verre',
      alcoholPercentage: 'Alcool (%) - Ex. : 14',
      wineType: 'Type de vin',
      wineTypePlaceholder:
        'Type de vin (ex. : Rouge, Blanc, Rosé, Champagne...)',
      blend: 'Assemblage',
      blendPlaceholder:
        'Assemblage (ex. : 50 % Negroamaro, 20 % Nero di Troia, 30 % Malvasia Nera)',
      yearOfProduction: 'Année de production',
      cellar: 'Cave',
      countryRegion: 'Pays/Région',
      provenance: 'Provenance',
      pricePerWeight:
        'Le prix est-il indiqué par unité de poids (ex. : kg, litres) plutôt que par portion ?',
      unitOfMeasurement: 'Unité de mesure',
      unitPlaceholder: 'Unité de mesure (ex. : Kg, L, Pcs)',
      assignAllergens: 'Attribuer des allergènes',
      productConfiguration: 'Configuration du produit',
      allergensTitle: 'Allergènes',
      bestSeller: 'Meilleure vente',
      bestSellerDesc:
        'Mettez ce produit en avant comme l’un des plus commandés et rendez-le plus visible sur le menu.',
      available: 'Disponible',
      availableDesc:
        'Affichez cette recette aux clients sur votre menu numérique et rendez-la commandable.',
      notAvailable: 'Non disponible',
      active: 'Actif',
      visible: 'Visible',
      delete: 'Supprimer la recette/le produit',
      dragHandle: 'Glisser pour réordonner la recette',
      deleteModal: {
        title: 'Supprimer la recette ?',
        description:
          'Voulez-vous vraiment supprimer cette recette ? Cette action est irréversible.',
      },
      validation: {
        nameRequired: 'Le nom de la recette est obligatoire',
      },
      toast: {
        createSuccess: 'Recette créée avec succès.',
        createError: 'Échec de la création de la recette. Veuillez réessayer.',
        updateSuccess: 'Recette mise à jour avec succès.',
        updateError:
          'Échec de la mise à jour de la recette. Veuillez réessayer.',
        deleteSuccess: 'Recette supprimée avec succès.',
        deleteError:
          'Échec de la suppression de la recette. Veuillez réessayer.',
        moveSuccess: 'Ordre de la recette mis à jour.',
        moveError:
          'Échec de la mise à jour de l’ordre de la recette. Veuillez réessayer.',
        saveBeforeImage:
          'Enregistrez la recette avant de téléverser une image.',
        imageUploadSuccess: 'Image de la recette téléversée avec succès.',
        imageUploadError: 'Échec du téléversement de l’image de la recette.',
        imageRemoveSuccess: 'Image de la recette supprimée avec succès.',
        imageRemoveError: 'Échec de la suppression de l’image de la recette.',
      },
    },
    allergens: {
      cereals: 'Céréales avec gluten',
      gluten: 'Gluten',
      crustaceans: 'Crustacés',
      molluscs: 'Mollusques',
      fish: 'Poisson',
      eggs: 'Œufs',
      peanuts: 'Arachides',
      soybean: 'Soja',
      mushrooms: 'Champignons',
      nuts: 'Fruits à coque',
      celery: 'Céleri',
      mustard: 'Moutarde',
      sesame: 'Sésame',
      sulfur: 'Anhydride sulfureux',
      lupins: 'Lupin',
      lactose: 'Lactose',
      corn: 'Maïs',
      pork: 'Porc',
      beef: 'Bœuf',
      poultry: 'Volaille',
      nickel: 'Nickel',
      legumes: 'Légumineuses',
      citrus: 'Agrumes',
      cacao: 'Cacao',
      honey: 'Miel',
    },
    translate: {
      action: 'Traduire',
      progressTitle: 'Traduction en cours...',
      progressDesc: 'Veuillez patienter jusqu’à la fin des traductions.',
      translationsTitle: 'Traductions',
      success: 'Traductions appliquées avec succès.',
      error: 'Échec de la traduction. Veuillez réessayer.',
    },
    previewPage: {
      pageTitle: 'Aperçu du menu',
      loading: 'Chargement du menu',
      menuLabel: 'Menu',
      sectionLabel: 'Section',
      priceLabel: 'Prix',
      noPhoto: 'Aucune photo disponible',
      bestSeller: 'Best',
      allergensTitle: 'Allergènes',
      poweredBy: 'Propulsé par PRO20',
      scrollCue: 'Aller au menu',
      storyContinue: 'Menu',
      close: 'Fermer',
      wine: {
        grape: 'Cépage',
        cellar: 'Domaine',
        year: 'Année',
        region: 'Région',
        alcohol: 'Alcool',
        glass: 'Verre',
        bottle: 'Bouteille',
      },
    },
  },
  landingPages: {
    title: 'Pages de Destination',
    subtitle: 'Liste des pages de destination pour {{branch}}',
    subtitleDefault: 'Afficher et gérer la liste des pages de destination',
    searchPlaceholder: 'Rechercher des pages de destination par nom',
    searchPlaceholder2: 'Saisir le nom de la page de destination',
    landingPageName: 'Nom de la page de destination',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    noBranch: 'Sélectionnez une succursale pour voir les pages de destination',
    empty: 'Aucune page de destination trouvée',
    emptyDesc:
      'Il n’y a pas encore de pages de destination pour cette succursale.',
    addLandingPage: 'Ajouter une page de destination',
    newLanding: 'Nouvelle page de destination',
    newLandingDesc:
      'Créez une nouvelle page de destination pour votre succursale.',
    createLanding: 'Créer la page de destination',
    statsCount: '{{count}} pages de destination',
    present: 'présentes',
    url: 'URL',
    qrCode: 'Code QR',
    preview: 'Aperçu',
    showPreview: 'Afficher l’aperçu',
    copyUrl: 'Copier l’URL',
    editLanding: 'Modifier la page de destination',
    deleteLanding: 'Supprimer la page de destination',
    duplicate: 'Dupliquer',
    validation: {
      landingPageNameMin: 'Le nom doit contenir au moins 2 caractères',
      landingPageNameMax: 'Le nom ne peut pas dépasser 50 caractères',
    },
    deleteModal: {
      title: 'Supprimer la page de destination ?',
      description:
        'Êtes-vous sûr de vouloir supprimer cette page de destination ? Cette action est irréversible.',
    },
    toast: {
      createSuccess: 'Page de destination créée avec succès.',
      createError:
        'Impossible de créer la page de destination. Veuillez réessayer.',
      deleteSuccess: 'Page de destination supprimée avec succès.',
      deleteError:
        'Impossible de supprimer la page de destination. Veuillez réessayer.',
      duplicateSuccess: 'Page de destination dupliquée avec succès.',
      duplicateError:
        'Impossible de dupliquer la page de destination. Veuillez réessayer.',
      updateSuccess: 'Page de destination mise à jour avec succès.',
      updateError:
        'Impossible de mettre à jour la page de destination. Veuillez réessayer.',
      logoUploadSuccess: 'Logo téléchargé avec succès.',
      logoUploadError: 'Échec du téléchargement du logo.',
      logoRemoveSuccess: 'Logo supprimé avec succès.',
      logoRemoveError: 'Échec de la suppression du logo.',
      backgroundUploadSuccess: 'Arrière-plan téléchargé avec succès.',
      backgroundUploadError: 'Échec du téléchargement de l’arrière-plan.',
      backgroundRemoveSuccess: 'Arrière-plan supprimé avec succès.',
      backgroundRemoveError: 'Échec de la suppression de l’arrière-plan.',
      buttonSaveSuccess: 'Bouton enregistré avec succès.',
      buttonSaveError: 'Échec de l’enregistrement du bouton.',
      buttonDeleteSuccess: 'Bouton supprimé avec succès.',
      buttonDeleteError: 'Échec de la suppression du bouton.',
    },
    edit: {
      pageTitle: 'Modifier la page de destination',
      subtitle: 'Configurez les médias et les boutons d’action.',
      formConfiguration: 'Configuration de la page de destination',
      formConfigurationDesc: 'Configurez les médias et les boutons d’action.',
      linkForm: 'Lien de la page de destination',
      close: 'Fermer',
      mediaTitle: 'Médias',
      mediaDescription:
        'Téléchargez un logo et une image d’arrière-plan pour votre page de destination.',
      detailsTitle: 'Détails',
      logo: 'Logo',
      background: 'Image d’arrière-plan',
      imageUpload: 'Cliquez ou faites glisser pour télécharger une image',
      removeImage: 'Supprimer l’image',
      uploading: 'Téléchargement...',
      removing: 'Suppression...',
      buttons: {
        title: 'Boutons de la page',
        description:
          'Ajoutez des boutons d’action sur votre page de destination. Utilisez l’icône ✓ pour enregistrer chaque ligne.',
        text: 'Texte du bouton',
        link: 'Lien',
        color: 'Arrière-plan',
        textColor: 'Couleur du texte',
        add: 'Ajouter un bouton',
        save: 'Enregistrer le bouton',
        delete: 'Supprimer le bouton',
        empty: 'Aucun bouton. Cliquez sur + pour en ajouter un.',
        textPlaceholder: 'ex. Réserver maintenant',
        linkPlaceholder: 'https://',
        validationRequired: 'Le texte et le lien du bouton sont obligatoires.',
      },
    },
    public: {
      documentTitle: 'Page d’atterrissage',
      loading: 'Chargement de la page d’atterrissage...',
      unavailableTitle: 'Page indisponible',
      unavailableDesc:
        'Cette page d’atterrissage est introuvable ou n’est plus disponible.',
      buttonFallback: 'Ouvrir',
    },
  },
  tags: {
    title: 'Tags',
    titleWithBranch: 'Tags {{branch}}',
    pageDescription: 'Aucun tag présent',
    subtitle: 'Liste des tags pour {{branch}}',
    subtitleDefault: 'Afficher et gérer la liste des tags',
    searchPlaceholder: 'Rechercher des tags par nom',
    searchPlaceholder2: 'Saisir le nom du tag',
    tagName: 'Nom du tag',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    noBranch: 'Sélectionnez une agence pour voir les tags',
    empty: 'Aucun tag présent',
    emptyDesc:
      'Créez le premier tag pour mieux organiser clients, réservations et segmentations marketing.',
    addTag: 'Ajouter un tag',
    createTitle: 'Ajouter un tag',
    createDesc: 'Créez un nouveau tag pour votre agence.',
    editTitle: 'Modifier le tag',
    editDesc: 'Mettez à jour le nom du tag.',
    editTag: 'Modifier le tag',
    duplicate: 'Dupliquer',
    typeLabel: 'TAG',
    statsCount: '{{count}} tags présents',
    updatedAgo: 'Mis a jour il y a 2 minutes',
    creating: 'Création...',
    saving: 'Enregistrement...',
    saveChanges: 'Enregistrer les modifications',
    toast: {
      createSuccess: 'Tag créé avec succès.',
      createError: 'Échec de la création du tag. Veuillez réessayer.',
      updateSuccess: 'Tag mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du tag. Veuillez réessayer.',
      deleteSuccess: 'Tag supprimé avec succès.',
      deleteError: 'Échec de la suppression du tag. Veuillez réessayer.',
      duplicateSuccess: 'Tag dupliqué avec succès.',
      duplicateError: 'Échec de la duplication du tag. Veuillez réessayer.',
    },
    validation: {
      tagNameMin: 'Le nom du tag doit comporter au moins 2 caractères',
      tagNameMax: 'Le nom du tag ne peut pas dépasser 50 caractères',
    },
    deleteTag: 'Supprimer l’étiquette',
    deleteModel: {
      deleteTitle: 'Supprimer le tag ?',
      deleteDescription:
        'Êtes-vous sûr de vouloir supprimer ce tag ? Cette action est irréversible.',
    },
  },
  publicRelations: {
    title: 'Relations Publiques',
    titleWithBranch: 'RP {{branch}}',
    pageDescription: 'Aucune RP présente',
    subtitle: 'Liste RP pour {{branch}}',
    subtitleDefault: 'Afficher et gérer la liste RP',
    searchPlaceholder: 'Rechercher une RP par nom',
    searchPlaceholder2: 'Saisir le nom de la RP',
    prName: 'Nom de la RP',
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    noBranch: 'Sélectionnez une agence pour voir les RP',
    empty: 'Aucune RP présente',
    emptyDesc:
      'Créez la première RP pour organiser parrainages, collaborations et gestion clients.',
    addPr: 'Ajouter une RP',
    createTitle: 'Ajouter une RP',
    createDesc:
      'Créez un nouveau contact relations publiques pour votre agence.',
    editTitle: 'Modifier la RP',
    editDesc: 'Mettez à jour le nom du contact relations publiques.',
    editPr: 'Modifier la RP',
    duplicate: 'Dupliquer',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} RP présentes',
    updatedAgo: 'Mis a jour il y a 2 minutes',
    creating: 'Création...',
    saving: 'Enregistrement...',
    saveChanges: 'Enregistrer les modifications',
    toast: {
      createSuccess: 'RP créée avec succès.',
      createError: 'Échec de la création de la RP. Veuillez réessayer.',
      updateSuccess: 'RP mise à jour avec succès.',
      updateError: 'Échec de la mise à jour de la RP. Veuillez réessayer.',
      deleteSuccess: 'RP supprimée avec succès.',
      deleteError: 'Échec de la suppression de la RP. Veuillez réessayer.',
      duplicateSuccess: 'RP dupliquée avec succès.',
      duplicateError: 'Échec de la duplication de la RP. Veuillez réessayer.',
    },
    validation: {
      prNameMin: 'Le nom de la RP doit comporter au moins 2 caractères',
      prNameMax: 'Le nom de la RP ne peut pas dépasser 50 caractères',
    },
    deletePR: 'Supprimer PR',
    deleteModel: {
      deleteTitle: 'Supprimer la RP ?',
      deleteDescription:
        'Êtes-vous sûr de vouloir supprimer cette RP ? Cette action est irréversible.',
    },
  },
  staff: {
    collaborators: {
      title: 'Collaborateurs',
      subtitle: 'Collaborateurs pour {{branch}}',
      subtitleDefault: 'Afficher et gérer la liste des collaborateurs',
      searchPlaceholder:
        'Rechercher des collaborateurs par prénom, nom, email, téléphone ou rôle',
      firstName: 'Prénom',
      lastName: 'Nom',
      firstNamePlaceholder: 'Saisir le prénom',
      lastNamePlaceholder: 'Saisir le nom',
      email: 'Email',
      phone: 'Numéro de Téléphone',
      role: 'Rôle',
      itemsPerPage: 'Éléments par page',
      viewDetails: 'Voir les Détails',
      sharedCredentials: 'Identifiants Partagés',
      addCollaborator: 'Ajouter un Collaborateur',
      editCollaborator: 'Modifier le Collaborateur',
      deleteCollaborator: 'Supprimer le Collaborateur',
      changePassword: {
        action: 'Changer le mot de passe',
        title: 'Changer le mot de passe',
        subtitle: 'Définir un nouveau mot de passe pour {{name}}',
        newPassword: 'Nouveau mot de passe',
        newPasswordPlaceholder: 'Saisir un mot de passe à 6 chiffres',
        confirmPassword: 'Confirmer le mot de passe',
        confirmPasswordPlaceholder: 'Confirmer le mot de passe à 6 chiffres',
        submit: 'Enregistrer le mot de passe',
        validation: {
          passwordDigits: 'Le mot de passe doit contenir exactement 6 chiffres',
          passwordMismatch: 'Les mots de passe ne correspondent pas',
        },
      },
      range: '{{start}} - {{end}} sur {{total}}',
      noBranch: 'Sélectionnez une succursale pour voir les collaborateurs',
      empty: 'Aucun collaborateur trouvé',
      emptyDesc: 'Il n’y a pas encore de collaborateurs pour cette succursale.',
      emailPlaceholder: 'Saisir l’email',
      phonePlaceholder: 'Saisir le numéro de téléphone',
      username: 'Nom d’utilisateur',
      usernamePlaceholder: 'Saisir le nom d’utilisateur',
      password: 'Mot de passe',
      passwordPlaceholder: 'Saisir le mot de passe',
      gender: 'Genre',
      selectGender: 'Sélectionner le genre',
      male: 'Homme',
      female: 'Femme',
      selectRole: 'Sélectionner le rôle',
      remunerationType: 'Type de rémunération',
      selectRemunerationType: 'Sélectionner le type de rémunération',
      hourly: 'Horaire',
      daily: 'Journalier',
      monthly: 'Mensuel',
      sendCredentials: 'Envoyer les identifiants',
      roles: {
        receptionist: 'Accueil',
        assistantCook: 'Aide-cuisinier',
        administrator: 'Administrateur',
        bartender: 'Barman',
        chef: 'Cuisinier',
        dishwasher: 'Plongeur',
        pizzaChef: 'Pizzaiolo',
        director: 'Directeur',
        waiter: 'Serveur',
        publicRelations: 'Relations publiques',
        deputyDirector: 'Directeur adjoint',
      },
      validation: {
        firstNameMin: 'Le prénom doit comporter au moins 2 caractères',
        lastNameMin: 'Le nom doit comporter au moins 2 caractères',
        firstNameMax: 'Le prénom ne peut pas dépasser 40 caractères',
        lastNameMax: 'Le nom ne peut pas dépasser 40 caractères',
        emailInvalid: 'Veuillez saisir une adresse email valide',
        phoneInvalid:
          'Le numéro de téléphone doit contenir entre 6 et 15 chiffres',
        countryCodeInvalid: 'Veuillez sélectionner un indicatif pays valide',
        usernameMin:
          'Le nom d’utilisateur doit comporter au moins 4 caractères',
        usernameMax: 'Le nom d’utilisateur ne peut pas dépasser 30 caractères',
        passwordMin: 'Le mot de passe doit comporter au moins 8 caractères',
        passwordDigits: 'Le mot de passe doit contenir exactement 6 chiffres',
        genderRequired: 'Veuillez sélectionner un genre',
        roleRequired: 'Veuillez sélectionner un rôle',
        remunerationRequired: 'Veuillez sélectionner un type de rémunération',
      },
      deleteModel: {
        deleteTitle: 'Supprimer le collaborateur ?',
        deleteDescription:
          'Êtes-vous sûr de vouloir supprimer ce collaborateur ? Cette action est irréversible.',
      },
      collaboratorDetails: {
        title: 'Détails du Collaborateur',
        subtitle:
          'Consultez les informations du collaborateur et les détails d’emploi.',
        personalDetails: 'Informations Personnelles',
        employmentDetails: 'Informations Professionnelles',
        jobDescription: 'Poste',
        dateOfBirth: 'Date de Naissance',
        branchCode: 'Code Succursale',
        contractType: 'Type de Contrat',
        startDateInduction: 'Date Début Induction',
        endDateInduction: 'Date Fin Induction',
        mobileDeviceAccessDetail: 'Détail d’Accès Appareil Mobile',
        uniqueId: 'ID Unique',
        platform: 'Plateforme',
        model: 'Modèle',
        machine: 'Machine',
        release: 'Release',
        fcmToken: 'Jeton FCM',
        registrationDate: 'Date d’Enregistrement',
        noMobileDevices: 'Aucun appareil mobile enregistré.',
        close: 'Fermer',
        contractTypes: {
          determined: 'Déterminé',
          indeterminate: 'Indéterminé',
        },
        visible: 'Visible',
        fired: 'Licencié',
        retribution: 'Rémunération',
        yes: 'Oui',
        no: 'Non',
      },
      toast: {
        createSuccess: 'Collaborateur créé avec succès.',
        createError:
          'Échec de la création du collaborateur. Veuillez réessayer.',
        updateSuccess: 'Collaborateur mis à jour avec succès.',
        updateError:
          'Échec de la mise à jour du collaborateur. Veuillez réessayer.',
        deleteSuccess: 'Collaborateur supprimé avec succès.',
        deleteError:
          'Échec de la suppression du collaborateur. Veuillez réessayer.',
        sendCredentialsSoon:
          'L’envoi des identifiants sera bientôt disponible.',
        sendCredentialsSuccess: 'Identifiants envoyés avec succès.',
        sendCredentialsError:
          'Échec de l’envoi des identifiants. Veuillez réessayer.',
        sendCredentialsMissingPhone:
          'Ce collaborateur n’a pas de numéro de téléphone valide.',
        sendCredentialsMissingCredentials:
          'Le nom d’utilisateur ou le mot de passe est manquant pour ce collaborateur.',
        changePasswordSuccess: 'Mot de passe mis à jour avec succès.',
        changePasswordError:
          'Échec de la mise à jour du mot de passe. Veuillez réessayer.',
      },
      credentialsMessage:
        'Vos identifiants Proventi :\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: 'Rapports des Collaborateurs',
      titleWithMonth: 'Rapport {{branch}} de {{monthYear}}',
      subtitle: 'Rapports des collaborateurs pour {{branch}}',
      subtitleDefault:
        'Afficher et gérer la liste des rapports des collaborateurs',
      noReportsPresent: 'Aucun report présent',
      searchPlaceholder: 'Rechercher par nom',
      name: 'Nom',
      lunch: 'Déjeuner',
      cena: 'Dîner',
      illness: 'Maladie',
      holiday: 'Congé',
      free: 'Libre',
      hoursWorked: 'Heures Travaillées',
      itemsPerPage: 'Éléments par page',
      range: '{{start}} - {{end}} sur {{total}}',
      noBranch:
        'Sélectionnez une succursale pour voir les rapports des collaborateurs',
      empty: 'Aucun rapport de collaborateur trouvé',
      emptyDesc:
        'Il n’y a pas encore de rapports de collaborateurs pour cette succursale.',
      previousMonth: 'Mois précédent',
      nextMonth: 'Mois suivant',
      downloadPdf: 'Télécharger le rapport de {{monthYear}}',
      downloadPdfSuccess: 'PDF du rapport collaborateurs téléchargé.',
      downloadPdfError:
        'Échec du téléchargement du PDF du rapport collaborateurs.',
      pdfTitle: 'Rapports des Collaborateurs',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} collaborateurs · Généré le {{date}}',
    },
  },
  extraServices: {
    title: 'Services et Produits Supplémentaires',
    subtitle:
      'Gérez les services, produits et commodités disponibles pour {{branchName}}',
    subtitleDefault: 'Afficher et gérer la liste des services supplémentaires',
    statsCount: '{{count}} services supplémentaires',
    searchPlaceholder: 'Rechercher un service ou un produit...',
    noBranch:
      'Sélectionnez une succursale pour voir les services supplémentaires',
    empty: 'Aucun service supplémentaire',
    emptyDesc:
      "Vous n'avez pas encore configuré de services ou produits supplémentaires. Ajoutez des commodités pour améliorer l'expérience de vos clients.",
    itemsPerPage: 'Éléments par page',
    range: '{{start}} - {{end}} sur {{total}}',
    columns: {
      service: 'Service',
      price: 'Prix',
      guests: 'Invités',
      state: 'État',
    },
    active: 'Actif',
    inactive: 'Inactif',
    createTitle: 'Créer un nouveau service',
    createDesc: 'Ajoutez un nouveau service ou produit pour votre succursale.',
    editTitle: 'Modifier le service',
    editDesc: 'Mettre à jour les informations du service',
    createService: 'Créer le service',
    addService: 'Ajouter un service',
    creating: 'Création...',
    saveChanges: 'Enregistrer les modifications',
    close: 'Fermer',
    name: 'Nom',
    namePlaceholder: 'Saisir le nom du service',
    description: 'Description',
    descriptionPlaceholder: 'Saisir la description',
    price: 'Prix (€)',
    pricePlaceholder: '0',
    guests: 'Nombre d’invités',
    guestsPlaceholder: '1',
    activeService: 'Service actif',
    serviceImage: 'Image du service',
    uploadImage: 'Télécharger une image',
    removeImage: 'Supprimer l’image',
    uploading: 'Téléchargement...',
    removing: 'Suppression...',
    editService: 'Modifier le service',
    duplicate: 'Dupliquer',
    deleteService: 'Supprimer le service',
    deleteModal: {
      title: 'Supprimer le service ?',
      description:
        'Êtes-vous sûr de vouloir supprimer ce service ? Cette action est irréversible.',
    },
    validation: {
      nameRequired: 'Le nom est obligatoire',
      nameMax: 'Le nom ne peut pas dépasser 120 caractères',
      descriptionMax: 'La description ne peut pas dépasser 300 caractères',
      numberInvalid: 'Saisissez uniquement un nombre valide',
      priceMin: 'Le prix ne peut pas être négatif',
      guestsMin: 'Le nombre d’invités ne peut pas être négatif',
      guestsInteger: 'Le nombre d’invités doit être un nombre entier',
    },
    toast: {
      createSuccess: 'Service créé avec succès.',
      createError: 'Échec de la création du service. Veuillez réessayer.',
      updateSuccess: 'Service mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du service. Veuillez réessayer.',
      deleteSuccess: 'Service supprimé avec succès.',
      deleteError: 'Échec de la suppression du service. Veuillez réessayer.',
      duplicateSuccess: 'Service dupliqué avec succès.',
      duplicateError: 'Échec de la duplication du service. Veuillez réessayer.',
      uploadSuccess: 'Image téléchargée avec succès.',
      uploadError: 'Échec du téléchargement de l’image.',
      removeImageSuccess: 'Image supprimée avec succès.',
      removeImageError: 'Échec de la suppression de l’image.',
    },
  },
  formMenu: {
    title: 'Form Menu',
    subtitleDefault: 'Consulter et gérer les Form Menus de votre succursale',
    statsCount: '{{count}} Form Menus',
    searchPlaceholder: 'Rechercher un Form Menu...',
    noBranch: 'Sélectionnez une succursale pour voir les Form Menus',
    empty: 'Aucun Form Menu',
    emptyDesc:
      'Vous n’avez pas encore configuré de Form Menu. Ajoutez-en un pour commencer.',
    columns: {
      item: 'Nom du menu',
      status: 'Statut',
      price: 'Prix',
      totalBooking: 'Réservations totales',
      forms: 'Formulaires',
      state: 'État',
    },
    formsCount: '{{count}} formulaires',
    active: 'Actif',
    inactive: 'Inactif',
    createTitle: 'Créer un Form Menu',
    createDesc: 'Ajoutez un nouveau Form Menu pour votre succursale.',
    editTitle: 'Modifier le Form Menu',
    editDesc: 'Mettre à jour les informations du Form Menu',
    createItem: 'Créer',
    addItem: 'Ajouter un Form Menu',
    saveChanges: 'Enregistrer les modifications',
    close: 'Fermer',
    name: 'Nom du menu',
    namePlaceholder: 'Saisir le nom du Form Menu',
    description: 'Description',
    descriptionPlaceholder: 'Saisir la description',
    price: 'Prix (€)',
    pricePlaceholder: '0',
    forms: 'Formulaires',
    formsPlaceholder: 'Sélectionner les formulaires',
    activeItem: 'Form Menu actif',
    itemImage: 'Image du menu',
    uploadImage: 'Télécharger une image',
    removeImage: 'Supprimer l’image',
    uploading: 'Téléversement…',
    weekdaysLabel: 'Jours de la semaine',
    selectAllWeekdays: 'Tout sélectionner',
    clearWeekdays: 'Effacer',
    availabilityDates: {
      title: 'Dates de disponibilité du menu',
      dateRange: 'Plage de dates',
      placeholder: 'Sélectionner une plage',
      empty: 'Aucune date de disponibilité. Ajoutez une plage pour commencer.',
      addRange: 'Ajouter une plage',
      createTitle: 'Ajouter une plage de dates',
      editTitle: 'Modifier la plage de dates',
      create: 'Ajouter',
      update: 'Mettre à jour',
      editRange: 'Modifier la plage',
      deleteRange: 'Supprimer la plage',
      deleteTitle: 'Supprimer la plage de dates ?',
      deleteDescription:
        'Voulez-vous vraiment supprimer cette plage de disponibilité ?',
      dateRequired: 'Sélectionnez une plage de dates.',
      dateOrder: 'La date de fin doit être postérieure ou égale au début.',
      dateOverlap:
        'Cette plage chevauche des dates de disponibilité existantes. Choisissez des dates libres.',
    },
    weekdays: {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche',
    },
    editItem: 'Modifier le Form Menu',
    duplicate: 'Dupliquer',
    editNotFound: 'Ce Form Menu est introuvable ou n’est plus disponible.',
    deleteItem: 'Supprimer le Form Menu',
    deleteModal: {
      title: 'Supprimer le Form Menu ?',
      description:
        'Voulez-vous vraiment supprimer ce Form Menu ? Cette action est irréversible.',
    },
    validation: {
      nameRequired: 'Le nom est requis',
      nameMax: 'Le nom ne peut pas dépasser 120 caractères',
      descriptionMax: 'La description ne peut pas dépasser 300 caractères',
      numberInvalid: 'Saisissez uniquement un nombre valide',
      priceMin: 'Le prix ne peut pas être négatif',
    },
    toast: {
      createSuccess: 'Form Menu créé avec succès.',
      createError: 'Échec de la création du Form Menu. Veuillez réessayer.',
      updateSuccess: 'Form Menu mis à jour avec succès.',
      updateError: 'Échec de la mise à jour du Form Menu. Veuillez réessayer.',
      deleteSuccess: 'Form Menu supprimé avec succès.',
      deleteError: 'Échec de la suppression du Form Menu. Veuillez réessayer.',
      duplicateSuccess: 'Form Menu dupliqué avec succès.',
      duplicateError:
        'Échec de la duplication du Form Menu. Veuillez réessayer.',
      showEnabled: 'Form Menu maintenant visible.',
      showDisabled: 'Form Menu maintenant masqué.',
      statusError:
        'Échec de la mise à jour du statut du Form Menu. Veuillez réessayer.',
      uploadSuccess: 'Image téléversée avec succès.',
      uploadError: 'Échec du téléversement de l’image.',
      removeImageSuccess: 'Image supprimée avec succès.',
      removeImageError: 'Échec de la suppression de l’image.',
      dateRangeCreateSuccess: 'Plage de disponibilité ajoutée.',
      dateRangeCreateError: 'Échec de l’ajout de la plage de disponibilité.',
      dateRangeUpdateSuccess: 'Plage de disponibilité mise à jour.',
      dateRangeUpdateError:
        'Échec de la mise à jour de la plage de disponibilité.',
      dateRangeDeleteSuccess: 'Plage de disponibilité supprimée.',
      dateRangeDeleteError:
        'Échec de la suppression de la plage de disponibilité.',
      dateRangeLoadError: 'Échec du chargement de la plage de disponibilité.',
    },
  },
  events: {
    title: 'Événements',
    subtitleDefault: 'Consulter et gérer les événements de la succursale',
    statsCount: '{{count}} événements',
    searchPlaceholder: 'Rechercher des événements...',
    filterDatePlaceholder: 'Filtrer par date',
    reset: 'Réinitialiser',
    noBranch: 'Sélectionnez une succursale pour voir les événements',
    empty: 'Aucun événement',
    emptyDesc:
      'Vous n’avez pas encore créé d’événements. Ajoutez un événement pour commencer.',
    columns: {
      event: 'Événement',
      start: 'Début',
      end: 'Fin',
      capacity: 'Capacité',
      totalBooking: 'Total Booking',
      forms: 'Formulaires',
      status: 'Statut',
    },
    formsCount: '{{count}} formulaires',
    createTitle: 'Créer un événement',
    createDesc: 'Ajoutez un nouvel événement pour votre succursale.',
    editTitle: 'Modifier l’événement',
    editDesc: 'Mettre à jour les informations de l’événement',
    createEvent: 'Créer l’événement',
    createItem: 'Créer',
    addEvent: 'Ajouter un événement',
    saveChanges: 'Enregistrer',
    close: 'Fermer',
    name: 'Nom',
    namePlaceholder: 'Saisir le nom de l’événement',
    description: 'Description',
    descriptionPlaceholder: 'Saisir la description',
    startDate: 'Date de début',
    startTime: 'Heure de début',
    endDate: 'Date de fin',
    endTime: 'Heure de fin',
    capacity: 'Capacité max',
    capacityPlaceholder: '100',
    visible: 'Afficher la capacité max',
    forms: 'Formulaires',
    formsPlaceholder: 'Sélectionner des formulaires',
    mandatory: 'Événement obligatoire',
    weekdaysLabel: 'Jours de la semaine',
    selectAllWeekdays: 'Tout sélectionner',
    clearWeekdays: 'Effacer',
    availabilityDates: {
      title: 'Dates de disponibilité de l’événement',
      dateRange: 'Plage de dates',
      placeholder: 'Sélectionner une plage de dates',
      empty: 'Aucune date de disponibilité. Ajoutez une plage pour commencer.',
      addRange: 'Ajouter une plage',
      createTitle: 'Ajouter une plage de dates',
      editTitle: 'Modifier la plage de dates',
      create: 'Ajouter',
      update: 'Mettre à jour',
      editRange: 'Modifier la plage',
      deleteRange: 'Supprimer la plage',
      deleteTitle: 'Supprimer la plage de dates ?',
      deleteDescription:
        'Voulez-vous vraiment supprimer cette plage de disponibilité ?',
      dateRequired: 'Sélectionnez une plage de dates.',
      dateOrder:
        'La date de fin doit être égale ou postérieure à la date de début.',
      dateOverlap:
        'Cette plage chevauche des dates existantes. Choisissez des dates libres.',
    },
    weekdays: {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche',
    },
    eventImage: 'Image de l’événement',
    uploadImage: 'Téléverser une image',
    removeImage: 'Supprimer l’image',
    uploading: 'Téléversement...',
    removing: 'Suppression...',
    editEvent: 'Modifier l’événement',
    duplicate: 'Dupliquer',
    deleteEvent: 'Supprimer l’événement',
    deleteModal: {
      title: 'Supprimer l’événement ?',
      description:
        'Voulez-vous vraiment supprimer cet événement ? Cette action est irréversible.',
    },
    validation: {
      nameRequired: 'Le nom est obligatoire',
      nameMax: 'Le nom ne peut pas dépasser 120 caractères',
      descriptionMax: 'La description ne peut pas dépasser 500 caractères',
      capacityInvalid: 'Saisissez une capacité valide',
      capacityInteger: 'La capacité doit être un nombre entier',
      capacityMin: 'La capacité doit être d’au moins 1',
      capacityMax: 'La capacité ne peut pas dépasser 999',
      startDateRequired: 'La date de début est obligatoire',
      startTimeRequired: 'L’heure de début est obligatoire',
      endDateRequired: 'La date de fin est obligatoire',
      endTimeRequired: 'L’heure de fin est obligatoire',
      endBeforeStart: 'La fin doit être postérieure au début',
    },
    toast: {
      createSuccess: 'Événement créé avec succès.',
      createError: 'Échec de la création de l’événement. Réessayez.',
      updateSuccess: 'Événement mis à jour avec succès.',
      updateError: 'Échec de la mise à jour de l’événement. Réessayez.',
      deleteSuccess: 'Événement supprimé avec succès.',
      deleteError: 'Échec de la suppression de l’événement. Réessayez.',
      duplicateSuccess: 'Événement dupliqué avec succès.',
      duplicateError: 'Échec de la duplication de l’événement. Réessayez.',
      showEnabled: 'L’événement est maintenant visible.',
      showDisabled: 'L’événement est maintenant masqué.',
      statusError:
        'Échec de la mise à jour du statut de l’événement. Réessayez.',
      uploadSuccess: 'Image téléversée avec succès.',
      uploadError: 'Échec du téléversement de l’image.',
      saveBeforeImage:
        'Renseignez les champs obligatoires de l’événement avant de téléverser une image.',
      removeImageSuccess: 'Image supprimée avec succès.',
      removeImageError: 'Échec de la suppression de l’image.',
      dateRangeCreateSuccess: 'Plage de disponibilité ajoutée.',
      dateRangeCreateError: 'Échec de l’ajout de la plage de disponibilité.',
      dateRangeUpdateSuccess: 'Plage de disponibilité mise à jour.',
      dateRangeUpdateError:
        'Échec de la mise à jour de la plage de disponibilité.',
      dateRangeDeleteSuccess: 'Plage de disponibilité supprimée.',
      dateRangeDeleteError:
        'Échec de la suppression de la plage de disponibilité.',
      dateRangeLoadError: 'Échec du chargement de la plage de disponibilité.',
    },
  },
  reports: {
    title: 'Rapports',
    subtitle: 'Tableau de bord analytique et rapports',
    comingSoon: 'Rapports bientôt disponibles',
    comingSoonDesc:
      'Analyses avancées et rapports personnalisés seront disponibles dans une version future.',
  },
  errors: {
    somethingWrong: "Une erreur s'est produite",
    unexpected: "Une erreur inattendue s'est produite. Réessayez.",
    pageNotFound: 'Page introuvable',
    pageNotFoundDesc:
      "La page que vous recherchez n'existe pas ou a été déplacée.",
    accessDenied: 'Accès refusé',
    accessDeniedDesc:
      "Vous n'avez pas la permission d'accéder à cette ressource. Contactez votre administrateur.",
    backToDashboard: 'Retour au tableau de bord',
    backToHome: "Retour à l'accueil",
    publicHint:
      'Vérifiez le lien que vous avez reçu, ou contactez l’établissement si vous avez besoin d’aide.',
    applicationError: "Erreur d'application",
    reload: 'Recharger la page',
  },
  publicPromo: {
    documentTitle: 'Promotion',
    loading: 'Chargement de la promotion',
    unavailableTitle: 'Promotion indisponible',
    unavailableDesc:
      'Ce lien de promotion est manquant, invalide ou n’est plus actif.',
    expiredDesc: 'Cette promotion a expiré et n’est plus disponible.',
    comingSoonDesc:
      'Cette promotion n’est pas encore prête à collecter des réponses.',
    formUnavailableTitle: 'Formulaire non disponible',
    formUnavailableMessage:
      'Ce formulaire de promotion est actuellement suspendu ou indisponible.',
    badge: 'Promotion',
    requestTitle: 'Demandez votre coupon',
    requestSubtitle: 'Renseignez vos informations pour demander la promotion.',
    optional: 'Facultatif',
    birthDate: 'Date de naissance',
    cap: 'Code postal',
    capPlaceholder: 'Ex. 75001',
    address: 'Adresse',
    addressPlaceholder: 'Rue et numéro',
    nationality: 'Nationalité',
    nationalityPlaceholder: 'Votre nationalité',
    requiredNote: '* Champs obligatoires',
    submitting: 'Enregistrement…',
    personalInfo: 'Informations personnelles',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Téléphone mobile',
    firstName: 'Prénom',
    firstNamePlaceholder: 'Prénom',
    lastName: 'Nom',
    lastNamePlaceholder: 'Nom',
    email: 'E-mail',
    emailPlaceholder: 'E-mail',
    consentsTitle: 'J’accepte le traitement des données personnelles',
    privacyConsent: 'Confidentialité et traitement des données',
    marketingConsent: 'Consentement marketing et communication',
    profilingConsent: 'Consentement au profilage',
    detailsLink: 'Détails',
    selectAll: 'Tout sélectionner',
    submit: 'Demander le coupon',
    toast: {
      success: 'Coupon généré avec succès.',
      error: 'Échec de la génération du coupon. Réessayez.',
    },
    thankYou: {
      title: 'Merci !',
      message: 'Votre demande a été envoyée avec succès.',
      continue: 'Continuer',
    },
    lookupFound: 'Nous avons trouvé vos informations et les avons renseignées.',
    welcomeBack: 'Bon retour, {{firstName}}. Nous avons déjà vos informations.',
    welcomeBackNoName: 'Bon retour. Nous avons déjà vos informations.',
    alreadyHasCoupon: 'Vous avez déjà un coupon pour cette promotion.',
    alreadyUsed: 'Ce coupon a déjà été utilisé.',
    maxCoupons:
      'Il n’est plus possible de générer de coupon pour cette promotion. La limite est atteinte.',
    lookupError:
      'Impossible de vérifier ce numéro. Vous pouvez quand même remplir le formulaire.',
    lookupNotFound: 'Cette promotion est introuvable.',
    lookupInvalid: 'Vérifiez le numéro de mobile et réessayez.',
    resend: {
      title: 'Envoyer votre coupon sur WhatsApp ?',
      message:
        'Vous avez déjà demandé un coupon. Nous pouvons vous le renvoyer sur WhatsApp avec le QR code.',
      confirm: 'Envoyer sur WhatsApp',
      decline: 'Pas maintenant',
      close: 'Fermer',
      success: 'Nous avons envoyé le coupon sur votre WhatsApp.',
      error: 'Le coupon n’a pas pu être envoyé. Réessayez.',
    },
    validation: {
      prefixRequired: 'Sélectionnez un indicatif pays.',
      phoneMin: 'Saisissez un numéro de mobile valide.',
      phoneRange: 'Saisissez de 6 à 15 chiffres.',
      firstNameRequired: 'Le prénom est requis.',
      lastNameRequired: 'Le nom est requis.',
      emailRequired: 'L’e-mail est requis.',
      emailInvalid: 'Saisissez une adresse e-mail valide.',
      birthDateInvalid: 'Saisissez une date valide, non future.',
      privacyRequired: 'Le consentement à la confidentialité est requis.',
    },
  },
  publicFidelity: {
    documentTitle: 'Carte fidelity',
    loading: 'Chargement du programme fidelity',
    unavailableTitle: 'Programme indisponible',
    unavailableDesc: 'Vérifiez le lien reçu ou contactez le restaurant.',
    badge: 'Votre programme de fidélité',
    cardLabel: 'Carte fidelity',
    previewPoints: 'Chaque visite compte.',
    previewDiscount: 'Des avantages pour vous.',
    requestTitle: 'Demandez votre carte',
    requestSubtitle: 'Créez votre carte personnelle et gardez-la avec vous.',
    optional: 'Facultatif',
    requiredNote: '* Champs obligatoires',
    submitting: 'Enregistrement…',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Votre numéro',
    firstName: 'Prénom',
    firstNamePlaceholder: 'Votre prénom',
    lastName: 'Nom',
    lastNamePlaceholder: 'Votre nom',
    email: 'E-mail',
    emailPlaceholder: 'nom@exemple.fr',
    consentsTitle: 'Vos préférences',
    privacyConsent: 'Confidentialité et traitement des données',
    marketingConsent: 'Consentement marketing et communications',
    profilingConsent: 'Consentement au profilage',
    detailsLink: 'Informations',
    selectAll: 'Tout sélectionner',
    submit: 'Créer ma carte',
    successTitle: 'Votre carte est prête.',
    successSubtitle: 'Enregistrez le QR et montrez-le au restaurant.',
    qrAlt: 'QR code de votre carte fidelity',
    openQr: 'Ouvrir et enregistrer le QR',
    welcomeBack: 'Bon retour, {{firstName}}. Nous avons déjà vos informations.',
    welcomeBackNoName: 'Bon retour. Nous avons déjà vos informations.',
    lookupError:
      'Nous n’avons pas pu vérifier ce numéro. Vous pouvez quand même remplir le formulaire.',
    lookupUnavailable: 'Ce programme n’est plus disponible.',
    lookupInvalid: 'Vérifiez le numéro de mobile et réessayez.',
    requestLink: 'Renvoyer le lien',
    requestLinkSending: 'Envoi de la demande…',
    requestLinkSent:
      'Si ce numéro est associé à une carte active, le lien sera envoyé à nouveau.',
    requestLinkError: 'Impossible de demander le lien. Veuillez réessayer.',
    errors: {
      alreadyRegistered:
        'Vous avez déjà une carte pour ce programme. Utilisez le QR reçu ou contactez le restaurant pour la récupérer.',
      unavailable: 'Ce programme n’est plus disponible.',
      generic:
        'La carte n’a pas pu être créée. Réessayez dans un instant ou contactez le restaurant.',
    },
    validation: {
      prefixRequired: 'Sélectionnez un indicatif.',
      phoneRange: 'Saisissez de 6 à 15 chiffres.',
      firstNameRequired: 'Saisissez le prénom.',
      lastNameRequired: 'Saisissez le nom.',
      emailInvalid: 'Vérifiez l’adresse e-mail.',
      privacyRequired: 'Le consentement à la confidentialité est requis.',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: 'Agrandir l’image : {{name}}',
      close: 'Fermer l’aperçu de l’image',
      error: 'Impossible de charger l’image.',
    },
    title: 'Réservation',
    welcome: 'Bienvenue à',
    loading: 'Chargement du formulaire de réservation',
    missingFormCode: 'Le code du formulaire est manquant dans l’URL.',
    loadError: 'Impossible de charger ce formulaire de réservation.',
    inactiveTitle: 'Formulaire indisponible',
    inactiveMessage:
      'Ce formulaire de réservation n’est pas actif actuellement.',
    invalidFormType: 'Ce lien n’est pas un formulaire de réservation.',
    steps: {
      date: 'Date',
      hours: 'Heures',
      menu: 'Menu',
      extra: 'Extra',
      details: 'Détails',
      calendar: 'Calendrier',
    },
    eventBanner: {
      countOne: '{{count}} événement ce mois-ci',
      count: '{{count}} événements ce mois-ci',
      tapHint: 'Appuyez pour voir quels jours',
    },
    menuBanner: {
      countOne: '{{count}} menu ce mois-ci',
      count: '{{count}} menus ce mois-ci',
      tapHint: 'Appuyez pour voir quels jours',
    },
    calendar: {
      legendEvent: 'Événement spécial',
      legendMenu: 'Menus disponibles',
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: 'Nombre de convives sans menu',
    },
    eventSheet: {
      fromTime: 'À partir de {{time}}',
      maxCovers: 'Max {{count}} couverts',
      gotIt: 'Continuer',
      continueWithEvent: 'Continuer pour l’événement',
      showMore: 'Afficher plus',
      showLess: 'Afficher moins',
      close: 'Fermer',
    },
    hours: {
      selectedDate: 'Date sélectionnée',
      guestsLabel: 'Convives (hors enfants)',
      childrenLabel: 'Enfants',
      timeSlotLabel: 'Créneau horaire disponible',
      guestOption: '{{count}} Convives',
      childrenOption: '{{count}} Enfants',
      overlimit: 'Dépassement de {{count}}',
      overbooking: 'Surbooking de {{count}}',
      availabilityFinished: 'Disponibilité terminée pour ce créneau horaire',
      attentionTitle: 'Attention',
      attentionMessage:
        'Le nombre de convives sélectionné dépasse la disponibilité restante pour ce créneau.',
      attentionOk: 'OK',
      callOverMaxOption: 'Llamar para mesas de más de {{count}} huéspedes',
      callOverMaxTitle: 'Info',
      callOverMaxMessageBefore: '¿Llamar al número',
      callOverMaxMessageAfter: 'para reservar una mesa?',
      callOverMaxHint:
        'Abriremos el marcador del teléfono para completar la llamada.',
      callOverMaxCall: 'Llamar',
      callOverMaxCancel: 'Cancelar',
    },
    extra: {
      badge: 'Extra',
      catalogTitle: 'Catalogue des services',
      mandatoryNotice:
        'Le choix d’au moins un produit/service est obligatoire.',
      serviceForGuests: 'Service/table pour {{count}} convives',
      decrease: 'Diminuer la quantité',
      increase: 'Augmenter la quantité',
    },
    menu: {
      badge: 'Menu',
      catalogTitle: 'Catalogue des menus',
      mandatoryQuantityHint:
        'Choisissez un menu (ou « Sans menu ») pour chaque personne. Le total des sélections doit être égal à {{count}} (adultes + enfants).',
      decrease: 'Diminuer la quantité',
      increase: 'Augmenter la quantité',
    },
    recap: {
      title: 'Récapitulatif de la réservation',
      time: 'Heures',
      guests: 'Convives',
      children: 'Enfants',
      selectedServices: 'Services sélectionnés',
      selectedMenus: 'Menus sélectionnés',
      total: 'Total',
      paymentOnSite: 'Paiement sur place',
    },
    details: {
      phoneTitle: 'Entrez votre numéro de téléphone pour continuer',
      welcomeBackTitle: 'Bon retour, {{name}} !',
      welcomeBackTitleGeneric: 'Bon retour !',
      welcomeBackMessage:
        'Nous avons déjà vos données. Vous pouvez continuer ou les mettre à jour si nécessaire.',
      personalInfo: 'Informations personnelles',
      consentsTitle: 'Je consens au traitement des données personnelles',
      privacyConsent: 'Traitement de la confidentialité et des données',
      marketingConsent: 'Consentement marketing et communication',
      commercialConsent:
        'Consentement au transfert de données à des tiers à des fins commerciales',
      profilingConsent: 'Consentement au profilage',
      detailsLink: 'Détails',
      selectAll: 'Tout sélectionner',
      consentModal: {
        title: 'INFORMATION SUR LE TRAITEMENT DES DONNÉES PERSONNELLES',
        subtitle: '(conformément à l’article 13 du Règ. UE n° 679/2016 - RGPD)',
        addressLabel: 'Adresse',
        emailLabel: 'E-mail',
        lastUpdated: 'Dernière mise à jour',
        section1Title: '1. Préambule',
        section1P1:
          'La présente information est fournie par {{branchName}} (Code fiscal et numéro de TVA), dont le siège social est situé à {{branchAddress}} (ci-après le « Responsable du traitement »). (ci-après le « Responsable du traitement »), conformément au Règlement UE 2016/679 (RGPD) et au D.lgs. 196/2003 (Code de la vie privée), tel que modifié par le <b>D.lgs. 101/2018.</b>',
        section1P2:
          'La présente information s’applique au traitement des données personnelles effectué par le Responsable via la plateforme <b>« Pro20 »</b> pour la gestion de l’activité, des réservations, des services proposés et des communications avec les clients.',
        section1P3: 'L’information est fournie aux Utilisateurs qui :',
        section1Li1:
          '• Utilisent le site web du Responsable pour demander des informations ou réserver une table ;',
        section1Li2:
          '• Fournissent volontairement leurs données via des appareils mobiles personnels ou locaux (ex. tablettes) pour accéder à des services tels que le programme de fidélité ou le Wi-Fi gratuit ;',
        section1Li3:
          '• Se présentent physiquement dans l’établissement et fournissent leurs données pour recevoir des newsletters, des offres commerciales et du matériel promotionnel sur les produits et services du Responsable.',
        section2Title: '2. Responsable du traitement',
        section2Sensitive:
          'Les données sensibles ne seront traitées que si strictement indispensables et avec le consentement de l’Utilisateur, manifesté par des actions explicites telles que la sélection d’une option en ligne (ex. « J’accepte » ou « Je consens ») ou la signature d’un formulaire papier.',
        section3Title: '3. Données traitées et modes d’acquisition',
        section3Intro: 'Les données sont acquises de la manière suivante :',
        section3Li1:
          '• Via la plateforme PRO20 pour la gestion des réservations et des services proposés.',
        section3Li2:
          '• Via le site web du Responsable, lorsque l’Utilisateur demande des informations ou réserve une table',
        section3Li3:
          '• Directement dans l’établissement, lorsque l’Utilisateur fournit des données pour accéder aux services (Wi-Fi, fidélisation, etc.).',
        section4Title: '4. Données de navigation',
        section4Body:
          'Si l’Utilisateur interagit avec le site web ou d’autres plateformes numériques, des données de navigation sont collectées automatiquement (ex. adresse IP, type d’appareil, système d’exploitation, pages visitées). L’utilisation des cookies et outils similaires est décrite dans la Cookie Policy, consultable sur le site web du Responsable.',
        section5Title: '5. Finalités du traitement',
        section51Title: '5.1 Finalités principales',
        section51Intro: 'Les données sont traitées pour :',
        section51Li1:
          '✔ Gérer les réservations et services demandés (file-rapide, Wi-Fi, fidélisation, questionnaires de satisfaction, etc.)',
        section51Li2:
          '✔ Respecter les obligations légales, fiscales et administratives',
        section51Li3:
          '✔ Protéger le personnel et le service contre les comportements illicites ou frauduleux',
        section51Li4:
          '✔ Analyses statistiques anonymes pour améliorer la qualité des services',
        section51Li5:
          '✔ Communiquer avec l’Utilisateur concernant la réservation ou d’autres services demandés',
        section51Note:
          '📌 La fourniture des données est nécessaire pour accéder aux services : leur non-fourniture peut empêcher la réservation ou la prestation du service.',
        section52Title:
          '5.2 Finalités secondaires (Marketing et promotions) : uniquement avec consentement explicite.',
        section52Intro:
          'Sous réserve d’un consentement explicite, les données peuvent être utilisées pour :',
        section52Li1:
          '📢 Envoi de communications promotionnelles et d’offres personnalisées, par e-mail, SMS ou autres canaux numériques',
        section52Li2: '📢 Études de marché et enquêtes sur l’expérience client',
        section52Li3:
          '📢 Analyse des habitudes de consommation pour offrir des expériences personnalisées.',
        section52Note:
          '⚠ Le consentement marketing est facultatif et peut être révoqué à tout moment en écrivant à {{email}}.',
        section6Title: '6. Destinataires des données',
        section6Intro: 'Les données collectées peuvent être communiquées à :',
        section6Li1:
          '• Personnel interne autorisé du Responsable, pour la gestion du service.',
        section6Li2:
          '• Prestataires de services informatiques et plateformes de gestion (ex. réservation en ligne, e-mail marketing, hébergement web), dans le respect d’accords de confidentialité.',
        section6Li3:
          '• Autorités publiques, en cas de demandes légales ou d’obligations réglementaires.',
        section6Note:
          '📌 Les données ne seront PAS divulguées ni vendues à des tiers.',
        section7Title: '7. Conservation des données',
        section7Intro: 'Les données seront conservées pour :',
        section7Li1:
          '📌 Finalités administratives et comptables : pendant le temps nécessaire au respect des obligations légales (jusqu’à 10 ans).',
        section7Li2:
          '📌 Finalités marketing : jusqu’au retrait du consentement ou, en l’absence d’interactions, pendant un maximum de 24 mois.',
        section7Li3:
          '📌 Données de navigation : selon ce qui est établi par la Cookie Policy.',
        section8Title: '8. Droits de l’Utilisateur',
        section8Intro: 'Les utilisateurs ont le droit de :',
        section8Li1:
          '✅ Accéder à leurs données personnelles et obtenir des informations sur leur traitement.',
        section8Li2:
          '✅ Demander la rectification ou la suppression des données personnelles.',
        section8Li3:
          '✅ Limiter ou s’opposer au traitement pour des motifs légitimes.',
        section8Li4:
          '✅ Retirer le consentement à tout moment sans préjudice de la licéité du traitement antérieur.',
        section8Li5:
          '✅ Déposer une plainte auprès de l’Autorité de protection des données (www.garanteprivacy.it).',
        section9Title:
          '9. Consentement au transfert de données à des tiers à des fins commerciales',
        section9Li1:
          '✅ Je consens au traitement de mes données personnelles à des fins de marketing direct par des tiers, y compris par leur cession à des partenaires commerciaux opérant dans des secteurs tels que l’édition, la technologie, les voyages, la finance, etc. Ces sujets pourront me contacter par e-mail, téléphone ou autres canaux pour leurs propres initiatives promotionnelles. Le consentement est facultatif et peut être révoqué à tout moment en écrivant à {{email}}.',
        section9Contact: '📧 Pour exercer vos droits, contactez : {{email}}',
        section10Title: '10. Modifications de l’information',
        section10Body:
          'Le Responsable se réserve le droit de mettre à jour cette information. Les modifications seront communiquées sur le site web ou par e-mail si nécessaire.',
      },
    },
    actions: {
      continue: 'Continuer',
      back: 'Retour',
      proceedToDetails: 'Passer aux informations personnelles',
    },
    validation: {
      date: 'Veuillez sélectionner une date.',
      hours: 'Veuillez compléter convives, enfants et créneau horaire.',
      menu: 'Veuillez vérifier votre sélection de menu.',
      menuMandatory:
        'Veuillez choisir les menus pour que le total corresponde aux adultes + enfants.',
      extra: 'Veuillez sélectionner au moins un service.',
    },
    fieldErrors: {
      firstNameMin: 'Le prénom est obligatoire.',
      firstNameInvalid: 'Le prénom ne peut contenir que des lettres.',
      lastNameMin: 'Le nom doit contenir au moins 2 caractères.',
      lastNameInvalid: 'Le nom ne peut contenir que des lettres.',
      emailRequired: 'L’e-mail est requis.',
      emailInvalid: 'Veuillez saisir un e-mail valide.',
      postalCodeRequired: 'Le code postal est requis.',
      dateOfBirthRequired: 'La date de naissance est requise.',
      specialRequestsRequired: 'Les demandes spéciales sont requises.',
      singleChoiceRequired: 'Veuillez sélectionner une option.',
      multiChoiceRequired: 'Veuillez sélectionner au moins une option.',
      dateOfBirthInvalid: 'Veuillez saisir une date de naissance valide.',
      dateOfBirthFuture: 'La date de naissance ne peut pas être dans le futur.',
      countryCodeInvalid: 'Veuillez sélectionner un indicatif valide.',
      phoneRequired: 'Le numéro de téléphone est requis.',
      phoneInvalid: 'Le numéro de téléphone ne doit contenir que des chiffres.',
      phoneMin: 'Le numéro doit contenir au moins 6 chiffres.',
      phoneMax: 'Le numéro peut contenir au maximum 15 chiffres.',
      privacyRequired: 'Le consentement privacy est requis.',
      cardRequestConsentRequired:
        'Veuillez accepter les conditions de vérification / acompte.',
      bookingTimeInvalid: 'Veuillez sélectionner une heure valide.',
      coverOverlimit:
        'Convives et enfants dépassent le maximum de couverts de ce formulaire.',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice:
        'Pour confirmer cette réservation, une carte de paiement est requise pour le créneau sélectionné.',
      alreadyOnFile:
        'Une carte est déjà enregistrée pour ce numéro. Confirmez la réservation pour l’utiliser.',
      acceptValidation:
        'J’accepte la vérification de la carte. Aucun montant ne sera débité ni bloqué pendant cette opération.',
      acceptDeposit:
        'J’accepte la préautorisation de {{amount}} sur la carte et les conditions régissant l’encaissement, l’annulation et le no-show.',
      acceptPayNow:
        'Je confirme le paiement de {{amount}} et j’accepte les conditions d’annulation, de remboursement et de no-show.',
      policyToggle: 'Lire la politique de paiement complète',
      policyTitle: 'Politique de paiement, carte et annulation',
      policy: {
        s1Title: '1. Identité de l’établissement',
        s1Body:
          'La réservation et le paiement sont gérés par {{branchName}}, {{branchAddress}}. Stripe agit exclusivement comme prestataire de paiement. Contact : {{contact}}.',
        s2Title: '2. Vérification de la carte',
        s2Body:
          'Lorsque seule la vérification est demandée, aucun montant n’est débité ni bloqué. La carte est vérifiée via Stripe.',
        s3Title: '3. Préautorisation / acompte',
        s3Body:
          'Une préautorisation de {{amount}} ({{perGuest}} par convive) peut être temporairement bloquée. Le délai de libération dépend de la banque.',
        s4Title: '4. Paiement anticipé',
        s4Body:
          'Le cas échéant, un acompte peut être débité selon les règles de l’établissement indiquées lors de la réservation.',
        s5Title: '5. Annulation et modification',
        s5Body:
          'L’annulation gratuite peut s’appliquer jusqu’au délai de l’établissement. Ensuite, des montants peuvent être retenus ou débités.',
        s6Title: '6. No-show et retard',
        s6Body:
          'En cas de no-show ou de retard important, l’établissement peut retenir ou débiter des montants selon sa politique.',
        s7Title: '7. Remboursements',
        s7Body:
          'Les remboursements éligibles sont traités sur le même moyen de paiement, dans les délais de l’établissement et du prestataire.',
        s8Title: '8. Conservation de la carte',
        s8Body:
          'Les données de carte peuvent être stockées sous forme tokenisée par Stripe et réutilisées uniquement avec un consentement spécifique. PRO20 et l’établissement ne stockent pas le numéro complet.',
        s9Title: '9. Litiges et assistance',
        s9Body:
          'Pour annulations, litiges ou assistance, contactez l’établissement ({{contact}}) en indiquant votre code de réservation.',
        version: 'Version {{version}}',
      },
    },
    legacyCard: {
      eyebrow: 'Vérification directe',
      title: 'Données de carte de crédit',
      subtitle: 'L’établissement n’utilise pas Stripe pour cette réservation.',
      holder: 'Nom sur la carte',
      number: 'Numéro de carte',
      month: 'Mois',
      year: 'Année',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: 'Carte vérifiée · se termine par {{last4}}',
      cancel: 'Annuler',
      verify: 'Vérifier la carte',
      verifying: 'Vérification…',
      confirm: 'Confirmer',
      verifySuccess: 'Carte vérifiée avec succès',
      verifyError: 'Les données de la carte ne sont pas valides',
      requiredTitle: 'Carte requise',
      required: 'La carte doit être vérifiée pour finaliser la réservation.',
      storeWarning:
        'La réservation a été créée, mais la carte n’a pas pu être enregistrée. Réessayez depuis la demande de carte.',
      saved: 'Carte enregistrée · se termine par {{last4}}',
      alreadyOnFile:
        'Une carte est déjà enregistrée pour ce client. Aucun autre détail n’est nécessaire.',
      pageLoadError: 'Impossible d’ouvrir cette demande de carte.',
      saveError: 'La carte n’a pas pu être enregistrée. Veuillez réessayer.',
      fieldRequired: 'Champ obligatoire',
      fieldInvalid: 'Valeur non valide',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: 'Vérifiez votre carte',
      verifyDescription:
        'Saisissez les informations de votre carte pour confirmer cette réservation. Aucun montant ne sera débité maintenant.',
      verifyConfirm: 'Vérifier la carte',
      payTitle: 'Finaliser le paiement',
      payDescription: 'Autorisez {{amount}} pour confirmer cette réservation.',
      payConfirm: 'Payer {{amount}}',
      initializing: 'Préparation du paiement sécurisé…',
      processing: 'Traitement…',
      initError: 'Impossible de démarrer le paiement Stripe. Réessayez.',
      confirmError: 'Le paiement n’a pas pu être effectué. Réessayez.',
      verifyPending: 'Stripe n’a pas encore confirmé la carte. Réessayez.',
      cardRequired:
        'La carte doit être vérifiée pour finaliser la réservation.',
      resumePayment: 'Finaliser le paiement',
      resumeNotice:
        'Votre réservation est en attente de paiement. Finalisez le paiement pour la confirmer : aucune nouvelle réservation ne sera créée.',
    },
    phonePlaceholder: 'Saisir le numéro de téléphone',
    unknownCustomer: 'Client inconnu',
    blacklist: 'Liste noire',
    customerDetails: 'Vos informations',
    bookingDetails: 'Détails de la réservation',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    phone: 'Téléphone',
    postalCode: 'Code postal',
    dateOfBirth: 'Date de naissance',
    guests: 'Convives',
    children: 'Enfants',
    time: 'Heure',
    selectTime: 'Sélectionner l’heure',
    slotNotAvailable: 'Disponibilité terminée pour ce créneau horaire',
    noAvailableTimes: 'Aucun horaire disponible pour la date sélectionnée.',
    specialRequests: 'Notes personnelles et demandes',
    comingWithDogs: 'Je viens avec des chiens',
    extraServices: 'Services supplémentaires',
    amenityGuests: '{{count}} convives',
    submit: 'Envoyer la réservation',
    submitError: 'Impossible d’envoyer votre réservation. Veuillez réessayer.',
    existingBooking: {
      title: 'Attention, une réservation existe déjà',
      date: 'Date',
      time: 'Heure',
      guests: 'Convives',
      children: 'Enfants',
      event: 'Événement',
      menus: 'Menus',
      noMenu: 'Sans menu',
      services: 'Services',
      continueQuestion:
        'Souhaitez-vous continuer et créer cette réservation malgré tout ?',
      continue: 'Continuer',
    },
    thankYou: {
      title: 'Merci !',
      message:
        'Votre demande de réservation a été reçue. Nous vous contacterons bientôt.',
      summaryTitle: 'Récapitulatif de la réservation',
      date: 'Date',
      time: 'Heure',
      guests: 'Convives',
      guestsValue: '{{guests}} Convive ({{children}} Enfants)',
      location: 'Lieu',
      name: 'Nom',
      phone: 'Téléphone',
      eventCode: 'Code événement',
      eventName: 'Événement',
      menus: 'Menus',
      makeAnother: 'Faire une autre réservation',
      backHome: 'Retour à l’accueil',
      continue: 'Continuer',
    },
  },
  publicBookingUpdate: {
    title: 'Modifier la réservation',
    loading: 'Chargement de la réservation',
    missingBookingCode: 'Le code de réservation est manquant dans l’URL.',
    loadError: 'Impossible de charger cette réservation.',
    formLoadError:
      'Impossible de charger les disponibilités pour cette réservation.',
    noFormTitle: 'Modification en ligne indisponible',
    noFormMessage:
      'Cette réservation ne peut pas être modifiée en ligne. Contactez directement l’établissement.',
    notEditable: 'La réservation n’est plus modifiable',
    hello: 'Bonjour {{name}}',
    subtitle: 'Vous modifiez votre réservation chez {{branch}}',
    currentBadge: 'Actuelle : {{time}} · {{date}}',
    currentlyBooked: 'Réservation actuelle',
    current: {
      title: 'Votre réservation',
      client: 'Client',
      date: 'Date actuelle',
      time: 'Heure actuelle',
      guests: 'Convives',
      children: 'Enfants',
      event: 'Événement',
      menu: 'Menu',
      menuPeople: 'Personnes',
      showDetails: 'Détails',
      hideDetails: 'Masquer',
    },
    pendingModification: {
      title: 'Une demande de modification est déjà en attente de confirmation',
      summary:
        '{{date}} · {{time}} · {{guests}} convives, {{children}} enfants',
      date: 'Date',
      time: 'Heure',
      guests: 'Convives',
      children: 'Enfants',
    },
    form: {
      title: 'Modifier la réservation',
      subtitle:
        'Choisissez une nouvelle date, un nombre de convives et un créneau.',
      selectedDate: 'Date sélectionnée',
      guestsLabel: 'Convives (enfants exclus)',
      childrenLabel: 'Enfants',
      timeSlotLabel: 'Créneau horaire disponible',
      guestOption: '{{count}} Convives',
      childrenOption: '{{count}} Enfants',
      selectTime: 'Sélectionner l’heure',
      noAvailableTimes: 'Aucun créneau disponible pour la date sélectionnée.',
      notesLabel: 'Notes et demandes personnelles',
      notesPlaceholder: 'Ajoutez une note pour l’établissement…',
      readyToUpdate:
        'Vérifiez vos modifications, puis appuyez sur Mettre à jour pour envoyer la demande.',
      calendarUnavailable:
        'Le calendrier n’est pas disponible pour cette réservation (formulaire ou succursale manquant).',
    },
    actions: {
      update: 'Modifier la réservation',
      updating: 'Mise à jour…',
      delete: 'Supprimer',
      cancelReservation: 'Annuler la réservation',
      cancelling: 'Annulation…',
    },
    deleteConfirm:
      'Voulez-vous vraiment annuler cette réservation ? Cette action est irréversible.',
    deleteModal: {
      title: 'Annuler la réservation ?',
      description:
        'Voulez-vous vraiment annuler cette réservation ? Cette action est irréversible.',
    },
    toast: {
      updateSuccess: 'Demande de modification envoyée.',
      updateError:
        'Impossible de mettre à jour la réservation. Veuillez réessayer.',
      validationError:
        'Veuillez corriger les champs indiqués avant de mettre à jour.',
      deleteSuccess: 'Réservation supprimée avec succès.',
      deleteError:
        'Impossible de supprimer la réservation. Veuillez réessayer.',
    },
    thankYou: {
      badge: 'Demande envoyée',
      title: 'Merci',
      headerMessage:
        'Nous avons reçu votre demande de modification et répondrons dès que possible.',
      statusLabel: 'Demande envoyée',
      awaitingTitle: 'La modification est en attente de confirmation',
      awaitingMessage:
        'Nous avons reçu votre demande de modification et répondrons dès que possible.',
      step1Title: 'Vérification par l’établissement',
      step1Message:
        'Le personnel vérifiera la disponibilité et les détails de la demande.',
      step2Title: 'Réponse automatique',
      step2Message:
        'Vous recevrez une mise à jour dès que la modification sera acceptée ou refusée.',
      closeHint:
        'Vous pouvez fermer cette page : votre demande a été enregistrée correctement.',
    },
    result: {
      updatedTitle: 'Réservation mise à jour',
      updatedMessage:
        'Vos modifications ont été enregistrées. L’établissement a été informé de votre mise à jour.',
      deletedTitle: 'Réservation supprimée',
      deletedMessage:
        'Votre réservation a été annulée. Nous espérons vous revoir une prochaine fois.',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: 'Déconnexion',
    login: {
      documentTitle: 'Connexion Backoffice',
      title: 'Connexion au Backoffice',
      subtitle:
        'Utilisez votre e-mail et mot de passe administrateur pour continuer.',
      emailPlaceholder: 'E-mail',
      passwordPlaceholder: 'Mot de passe',
      rememberCredentials: 'Se souvenir des identifiants',
      signIn: 'Se connecter',
      invalidCredentials: 'E-mail ou mot de passe invalide.',
      adminBadge: 'Accès admin',
      authorizedOnly: 'Personnel autorisé uniquement',
      heroTitle: 'Pilotez la plateforme Pro20 en toute confiance',
      heroSubtitle:
        'Accès backoffice sécurisé pour restaurants, messagerie et contrôles plateforme.',
      heroCardTitle: 'Centre de contrôle sécurisé',
      heroCardSubtitle: 'Opérations plateforme',
      heroPointOne: 'Gérez restaurants, équipes et onboarding',
      heroPointTwo: 'Suivez la santé de la messagerie et les alertes',
      heroPointThree:
        'Appliquez des réglages globaux avec une pleine traçabilité',
      trustedBy: 'Fiable pour des opérations sécurisées',
      trustAccessTitle: 'Accès contrôlé',
      trustAccessDesc: 'Identifiants admin uniquement avec sessions protégées',
      trustAuditTitle: 'Prêt pour audit',
      trustAuditDesc: 'Les actions critiques restent traçables',
      trustPlatformTitle: 'Échelle plateforme',
      trustPlatformDesc: 'Pilotez plusieurs lieux depuis une console',
      validation: {
        emailRequired: "L'e-mail est obligatoire",
        emailInvalid: 'Saisissez une adresse e-mail valide',
        emailMax: "L'e-mail doit contenir au maximum 255 caractères",
        passwordMin: 'Le mot de passe doit contenir au moins 8 caractères',
        passwordMax: 'Le mot de passe doit contenir au maximum 128 caractères',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: 'Bienvenue sur le panneau opérationnel Pro20',
    headingWelcomeBack: 'Bon retour, {{name}}',
    headingWelcomeBackGeneric: 'Bon retour',
    actions: {
      announcements: 'Annonces',
      notifications: 'Notifications',
      globalAlertTooltip: "Alerte globale sur l'application",
      expiredActivities: 'Activités expirées',
      expiredActivitiesTooltip: "Activer/désactiver les alertes d'échéance",
      removeBanner: 'Supprimer la bannière',
      removeBannerTooltip: "Supprimer la bannière globale de l'application",
    },
    releaseMobile: {
      title: 'Release Mobile',
      currentVersion: 'Version actuelle {{version}}',
      manageVersion: 'Gérer la version',
      toast: {
        versionUpdated: "Version de l'application mise à jour",
        versionUpdateFailed:
          "Impossible de mettre à jour la version de l'application",
        bannerRemoved: 'Bannière globale supprimée',
        bannerRemoveFailed: 'Impossible de supprimer la bannière globale',
      },
    },
    manageVersionModal: {
      title: "Mettre à jour la version de l'application",
      loading: 'Chargement de la version actuelle…',
      loadError: 'Impossible de charger la version actuelle.',
      major: 'Major',
      majorHint: 'Changements majeurs',
      minor: 'Minor',
      minorHint: 'Nouvelles fonctionnalités',
      bug: 'Bug',
      bugHint: "Corrections de l'application",
      patch: 'Patch',
      patchHint: 'Release Review',
      formatHint: 'Le format publié sera Major.Minor.Bug.Patch.',
      update: 'Mettre à jour la version',
    },
    removeBannerModal: {
      title: 'Supprimer la bannière globale ?',
      description: "Le message ne sera plus affiché dans l'application.",
      confirm: 'Supprimer la bannière',
      cancel: 'Annuler',
    },
    globalAlertModal: {
      title: "Saisissez le nouveau message d'alerte global",
      confirmation: 'Confirmation',
      cancel: 'Annuler',
      toast: {
        success: 'Alerte globale mise à jour',
        failed: "Impossible de mettre à jour l'alerte globale",
      },
    },
    createActivityModal: {
      title: 'Créer une succursale',
      cancel: 'Annuler',
      create: 'Créer',
      selectUser: 'Sélectionner un utilisateur',
      selectUserPlaceholder: 'Sélectionnez un utilisateur',
      selectUserSearchPlaceholder: 'Rechercher un utilisateur par nom ou email',
      restaurantName: 'Nom du restaurant',
      email: 'Email',
      phone: 'Téléphone',
      vatNumber: 'Numéro de TVA',
      companyName: "Nom de l'entreprise",
      sections: {
        marketing: 'Marketing',
        address: 'Adresse',
        geolocation: 'Géolocalisation',
        integration: 'Intégration',
      },
      gtmCode: 'Code GTM',
      gtmCodeHint: 'Code Google Tag Manager',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'Le code Pixel ne contient que des chiffres',
      address: 'Adresse',
      postalCode: 'Code postal',
      city: 'Ville',
      country: 'Pays',
      latitude: 'Latitude',
      latitudeHint: 'Ex. 45.464211',
      longitude: 'Longitude',
      longitudeHint: 'Ex. 9.191383',
      ipraticoApiKey: 'Clé API iPratico',
      ipraticoApiKeyHint: "Clé API pour l'intégration avec iPracto",
      noUsers: 'Aucun utilisateur disponible',
      toast: {
        created: 'Succursale créée',
        createFailed: 'Impossible de créer la succursale',
      },
      validation: {
        userRequired: "La sélection de l'utilisateur est obligatoire",
        restaurantRequired: 'Le nom du restaurant est obligatoire',
        emailInvalid: 'Saisissez une adresse email valide',
        facebookPixelNumeric:
          'Facebook Pixel ne doit contenir que des chiffres',
      },
    },
    editActivity: {
      pageTitle: "Modifier l'activité",
      breadcrumbRoot: 'backoffice',
      close: 'Fermer',
      back: 'Retour aux activités',
      notFound: 'Activité introuvable.',
      update: 'Mettre à jour',
      enable: 'Activer',
      disabled: 'Désactivé',
      smsRemaining: 'SMS résiduels',
      addSms: 'Ajouter des SMS',
      editSms: 'Modifier les SMS résiduels',
      configureSmsModal: {
        title: 'Mettre à jour le nombre de SMS restants',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configurer SMS',
      },
      aiCreditsRemaining: 'Crédits IA',
      editAiCredits: 'Modifier les crédits IA',
      configureAiCreditsModal: {
        title: 'Mettre à jour le solde des crédits IA',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configurer les crédits IA',
      },
      emailSetup: 'Configuration email',
      editEmailConfig: 'Modifier la configuration email',
      restoreDefaultEmailConfig: 'Restaurer la configuration par défaut',
      emailFields: {
        mailResidue: 'Résidu email',
        customerEmail: 'Email client',
        fromAddress: 'Adresse d’expédition',
        password: 'Mot de passe',
        smtpServer: 'Serveur SMTP',
        smtpPort: 'Port SMTP',
      },
      configureEmailModal: {
        title: 'Mettre à jour le nombre d’emails restants',
        mailResidue: 'Résidu email',
        customerEmail: 'Email client pour la messagerie personnalisée',
        fromAddress: 'Adresse d’expédition',
        password: 'Mot de passe généré',
        smtpServer: 'Serveur SMTP',
        smtpPort: 'Port SMTP',
        required: 'Ce champ est obligatoire',
        save: 'Enregistrer la configuration',
      },
      sections: {
        general: 'Données générales',
        subscription: 'Abonnement',
        address: 'Adresse',
        coordinates: 'Coordonnées',
        marketing: 'Marketing',
        integrations: 'Intégrations',
        communications: 'Communications',
      },
      restaurantName: 'Nom de la succursale',
      branchNameOnGoogle: 'Nom de marque (Google)',
      email: 'Email',
      capacity: 'Capacité du local',
      vatNumber: 'Numéro de TVA',
      companyName: "Nom de l'entreprise",
      phone: 'Mobile',
      url: 'URL',
      deadline: "Date d'expiration",
      address: 'Adresse',
      postalCode: 'Code postal',
      city: 'Ville',
      province: 'Province / Localité',
      region: 'Région',
      country: 'Pays',
      longitude: 'Longitude',
      latitude: 'Latitude',
      gtmCode: 'Code GTM',
      facebookPixel: 'Code FB Pixel',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: 'Saisir la clé',
      menuRestaurant: 'Menu restaurant',
      menuRestaurantDesc:
        'Activez le menu numérique du restaurant pour cette succursale.',
      officialApi: 'API officielles',
      officialApiDesc:
        "Activez l'API officielle WhatsApp pour cette succursale.",
      googleReservation: 'Google Reservation',
      googleReservationDesc:
        'Activez Google Reservation pour cette succursale.',
      supplierOrders: 'Commandes fournisseurs',
      supplierOrdersDesc:
        'Activez les commandes fournisseurs pour cette succursale.',
      deliveryTakeaway: 'Livraison / À emporter',
      deliveryTakeawayDesc:
        'Activez la livraison et l’à emporter pour cette succursale. Nécessite le Menu restaurant.',
      automation: 'Automatisation',
      automationDesc: 'Activez l’automatisation pour cette succursale.',
      statusEnabled: 'Active',
      statusDisabled: 'Désactivée',
      sectionHints: {
        general:
          'Nom, contact et nombre d’invités que cette succursale peut accueillir.',
        subscription: 'Date d’expiration de l’accès à cette succursale.',
        address: 'Où les clients et les partenaires trouvent cette succursale.',
        coordinates:
          'Position sur la carte utilisée par les annonces publiques.',
        marketing: 'Codes de suivi du site de cette succursale.',
        integrations:
          'Produits et services partenaires reliés à cette succursale.',
        communications:
          'Solde SMS, crédits IA et boîte utilisée pour les messages aux clients.',
      },
      publicApi: {
        title: 'API REST publique',
        description:
          'Exposez l’historique des réservations de cette succursale. Les clients s’authentifient avec l’en-tête X-API-KEY.',
        configure: 'Configurer',
        statusConfigured: 'Configurée',
        statusMissing: 'Non configurée',
        notConfigured:
          'Pas encore de X-API-KEY. Configurez cette succursale pour la créer sur le serveur.',
        loadFailed: 'Impossible de charger la X-API-KEY.',
        retry: 'Réessayer',
        configureFailed: 'Impossible de créer la X-API-KEY',
        configured: 'X-API-KEY créée',
        deleteConfiguration: 'Supprimer la configuration',
        deleteConfirmTitle: 'Supprimer la configuration de l’API REST ?',
        deleteConfirmDescription:
          'Cela retire la clé X-API-KEY de cet établissement. Les appelants existants ne pourront plus l’utiliser.',
        deleted: 'Configuration de l’API REST supprimée',
        deleteFailed: 'Impossible de supprimer la configuration de l’API REST',
        keyLabel: 'X-API-KEY',
        keyHint:
          'Envoyez cette valeur dans l’en-tête X-API-KEY à chaque requête d’historique.',
        showKey: 'Afficher la clé',
        hideKey: 'Masquer la clé',
        copyKey: 'Copier la clé',
        copiedKey: 'X-API-KEY copiée',
        copyFailed: 'Impossible de copier dans le presse-papiers',
        bookingHistory: 'Historique des réservations',
        sampleRequest: 'Exemple de requête',
        sampleResponse: 'Exemple de réponse',
        copySample: 'Copier l’exemple',
        copiedSample: 'Exemple copié',
        filtersHint:
          'Chaque paramètre est listé séparément. L’endpoint change selon les filtres inclus.',
        copyFilters: 'Copier tous les filtres',
        showFilters: 'Afficher les filtres',
        hideFilters: 'Masquer les filtres',
        copiedFilters: 'Filtres copiés',
        endpointLabel: 'Endpoint',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: 'Succursale mise à jour',
        updateFailed: 'Impossible de mettre à jour la succursale',
        featureUpdated: 'Fonctionnalité mise à jour',
        featureUpdateFailed: 'Impossible de mettre à jour la fonctionnalité',
        deliveryRequiresMenu:
          'Activez le Menu restaurant avant d’activer Livraison / À emporter.',
        smsUpdated: 'Nombre de SMS mis à jour',
        smsUpdateFailed: 'Impossible de mettre à jour le nombre de SMS',
        aiCreditsUpdated: 'Crédits IA mis à jour',
        aiCreditsUpdateFailed: 'Impossible de mettre à jour les crédits IA',
        emailUpdated: 'Configuration email enregistrée',
        emailRestored: 'Configuration email restaurée',
      },
      validation: {
        restaurantRequired: 'Le nom de la succursale est obligatoire',
        emailInvalid: 'Saisissez une adresse email valide',
        urlInvalid: 'Saisissez un lien valide',
        facebookPixelNumeric:
          'Facebook Pixel ne doit contenir que des chiffres',
        capacityInvalid: 'Saisissez une capacité valide',
        capacityInteger: 'La capacité doit être un nombre entier',
        capacityMin: 'La capacité doit être d’au moins 0',
      },
    },
    activities: {
      title: 'Succursales',
      count: '{{count}} succursales',
      create: 'Créer une succursale',
      loadError: 'Impossible de charger les succursales. Veuillez réessayer.',
      expiringCaption: 'Expiration',
      expiringAria: '{{count}} succursales expirent en {{month}}',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: 'Sécurité',
    },
    security: {
      title: 'Changer le mot de passe',
      subtitle: 'Mettez à jour le mot de passe de votre compte backoffice.',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      submit: 'Mettre à jour le mot de passe',
      validation: {
        required: 'Le mot de passe est obligatoire',
        minLength: 'Le mot de passe doit contenir au moins 6 caractères',
        confirmRequired: 'Veuillez confirmer votre mot de passe',
        mismatch: 'Les mots de passe ne correspondent pas',
      },
      toast: {
        success: 'Mot de passe mis à jour avec succès',
        failed:
          'Impossible de mettre à jour le mot de passe. Veuillez réessayer.',
      },
    },
    users: {
      title: 'Utilisateurs',
      count: '{{count}} utilisateurs',
      create: 'Créer un utilisateur',
      searchPlaceholder: 'Rechercher un utilisateur..',
      loadError: 'Impossible de charger les utilisateurs. Veuillez réessayer.',
      table: {
        userCode: 'UserCode',
        name: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        activity: 'Activité',
        visibleOnApp: "Visible sur l'app",
      },
      tooltips: {
        linkActivity: 'Lier une activité à l’utilisateur',
        editUser: 'Modifier l’utilisateur',
        goToBackoffice: 'Aller au backoffice de l’utilisateur',
        deleteUser: 'Supprimer l’utilisateur',
      },
      fields: {
        name: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        password: 'Mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
      },
      createModal: {
        title: 'Créer un utilisateur',
        cancel: 'Annuler',
        create: 'Créer',
      },
      editModal: {
        title: 'Modifier l’utilisateur',
        cancel: 'Annuler',
        update: 'Mettre à jour',
      },
      linkActivityModal: {
        title: 'Sélectionner une activité',
        searchLabel: 'Rechercher une activité',
        searchPlaceholder: 'Rechercher par nom ou code',
        availableTitle: 'Activités disponibles',
        cancel: 'Annuler',
        confirm: 'Confirmer',
      },
      relatedActivitiesModal: {
        title: 'Activités associées',
        name: 'Nom',
        code: 'Code',
        expiration: 'Expiration',
        unlink: "Dissocier l'activité",
        close: 'Fermer',
      },
      unlinkModal: {
        title: 'Dissocier l’activité ?',
        description:
          'Voulez-vous vraiment dissocier « {{name}} » de cet utilisateur ? Cette action est irréversible.',
      },
      deleteModal: {
        title: 'Supprimer l’utilisateur ?',
        description:
          'Voulez-vous vraiment supprimer « {{name}} » ? Cette action est irréversible.',
      },
      validation: {
        nameRequired: 'Le nom est obligatoire',
        emailRequired: 'L’email est obligatoire',
        emailInvalid: 'Entrez une adresse email valide',
        passwordMin: 'Le mot de passe doit contenir au moins 6 caractères',
        passwordMismatch: 'Les mots de passe ne correspondent pas',
      },
      toast: {
        created: 'Utilisateur créé',
        createFailed: "Impossible de créer l'utilisateur",
        updated: 'Utilisateur mis à jour',
        updateFailed: "Impossible de mettre à jour l'utilisateur",
        deleted: 'Utilisateur supprimé',
        deleteFailed: "Impossible de supprimer l'utilisateur",
        loadFailed: "Impossible de charger les détails de l'utilisateur",
        activitiesLinked: 'Activités liées à l’utilisateur',
        activitiesLinkFailed: "Impossible d'associer les activités",
        activityUnlinked: "Activité dissociée de l'utilisateur",
        activityUnlinkFailed: "Impossible de dissocier l'activité",
        noAssociatedActivity: 'Aucune activité associée',
        visibilityUpdated: "Visibilité de l'utilisateur mise à jour",
        visibilityUpdateFailed:
          "Impossible de mettre à jour la visibilité de l'utilisateur",
      },
    },
    reports: {
      badge: 'Moniteur d’intégration',
      title: 'Erreurs iPratico',
      subtitle:
        'Consultez les requêtes, réponses et anomalies enregistrées pendant les opérations vers iPratico.',
      errorsFound: 'erreurs trouvées',
      actions: {
        update: 'Mettre à jour',
        reset: 'Réinitialiser',
        filter: 'Filtrer',
      },
      filters: {
        branchCode: 'Code agence',
        operation: 'Opération',
        httpStatus: 'Statut HTTP',
        fromDay: 'Depuis le jour',
        toDay: 'Jusqu’au jour',
        allStatuses: 'Tous les statuts',
        status400: '400 — Requête invalide',
        status401: '401 — Non autorisé',
        status403: '403 — Accès refusé',
        status404: '404 — Ressource introuvable',
        status409: '409 — Conflit',
        status429: '429 — Trop de requêtes',
        status500: '500 — Erreur iPratico',
        status502: '502 — Gateway',
        status503: '503 — Service indisponible',
      },
      table: {
        date: 'Date',
        branch: 'Agence',
        operation: 'Opération',
        status: 'Statut',
        endpoint: 'Endpoint',
        mistake: 'Erreur',
      },
      detail: {
        title: 'Détail de l’erreur',
        branch: 'Agence',
        branchName: 'Nom de l’établissement',
        branchCode: 'Code établissement',
        date: 'Date',
        method: 'Méthode',
        status: 'Statut',
        apiKey: 'X-API-KEY utilisée',
        endpoint: 'Endpoint',
        message: 'Message',
        requestPayload: 'Payload requête',
        responsePayload: 'Réponse iPratico',
      },
      tooltips: {
        openDetail: 'Ouvrir le détail',
        closeDetail: 'Fermer le détail',
        copyPayload: 'Copier le payload',
      },
      toast: {
        updated: 'Rapports mis à jour',
        copied: 'Copié dans le presse-papiers',
        loadFailed: 'Impossible de charger les erreurs iPratico',
      },
      loadError:
        'Impossible de charger les erreurs iPratico. Veuillez réessayer.',
    },
    searchPlaceholder: 'Rechercher par nom, code, entreprise ou TVA',
    filters: {
      features: {
        label: 'Fonctionnalités',
        google: 'Réservation Google',
        menu: 'Menu du restaurant',
        ipraticoKey: 'Clé iPratico',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: 'Nom de la succursale',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: 'Expiration',
      expiredOn: 'Expiré le {{date}}',
      alertOnApp: 'Alerte sur App',
      companyName: "Nom de l'entreprise",
      noCompanyName: "Aucun nom d'entreprise",
      delete: 'Supprimer la succursale',
      settings: 'Mettre à jour la succursale',
      chat: 'Surveiller les messages',
      send: 'Aller au backoffice',
      tooltips: {
        whatsappBusiness: 'WhatsApp Business activé',
        menu: 'Menu activé',
        google: 'Réservation Google activée',
        ipraticoKey: 'Clé iPratico',
        sms: 'SMS restants : {{count}}',
        aiCredits: 'Crédits IA : {{count}}',
        loadError: 'Impossible de charger les utilisateurs de cette succursale',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect actif — voir ou supprimer la configuration',
        pending: 'Terminer, reprendre ou supprimer l’onboarding Stripe',
        unlinked: 'Connecter la succursale à Stripe Connect',
      },
      link: {
        title: 'Lien d’onboarding Stripe',
        body: 'Le lien pour {{branch}} est prêt.',
        warning:
          'Ce lien Stripe est temporaire et à usage unique : partagez-le ou utilisez-le au plus vite.',
        openLink: 'Ouvrir le lien',
        copyLink: 'Copier le lien',
        linkCopied: 'Lien copié',
        copyFailed:
          'Copie impossible — sélectionnez et copiez le lien manuellement.',
      },
      connected: {
        title: 'Stripe Connect actif',
        body: '{{branch}} est déjà connectée et peut recevoir paiements et virements.',
      },
      pending: {
        title: 'Vérifications Stripe en cours',
        body: 'Stripe demande encore des vérifications pour {{branch}}.',
        resume: 'Reprendre l’onboarding',
      },
      error: {
        title: 'Configuration Stripe échouée',
        fallback: 'Vérifiez la configuration Stripe et réessayez.',
      },
      remove: {
        action: 'Supprimer la configuration Stripe',
        confirmTitle: 'Supprimer la configuration Stripe ?',
        confirmBody:
          'Déconnecter Stripe pour {{branch}} ? La succursale ne pourra plus accepter les paiements Stripe tant qu’elle n’aura pas été reconnectée.',
        success: 'Configuration Stripe supprimée',
        failed: 'Impossible de supprimer la configuration Stripe',
      },
      close: 'Fermer',
    },
    accessUserModal: {
      title: 'Sélectionnez un utilisateur pour accéder au backoffice',
      name: 'Nom',
      email: 'Email',
      close: 'Fermer',
      loadError: 'Impossible de charger les utilisateurs de cette succursale',
    },
    signInAsUser: {
      success: 'Connecté en tant qu’utilisateur sélectionné',
      failed: 'Impossible de se connecter en tant que cet utilisateur',
      missingIdentity: 'Le code utilisateur et l’email sont obligatoires',
    },
    expiringBranchesModal: {
      title: 'Succursales expirant en {{month}}',
      name: 'Nom',
      userCode: 'Usercode',
      email: 'Mail',
      expires: 'Expiration',
      close: 'Fermer',
      empty: 'Aucune succursale n’expire ce mois-ci.',
      openDetails: 'Ouvrir les détails de la succursale',
    },
    deleteActivityModal: {
      title: 'Confirmez-vous la suppression de la succursale ?',
      description:
        'Écrivez ici le code succursale ({{branchCode}}) pour continuer la suppression',
      branchCodePlaceholder: 'CODE SUCCURSALE',
      eliminate: 'Supprimer',
      cancel: 'Annuler',
      deleted: 'Succursale supprimée',
      deleteFailed: 'Impossible de supprimer la succursale',
    },
    access: {
      title: 'Accès backoffice',
      description:
        'Saisissez le code numérique pour accéder au panneau opérationnel Pro20.',
      codePlaceholder: '',
      enter: 'Entrer',
    },
    validation: {
      codeRequired: "Le code d'accès est requis",
      codeNumeric: "Le code d'accès ne doit contenir que des chiffres",
      invalidCode: "Code d'accès invalide",
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle: 'Vérifiez les envois normaux et planifiés pour les tests API.',
      close: 'Fermer',
      reset: 'Réinitialiser',
      update: 'Mettre à jour',
      stats: {
        posted: 'Publiés sur cette page',
        errors: 'Erreurs sur cette page',
        inProgress: 'En cours sur cette page',
      },
      filters: {
        origin: 'Origine',
        state: 'État',
        channel: 'Canal',
        reference: 'Référence',
        referencePlaceholder: 'Référence',
        originEveryone: 'Tous',
        originNormal: 'Normal',
        originScheduled: 'Planifié',
        originBulk: 'En masse',
        stateEveryone: 'Tous',
        stateSent: 'Envoyé',
        stateErrors: 'Erreurs',
        statePending: 'En attente',
        stateInQueue: 'En coda',
        stateInProgress: 'En cours',
        channelEveryone: 'Tous',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'Email',
        channelSms: 'SMS',
      },
      eventsFound: '{{count}} événements trouvés',
      empty: 'Aucun message trouvé avec les filtres sélectionnés.',
      loadError: 'Impossible de charger les événements message. Réessayez.',
      actions: {
        resubmit: 'Renvoyer le message',
      },
      toast: {
        resubmitted: 'Message renvoyé',
        resubmitFailed: 'Impossible de renvoyer le message',
      },
      table: {
        date: 'Data',
        type: 'Type',
        recipient: 'Destinataire',
        reference: 'Référence',
        message: 'Message',
        channel: 'Canal',
        state: 'État',
        attempts: 'Tentatives',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: 'Détails',
      },
    },
  },
  homeLanding: {
    documentTitle:
      'PRO20 — Logiciel de réservation et de tables pour restaurants',
    login: 'Connexion',
    bookDemo: 'Réserver une démo',
    startFreeTrial: 'Demander un essai gratuit',
    learnMore: 'En savoir plus',
    nav: {
      home: 'Accueil',
      product: 'Produit',
      solutions: 'Solutions',
      industries: 'Secteurs',
      integrations: 'Intégrations',
      pricing: 'Tarifs',
      resources: 'Ressources',
      why: 'Pourquoi PRO20',
      apps: 'Apps',
      howItWorks: 'Comment ça marche',
      reviews: 'Avis clients',
      faq: 'FAQ',
      about: 'À propos',
    },
    hero: {
      partnerBadge: 'Partenaire Google Reservation C2C',
      metaPartnerBadge: 'Fournisseur technologique officiel Meta',
      titleBefore: 'Plateforme tout-en-un pour',
      titleHighlight: 'restaurants',
      titleAfter: 'et réservation de tables.',
      subtitle:
        'Gérez réservations, plans de salle, équipe, marketing, paiements et ravissez vos clients — le tout dans une plateforme puissante.',
      checkGoogle: 'Google Reservations',
      checkWhatsapp: 'WhatsApp Official API',
      checkFloor: 'Plan de salle et tables',
      checkMarketing: 'Marketing et fidélité',
      checkPayments: 'Paiements et acomptes',
      checkApps: 'Apps iOS et Android',
      trustedBy:
        'Adopté par plus de 100 restaurants dans plus de 10 villes d’Italie.',
      previewAlt: 'Aperçu du tableau de bord de réservation PRO20',
      chatAria: 'Ouvrir le chat',
    },
    trust: {
      heading: 'Adopté par plus de 100 restaurants et marques de lieux',
      subtitle:
        'Restaurants indépendants et groupes multi-sites utilisent PRO20 pour remplir leurs tables chaque jour.',
    },
    stats: {
      title: 'Adopté par des équipes de restaurants dans le monde entier',
      subtitle:
        'Une vraie échelle pour restaurants, cafés, beach clubs et bars.',
      restaurantsValue: '100+',
      restaurantsLabel: 'Restaurants',
      countriesValue: '10+',
      countriesLabel: 'Villes en Italie',
      bookingsValue: '2M+',
      bookingsLabel: 'Réservations gérées',
      uptimeValue: '99.9%',
      uptimeLabel: 'Disponibilité',
    },
    features: {
      title: 'Tout pour gérer vos réservations',
      subtitle:
        'Des réservations et plans de salle au marketing, fidélité et intégrations — une seule plateforme.',
      reservationsTitle: 'Réservations intelligentes',
      reservationsDesc:
        'Acceptez, gérez et optimisez les réservations sur tous les canaux en temps réel.',
      floorTitle: 'Plan de salle et tables',
      floorDesc:
        'Cartographie visuelle avec statut des tables en direct pour un service plus fluide.',
      bookingLinkTitle: 'Lien public de réservation',
      bookingLinkDesc:
        'Créez un formulaire de réservation et partagez l’URL publique sur votre site, QR, WhatsApp ou réseaux pour que les clients réservent en ligne.',
      marketingTitle: 'Marketing et campagnes',
      marketingDesc:
        'Touchez vos clients avec des offres ciblées, rappels et campagnes.',
      loyaltyTitle: 'Fidélité et promos',
      loyaltyDesc:
        'Récompensez les habitués et boostez les retours avec des promotions flexibles.',
      reportsTitle: 'Rapports et insights',
      reportsDesc:
        'Suivez occupation, revenus et tendances clients avec des tableaux de bord clairs.',
    },
    why: {
      title: 'Pourquoi PRO20 ?',
      subtitle:
        'Conçu pour restaurants et bars — pas un widget de réservation générique.',
      allInOneTitle: 'Plateforme tout-en-un',
      allInOneDesc:
        'Réservations, plan de salle, marketing, fidélité et paiements au même endroit.',
      channelsTitle: 'Natif Google et WhatsApp',
      channelsDesc:
        'Retrouvez vos clients là où ils réservent et messagent déjà.',
      hospitalityTitle: 'Pensé pour le service en salle',
      hospitalityDesc:
        'Plan de salle, workflows équipe et outils live adaptés au service.',
      appsTitle: 'Apps pour chaque appareil',
      appsDesc:
        'Apps natives téléphone et iPad sur Android et iOS, synchronisées avec le backoffice web.',
      fidelityTitle: 'Fidélité et récompenses',
      fidelityDesc:
        'Récompensez les habitués avec points, cartes et promos — boostez les retours depuis un seul programme.',
      supportTitle: 'Support 24×7',
      supportDesc:
        'Une aide disponible en permanence quand votre équipe en a besoin — jour et nuit, tous les jours.',
    },
    industries: {
      title: 'Conçu pour chaque type d’établissement',
      subtitle:
        'De la gastronomie aux beach clubs — PRO20 s’adapte à la façon dont votre lieu accueille les clients.',
      restaurants: 'Restaurants',
      cafes: 'Cafés',
      beachClubs: 'Beach Clubs',
      bars: 'Bars et pubs',
      rooftops: 'Rooftops',
      resorts: 'Resorts',
    },
    integrations: {
      title: 'Intégrations puissantes',
      subtitle:
        'Connectez les outils que vous utilisez déjà — réservations, messagerie, POS et paiements.',
      google: 'Google Reservations',
      whatsapp: 'WhatsApp Official API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Paiements Stripe',
      meta: 'Partenaire technologique Meta',
    },
    engage: {
      title: 'Communiquez. Engagez. Ravissez.',
      subtitle:
        'Connectez-vous instantanément à vos clients via WhatsApp Official API et messages automatisés.',
      confirmations: 'Confirmations de réservation',
      reminders: 'Rappels et alertes',
      offers: 'Offres et promotions',
      feedback: 'Avis et retours',
      chatGuest: 'Bonjour ! Votre table pour 2 est confirmée à 20h00.',
      chatModify: 'Modifier la réservation',
      chatCancel: 'Annuler la réservation',
      chatDirections: 'Itinéraire',
    },
    apps: {
      title: 'Gérez votre établissement depuis le téléphone ou l’iPad',
      subtitle:
        'Apps natives PRO20 pour téléphones et iPads — sur Android et iOS, pour que votre équipe reste synchronisée en salle et en mobilité.',
      phoneTablet: 'Téléphones et iPads',
      androidIos: 'Android et iOS',
      floorPlan: 'Plan de salle complet et contrôle des tables sur iPad',
      onTheGo: 'Gérez les réservations depuis votre téléphone partout',
      sync: 'Les mêmes données live que le backoffice web',
      appStore: 'Télécharger sur l’App Store',
      googlePlay: 'Disponible sur Google Play',
    },
    about: {
      title: 'À propos',
      subtitle:
        'Nous aidons les marques de restauration à transformer chaque table en une meilleure expérience client.',
      body: 'PRO20 est la plateforme tout-en-un de réservation de tables, adoptée par des établissements dans le monde entier. Des restaurants indépendants aux groupes multi-sites, nous combinons réservations, plans de salle, messagerie clients et marketing pour que les opérateurs se concentrent sur l’hospitalité — pas sur la jonglerie d’outils.',
      missionTitle: 'Notre mission',
      missionBody:
        'Rendre simples, connectées et agréables les opérations de réservation de premier plan pour chaque établissement.',
      pillarGuests: 'Clients d’abord',
      pillarOperators: 'Opérateurs autonomes',
      pillarConnected: 'Toujours connectés',
      imageAlt: 'Équipe de restaurant au travail',
    },
    howItWorks: {
      title: 'Démarrez en 6 étapes simples',
      subtitle:
        'Tout ce qu’il faut pour gérer les opérations de votre restaurant, de la configuration aux clients satisfaits.',
      steps: {
        s1: {
          title: 'Créez votre restaurant',
          desc: 'Configurez le profil avec cuisine, emplacement et image de marque en quelques minutes.',
        },
        s2: {
          title: 'Concevez salles et tables',
          desc: 'Créez le plan de salle avec le planner glisser-déposer et organisez vos tables.',
        },
        s3: {
          title: 'Configurez les règles de réservation',
          desc: 'Définissez créneaux, taille de groupes, acomptes et politiques adaptés à votre service.',
        },
        s4: {
          title: 'Partagez et recevez des réservations',
          desc: 'Publiez votre lien de réservation sur le site, WhatsApp, réseaux ou QR.',
        },
        s5: {
          title: 'Gérez et développez',
          desc: 'Pilotez la salle, le personnel et le marketing depuis un seul endroit.',
        },
        s6: {
          title: 'Analysez et optimisez',
          desc: 'Utilisez des rapports clairs sur occupation, revenus et tendances pour améliorer chaque service.',
        },
      },
      mocks: {
        createTitle: 'Créez votre restaurant',
        name: 'Nom du restaurant',
        cuisine: 'Type de cuisine',
        location: 'Emplacement',
        continue: 'Continuer',
        floorTitle: 'Plan de salle',
        rulesTitle: 'Paramètres de réservation',
        timeSlots: 'Créneaux',
        bookingRules: 'Règles de réservation',
        advancePayment: 'Paiement anticipé',
        shareTitle: 'Partager le lien de réservation',
        manageTitle: 'Aperçu du jour',
        reservations: 'Réservations',
        occupied: 'Tables occupées',
        walkIns: 'Walk-ins',
        revenue: 'Revenus',
        reportsTitle: 'Aperçu des rapports',
        totalReservations: 'Réservations totales',
        occupancy: 'Taux d’occupation',
      },
      ctaText:
        'Prêt à démarrer ? Rejoignez des milliers de restaurants qui grandissent déjà avec PRO20.',
      bookDemo: 'Réserver une démo',
      contactSales: 'Contacter les ventes',
    },
    faq: {
      title: 'Questions fréquentes',
      subtitle:
        'Réponses rapides sur le logiciel de réservation PRO20 pour établissements multi-sites.',
      viewAll: 'Voir toutes les FAQ',
      q1: 'Puis-je partager un lien public de réservation ?',
      a1: 'Oui. Créez un formulaire de réservation et partagez l’URL publique sur votre site, QR, WhatsApp ou réseaux pour que les clients réservent en ligne.',
      q2: 'Plusieurs restaurants sont-ils pris en charge ?',
      a2: 'Oui. Les groupes multi-sites peuvent gérer leurs établissements de façon centralisée via le Backoffice PRO20.',
      q3: 'Puis-je connecter WhatsApp ?',
      a3: 'Oui. PRO20 prend en charge WhatsApp Business et l’API officielle Meta pour confirmations, rappels et campagnes.',
      q4: 'Quelles intégrations sont disponibles ?',
      a4: 'Google Reservations, WhatsApp Official API, systèmes POS, Stripe, Razorpay et bien d’autres.',
      q5: 'Y a-t-il une application mobile ?',
      a5: 'Oui. Des apps natives pour téléphones et iPads sur Android et iOS, synchronisées avec la plateforme web.',
      q6: 'Comment démarrer ?',
      a6: 'Réservez une démo ou lancez l’essai gratuit — nous vous aidons à configurer salles, tables et votre lien public de réservation.',
    },
    reviews: {
      title: 'Ne nous croyez pas sur parole',
      subtitle:
        'Des restaurants dans plus de 10 villes d’Italie utilisent déjà PRO20 pour gérer les réservations, remplir les tables et ravir les clients. Prêt à voir ce dont on parle ?',
      reassurance:
        '(Oui, vraiment. Pas de petites lignes. Pas de pression commerciale. Juste un logiciel solide et une équipe qui comprend les restaurants.)',
      trustLabel: 'D’après des centaines d’avis',
      r1: {
        quote:
          'PRO20 a remplacé trois outils que nous utilisions avant. Réservations et WhatsApp enfin au même endroit.',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote:
          'La mise en place a été rapide et le planificateur de tables est exactement ce dont le personnel avait besoin les soirs de rush.',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote:
          'La capacité saisonnière et la messagerie clients étaient un chaos. Maintenant c’est prévisible et professionnel.',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          'Notre lien public a rempli des créneaux du soir que nous laissions vides. Les clients réservent seuls — nous confirmons.',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: 'Prêt à faire passer votre restaurant au niveau supérieur ?',
      body: 'PRO20 est conçu pour simplifier les opérations quotidiennes, augmenter les réservations et réduire le stress de gérer un établissement très fréquenté.',
      closing:
        'Commencez avec PRO20 dès aujourd’hui et constatez la différence.',
    },
    cta: {
      heading: 'Prêt à transformer votre restaurant ?',
      subtitle:
        'Rejoignez des milliers de restaurants qui se développent avec PRO20.',
    },
    footer: {
      blurb:
        'Le logiciel tout-en-un de réservation restaurant, d’engagement client et d’opérations d’établissement.',
      product: 'Produit',
      productReservations: 'Réservations',
      productFloor: 'Plan de salle',
      productMarketing: 'Marketing',
      productLoyalty: 'Fidélité',
      productReports: 'Rapports',
      productIntegrations: 'Intégrations',
      solutions: 'Solutions',
      solutionsRestaurants: 'Pour restaurants',
      solutionsCafes: 'Pour cafés',
      solutionsBeach: 'Pour beach clubs',
      solutionsBars: 'Pour bars',
      solutionsChains: 'Pour chaînes',
      resources: 'Ressources',
      resourcesBlog: 'Blog',
      resourcesGuides: 'Guides',
      resourcesHelp: 'Centre d’aide',
      resourcesCases: 'Études de cas',
      resourcesApi: 'Documentation API',
      resourcesApps: 'Apps mobiles',
      company: 'Entreprise',
      companyAbout: 'À propos',
      companyCareers: 'Carrières',
      companyContact: 'Contact',
      companyPartners: 'Partenaires',
      legal: 'Mentions légales',
      legalPrivacy: 'Politique de confidentialité',
      legalTerms: 'Conditions d’utilisation',
      legalCookies: 'Politique cookies',
      copyright: '© {{year}} PRO20. Tous droits réservés.',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: 'FAQ — Logiciel de réservation restaurant PRO20',
    title: 'Questions fréquentes',
    subtitle:
      'Réponses rapides aux questions les plus courantes sur le logiciel de réservation PRO20.',
    searchPlaceholder: 'Recherchez votre question...',
    emptyResults:
      'Aucune question ne correspond à votre recherche. Essayez un autre mot-clé ou une autre catégorie.',
    categories: {
      all: 'Toutes les questions',
      reservations: 'Réservations',
      whatsapp: 'WhatsApp',
      payments: 'Paiements',
      reports: 'Rapports',
      staff: 'Personnel et utilisateurs',
      integrations: 'Intégrations',
      general: 'Général',
    },
    promo: {
      badge: 'Plateforme tout-en-un',
      titleBefore: 'Tout ce qu’il vous faut pour',
      titleAccent: 'gérer et développer',
      titleAfter: 'votre restaurant',
      body: 'Des réservations au marketing, de l’automatisation WhatsApp aux rapports — PRO20 vous aide à gérer votre activité plus intelligemment.',
      restaurantsValue: '10 000+',
      restaurantsLabel: 'Restaurants',
      bookingsValue: '2M+',
      bookingsLabel: 'Réservations',
      countriesValue: '10+',
      countriesLabel: 'Villes en Italie',
      uptimeValue: '99,9%',
      uptimeLabel: 'Disponibilité',
    },
    items: {
      q1: 'Puis-je partager des liens de réservation ?',
      a1: 'Oui. Créez un formulaire de réservation et partagez l’URL publique sur votre site, QR, WhatsApp ou réseaux pour que les clients réservent en ligne.',
      q1Tag1: 'URL publique',
      q1Tag2: 'Code QR',
      q1Tag3: 'WhatsApp',
      q1Tag4: 'Réseaux sociaux',
      q1Tag5: 'Site web',
      q2: 'PRO20 prend-il en charge plusieurs restaurants ou sites ?',
      a2: 'Oui. Les groupes multi-sites gèrent les établissements de façon centralisée via le Backoffice PRO20, avec contrôles et rapports partagés.',
      q3: 'Puis-je connecter WhatsApp Official et envoyer des messages ?',
      a3: 'Oui. PRO20 prend en charge WhatsApp Business et l’API officielle Meta pour confirmations, rappels, campagnes et messagerie.',
      q4: 'Quelles passerelles de paiement sont prises en charge ?',
      a4: 'PRO20 s’intègre à Stripe et d’autres prestataires pour collecter des acomptes et réduire les no-shows.',
      q5: 'Existe-t-il une application mobile ?',
      a5: 'Oui. Des apps natives pour téléphones et iPads sur Android et iOS, synchronisées avec la plateforme web.',
      q6: 'Quelles langues sont prises en charge ?',
      a6: 'PRO20 prend en charge plusieurs langues pour opérateurs et clients, dont l’anglais, l’italien, l’espagnol, le français, le néerlandais et le chinois.',
      q7: 'Puis-je gérer mon personnel et ses rôles ?',
      a7: 'Oui. Attribuez rôles et permissions pour que l’équipe ne voie que le nécessaire — de la salle aux managers multi-sites.',
      q8: 'Puis-je gérer des campagnes marketing et des codes promo ?',
      a8: 'Oui. Lancez campagnes, offres et codes promo depuis PRO20 pour remplir les créneaux plus calmes et récompenser les habitués.',
      q9: 'Quels types de rapports puis-je obtenir ?',
      a9: 'Suivez occupation, réservations, revenus et activité clients avec des tableaux de bord pensés pour la restauration.',
      q10: 'Mes données sont-elles sécurisées ?',
      a10: 'Nous utilisons des pratiques de sécurité standard pour protéger les données du restaurant et des clients, avec une disponibilité fiable.',
    },
    cta: {
      eyebrow: 'Encore des questions ?',
      title: 'Nous sommes là pour vous aider !',
      body: 'Échangez avec nos experts restaurant et découvrez comment PRO20 peut transformer votre activité.',
      bookDemo: 'Réserver une démo',
      contactSales: 'Contacter les ventes',
      startTrial: 'Ou demander un essai gratuit',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: 'Politique de confidentialité — PRO20',
      loading: 'Chargement de la politique de confidentialité…',
      loadError:
        'Impossible de charger les informations du responsable. La politique est affichée avec des informations limitées.',
    },
    privacyPolicy: {
      documentTitle: 'Informations sur la confidentialité | Proventi',
      title: 'Information sur le traitement des données personnelles',
      subtitle:
        'Conformément aux articles 13 et 14 du règlement (UE) 2016/679 – RGPD',
      contents: 'Sur cette page',
      body: privacyPolicyBodyFr,
    },
    terms: {
      documentTitle: 'Conditions d’utilisation — PRO20',
      title: 'Conditions d’utilisation',
      subtitle: 'Ces conditions seront mises à jour prochainement.',
      body: 'Les Conditions d’utilisation de PRO20 sont en cours de préparation. Revenez plus tard ou contactez-nous si vous avez besoin d’une copie entre-temps.',
    },
  },
};
